import { DEFLATOR_PPM_START, Frequency, PLAUSIBLE_GROWTH_CEILING_BPS, WRAPPER_VALUES, formatGbpWhole, isoDate, pence, wrapperLabel, } from '@cf/shared';
import { annualAllowanceExcess, annualAllowanceWithCarryForward, assertSupportedTaxRegion, capitalGainsTax, effectiveAnnualAllowance, incomeTax, inheritanceTax, isaSubscriptionRoom, jisaSubscriptionRoom, lisaBonus, lisaSubscriptionRoom, nationalInsurance, relievableContributionLimit, section24Relief, 
// A student loan repayment is a DEBT REPAYMENT, not tax — imported here
// because PAYE collects it beside NI, never because it belongs in `totalTax`.
studentLoanRepayment, studentLoanWriteOffYear, taxFreeLumpSum, } from '@cf/tax';
import { ENGINE_VERSION } from './version.js';
import { amortiseYear, levelMonthlyPayment } from './amortise.js';
import { ageAtTaxYearStart, taxYearContaining, taxYearLabel } from './tax-year.js';
import { resolveEscalationBpsForYear, statePensionEscalationBps } from './assumptions.js';
import { expandPlannedEvents } from './planned-events.js';
import { checkPlanCompleteness } from './plan-completeness.js';
import { validatePlan } from './plan-validation.js';
import { DEFAULT_END_AT_CLIENT_AGE, NO_SWEEP_WATERFALL, DEFAULT_WITHDRAWAL_WATERFALL, MAX_PROJECTION_AGE, } from './household-types.js';
/**
 * Household projection — the v2 engine. PURE and DETERMINISTIC: time comes
 * only from the injected {@link Clock}, tax only from the injected (reviewed)
 * {@link TaxRuleSet}, inputs are never mutated, and identical inputs produce
 * an identical, deeply-frozen result. Client and partner are TAXED SEPARATELY
 * throughout; only spending and the surplus/deficit decisions are
 * household-level. Internal year N is the UK tax year 6 Apr N – 5 Apr N+1.
 *
 * ANNUAL OPERATION ORDER (documented in docs/CALCULATION_METHOD.md and
 * asserted in household.test.ts):
 *  1. ages/status         — alive, retired, state-pension status per person
 *  2. crystallisation     — DC pots crystallise at retirement (PCLS → cash)
 *  3. debt amortisation   — monthly interest accrual + payments per liability
 *                           (re-fix rate changes and annual overpayments applied)
 *  4. property rent       — BTL yield on start-of-year value (Section 24 aware)
 *  5. income accrual      — streams per person, escalated; cash interest
 *  6. contributions       — regular asset contributions inside each window
 *  7. spending            — inflation-linked from each item's first year
 *  8. scheduled drawdown  — dc_pension streams draw from the crystallised pot
 *  9. per-person tax      — NI + income tax + Section 24 credit + AA charge
 * 10. surplus/deficit     — surplus waterfall placement (cash-buffer aware),
 *                           or the configurable WITHDRAWAL waterfall (CGT with
 *                           pooling on GIA sales; grossed-up pension draws)
 * 11. growth              — wrapper growth applied to end-of-year balances
 * 12. death processing    — spouse-exempt transfers to the survivor
 * 13. record              — immutable year snapshot + audit row (conservation
 *                           identities hold to the penny; see HouseholdYearAudit)
 */
const FREQ_PER_YEAR = {
    [Frequency.Once]: 1,
    [Frequency.Monthly]: 12,
    [Frequency.Quarterly]: 4,
    [Frequency.Annually]: 1,
};
/** amount × bps / 10000, truncated toward zero. */
function bpsOf(amount, bps) {
    return Math.trunc((amount * bps) / 10_000);
}
/** Compound an integer-pence figure by bps over n whole years (truncating). */
function compound(base, bps, years) {
    let acc = base;
    for (let i = 0; i < years; i += 1)
        acc += bpsOf(acc, bps);
    return acc;
}
/**
 * Escalate `base` from `fromYear` to `atYear` under a first-class escalation.
 * Non-stepped kinds resolve to a constant rate and compound in one shot (exactly
 * as before — byte-identical). A `stepped` schedule compounds year-by-year so
 * each projection year picks its active step (FEATURES §0.1d/e). `fallbackBps`
 * covers the legacy `inflationLinked`/undefined case.
 */
function escalate(base, esc, fallbackBpsFor, fromYear, atYear, ctxFor) {
    // Always compound year-by-year with THAT year's context, so scheduled
    // assumption windows apply exactly; with a constant context this is
    // penny-identical to compounding a single resolved rate.
    let acc = base;
    for (let y = fromYear + 1; y <= atYear; y += 1) {
        const rate = esc === undefined ? fallbackBpsFor(y) : resolveEscalationBpsForYear(esc, y, ctxFor(y));
        acc = compound(acc, rate, 1);
    }
    return acc;
}
function annualAmount(amount, frequency) {
    return amount * FREQ_PER_YEAR[frequency];
}
/** Marginal-rate estimate for the annual allowance charge (a documented
 * approximation; unscheduled pension draws are taxed through the bands). */
const MARGINAL_BPS = { none: 0, basic: 2000, higher: 4000, additional: 4500 };
function emptyPots() {
    return {
        cash: 0,
        cashIsa: 0,
        stocksIsa: 0,
        lisa: 0,
        nsi: 0,
        gia: { value: 0, cost: 0 },
    };
}
function potSetTotal(s) {
    return s.cash + s.cashIsa + s.stocksIsa + s.lisa + s.nsi + s.gia.value;
}
/**
 * NS&I premium-bond maximum holding per person — fallback for rule sets that
 * predate the provenanced `rules.nsi` product data (post-Stage-4 sets carry
 * the cited figure).
 */
const NSI_MAX_HOLDING_FALLBACK = 50_000_00;
/** Wrappers a market shock may not target: cash interest is tax-aligned at
 * source (step 5 ↔ step 11), and DB/state pensions hold no modelled capital. */
const UNSHOCKABLE_WRAPPERS = new Set([
    'cash',
    'db_pension',
    'state_pension',
]);
/** Whole pounds for warning copy, e.g. 5000000 → "£50,000". */
function gbpWhole(penceValue) {
    return `£${Math.trunc(penceValue / 100).toLocaleString('en-GB')}`;
}
function dcTotal(p) {
    let t = 0;
    for (const pot of p.dcPots)
        t += pot.uncrystallised + pot.drawdown;
    return t;
}
/** Plain-English name for each surplus-sweep destination, for the assumptions page. */
const SWEEP_TARGET_WORDS = {
    isa: 'to an ISA',
    pension: 'to a pension',
    gia: 'to a general investment account',
    lisa: 'to a Lifetime ISA',
    jisa: 'to a Junior ISA',
    spend: 'towards a stated spend',
    liability: 'to overpay a debt',
};
function newAcc() {
    return {
        nonSavings: 0,
        savings: 0,
        dividends: 0,
        untaxedInflow: 0,
        cashInterest: 0,
        cashInterestShielded: 0,
        cashInterestTaxable: 0,
        giaYield: 0,
        giaYieldShielded: 0,
        giaYieldTaxable: 0,
        employment: 0,
        selfEmployment: 0,
        rentalProfits: 0,
        financeCosts: 0,
        grossPensionContributions: 0,
        employerPensionContributions: 0,
        realisedGains: 0,
        residentialGains: 0,
        badrGains: 0,
        isaSubscribed: 0,
        lisaSubscribed: 0,
        drawTax: 0,
        unscheduledGross: 0,
    };
}
/** An escalation's rate/delta must be a finite integer bps (may be negative — a
 * fee could fall below inflation) within ±10000 (±100%/yr) so compounding can
 * never fabricate unsafe magnitudes. Guards non-integers reaching compound(). */
function assertEscalation(esc, label) {
    if (esc.kind === 'stepped') {
        for (const step of esc.steps)
            assertEscalation(step.rate, label);
        assertRateBps(esc.capBps, label);
        assertRateBps(esc.floorBps, label);
        return;
    }
    const v = esc.kind === 'fixed' ? esc.rateBps : esc.kind === 'none' ? 0 : esc.deltaBps;
    assertRateBps(v, label);
    if (esc.kind !== 'none') {
        assertRateBps(esc.capBps, label);
        assertRateBps(esc.floorBps, label);
    }
}
/** A rate/delta/collar bound must be a whole number of bps within ±100%/yr. */
function assertRateBps(v, label) {
    if (v === undefined)
        return;
    if (!Number.isInteger(v) || v < -10_000 || v > 10_000) {
        throw new RangeError(`${label} rate/delta must be a whole number of basis points between -10000 and 10000`);
    }
}
function validate(input, clock) {
    if (!Number.isInteger(clock.startYear)) {
        throw new RangeError(`clock.startYear must be a whole calendar year: ${clock.startYear}`);
    }
    if (input.assumptions.horizonYears !== undefined &&
        (!Number.isInteger(input.assumptions.horizonYears) || input.assumptions.horizonYears < 1)) {
        throw new RangeError('horizonYears must be a positive whole number');
    }
    if (input.people.length < 1 || input.people.length > 2) {
        throw new RangeError('A household has one client and at most one partner');
    }
    if (!input.people.some((p) => p.id === 'client')) {
        throw new RangeError('The household must include the client');
    }
    if (new Set(input.people.map((p) => p.id)).size !== input.people.length) {
        throw new RangeError('Duplicate person ids in household');
    }
    for (const person of input.people) {
        // Hard product gate: refuse Scottish taxpayers rather than mis-taxing.
        assertSupportedTaxRegion(person.taxRegion);
    }
    const window = input.window;
    if (window?.endYear != null && window.endYear < clock.startYear) {
        throw new RangeError('window.endYear must not be before the projection start year');
    }
    if (window?.endAtClientAge != null) {
        const client = input.people.find((p) => p.id === 'client');
        if (window.endAtClientAge < client.currentAge) {
            throw new RangeError('window.endAtClientAge must not be below the client’s current age');
        }
    }
    for (const dep of input.dependents ?? []) {
        // Throws on a malformed date; also anchors dependent ages deterministically.
        ageAtTaxYearStart(dep.dateOfBirth, clock.startYear);
    }
    const personIds = new Set(input.people.map((p) => p.id));
    const liabilityIds = new Set(input.liabilities.map((l) => l.id));
    const dependentIds = new Set((input.dependents ?? []).map((d) => d.id));
    for (const asset of input.assets) {
        const linked = asset.property?.linkedLiabilityId;
        if (linked != null && !liabilityIds.has(linked)) {
            throw new RangeError(`Asset "${asset.label}" links to unknown liability "${linked}"`);
        }
        if (asset.ownerId !== 'joint' && !personIds.has(asset.ownerId)) {
            throw new RangeError(`Asset "${asset.label}" is owned by an absent person`);
        }
        for (const [label, v] of Object.entries({
            currentValue: asset.currentValue,
            contribution: asset.contribution,
            ...(asset.costBasis !== undefined ? { costBasis: asset.costBasis } : {}),
            ...(asset.employerContribution !== undefined
                ? { employerContribution: asset.employerContribution }
                : {}),
        })) {
            if (!Number.isSafeInteger(v) || v < 0) {
                throw new RangeError(`Asset "${asset.label}" ${label} must be non-negative integer pence`);
            }
        }
        if (asset.contributionStartYear != null &&
            asset.contributionEndYear != null &&
            asset.contributionEndYear < asset.contributionStartYear) {
            throw new RangeError(`Asset "${asset.label}" contribution window ends before it starts`);
        }
        // Yield / fee / rental-yield overrides are non-negative integer bps (a
        // negative fee would be a fabricated inflow; a non-integer breaks pence
        // discipline downstream). Growth MAY be negative (a deliberate bear-case
        // override, or depreciation on vehicles/chattels) down to -100%.
        for (const [label, v] of Object.entries({
            ...(asset.yieldBps != null ? { yieldBps: asset.yieldBps } : {}),
            ...(asset.feeDragBps != null ? { feeDragBps: asset.feeDragBps } : {}),
            ...(asset.property ? { rentalYieldBps: asset.property.rentalYieldBps } : {}),
        })) {
            if (!Number.isInteger(v) || v < 0) {
                throw new RangeError(`Asset "${asset.label}" ${label} must be a non-negative integer bps`);
            }
        }
        if (asset.growthRateBps != null &&
            (!Number.isInteger(asset.growthRateBps) || asset.growthRateBps < -10_000)) {
            throw new RangeError(`Asset "${asset.label}" growthRateBps must be an integer bps no lower than -10000`);
        }
        if (asset.contributionEscalation !== undefined) {
            assertEscalation(asset.contributionEscalation, `Asset "${asset.label}" contributionEscalation`);
        }
        if (asset.employerContribution !== undefined && asset.wrapper !== 'dc_pension') {
            throw new RangeError(`Asset "${asset.label}": employer contributions are DC-pension-only`);
        }
        if (asset.accessAge != null) {
            if (asset.wrapper !== 'dc_pension') {
                throw new RangeError(`Asset "${asset.label}": accessAge applies to DC pensions only`);
            }
            if (!Number.isInteger(asset.accessAge) || asset.accessAge < 0 || asset.accessAge > 110) {
                throw new RangeError(`Asset "${asset.label}" accessAge must be a whole age (0–110)`);
            }
        }
        if (asset.wrapper === 'jisa') {
            if (asset.dependentId === undefined || !dependentIds.has(asset.dependentId)) {
                throw new RangeError(`JISA "${asset.label}" must name a known dependent`);
            }
        }
        else if (asset.dependentId !== undefined) {
            throw new RangeError(`Asset "${asset.label}": dependentId applies to JISAs only`);
        }
        if (asset.bprRateBps !== undefined) {
            if (asset.wrapper !== 'business') {
                throw new RangeError(`Asset "${asset.label}": bprRateBps applies to business interests`);
            }
            if (!Number.isInteger(asset.bprRateBps) ||
                asset.bprRateBps < 0 ||
                asset.bprRateBps > 10_000) {
                throw new RangeError(`Asset "${asset.label}" bprRateBps must be 0–10000 bps`);
            }
        }
        if (asset.wrapper === 'other_asset' && asset.yieldBps != null && asset.yieldBps !== 0) {
            throw new RangeError(`Asset "${asset.label}": other assets carry no income yield — model income separately`);
        }
        if (asset.property?.isPrimaryResidence === true && asset.property.isBuyToLet) {
            throw new RangeError(`Asset "${asset.label}" cannot be both main home and buy-to-let`);
        }
        // ISAs, LISAs and pensions are individually owned by law — a "joint"
        // holding would silently split them 50/50 and mis-model the tax.
        if (asset.ownerId === 'joint' &&
            (asset.wrapper === 'cash_isa' ||
                asset.wrapper === 'stocks_isa' ||
                asset.wrapper === 'lisa' ||
                asset.wrapper === 'dc_pension')) {
            throw new RangeError(`Asset "${asset.label}": ${wrapperLabel(asset.wrapper)} accounts are individually owned — model one per person`);
        }
    }
    for (const stream of input.incomeStreams) {
        if (!personIds.has(stream.ownerId)) {
            throw new RangeError(`Income "${stream.label}" is owned by an absent person`);
        }
        if (!Number.isSafeInteger(stream.amount) || stream.amount < 0) {
            throw new RangeError(`Income "${stream.label}" amount must be non-negative integer pence`);
        }
        if (stream.dbSpouseFractionBps !== undefined &&
            (!Number.isInteger(stream.dbSpouseFractionBps) ||
                stream.dbSpouseFractionBps < 0 ||
                stream.dbSpouseFractionBps > 10_000)) {
            throw new RangeError(`Income "${stream.label}" spouse fraction must be 0–10000 bps`);
        }
        if (stream.pensionSharingDebitBps !== undefined &&
            (!Number.isInteger(stream.pensionSharingDebitBps) ||
                stream.pensionSharingDebitBps < 0 ||
                stream.pensionSharingDebitBps > 10_000)) {
            throw new RangeError(`Income "${stream.label}" pension sharing debit must be 0–10000 bps`);
        }
    }
    for (const step of input.waterfall?.steps ?? []) {
        if (step.annualCap !== null && (!Number.isSafeInteger(step.annualCap) || step.annualCap < 0)) {
            throw new RangeError('Waterfall step annualCap must be non-negative integer pence');
        }
        if (step.targetBalance != null &&
            (!Number.isSafeInteger(step.targetBalance) || step.targetBalance < 0)) {
            throw new RangeError('Waterfall step targetBalance must be non-negative integer pence');
        }
        if (step.targetBalanceMonthsOfSpending != null &&
            (!Number.isInteger(step.targetBalanceMonthsOfSpending) ||
                step.targetBalanceMonthsOfSpending < 0 ||
                step.targetBalanceMonthsOfSpending > 120)) {
            throw new RangeError('Waterfall cash-buffer months must be a whole number of months (0–120)');
        }
    }
    for (const item of input.spending) {
        if (!Number.isSafeInteger(item.amount) || item.amount < 0) {
            throw new RangeError(`Spending "${item.label}" amount must be non-negative integer pence`);
        }
        if (item.escalation !== undefined) {
            assertEscalation(item.escalation, `Spending "${item.label}" escalation`);
        }
    }
    const lifeEventIds = new Set();
    for (const event of input.lifeEvents ?? []) {
        if (lifeEventIds.has(event.id)) {
            throw new RangeError(`Duplicate life event id "${event.id}"`);
        }
        lifeEventIds.add(event.id);
        if (!Number.isSafeInteger(event.amount) || event.amount < 0) {
            throw new RangeError(`Life event "${event.label}" amount must be non-negative integer pence`);
        }
        if (!Number.isInteger(event.year)) {
            throw new RangeError(`Life event "${event.label}" year must be a whole calendar year`);
        }
    }
    for (const l of input.liabilities) {
        // null = rate not yet provided: the liability is excluded from the
        // projection with a standing warning rather than modelled at 0%.
        if (l.interestRateBps !== null &&
            (!Number.isInteger(l.interestRateBps) || l.interestRateBps < 0)) {
            throw new RangeError(`Liability "${l.label}" rate must be non-negative integer bps`);
        }
        if (!Number.isSafeInteger(l.monthlyPayment) || l.monthlyPayment < 0) {
            throw new RangeError(`Liability "${l.label}" payment must be non-negative integer pence`);
        }
        if (l.annualOverpayment !== undefined &&
            (!Number.isSafeInteger(l.annualOverpayment) || l.annualOverpayment < 0)) {
            throw new RangeError(`Liability "${l.label}" overpayment must be non-negative integer pence`);
        }
        for (const change of l.rateChanges ?? []) {
            if (!Number.isInteger(change.fromYear)) {
                throw new RangeError(`Liability "${l.label}" rate change year must be a whole year`);
            }
            if (!Number.isInteger(change.interestRateBps) || change.interestRateBps < 0) {
                throw new RangeError(`Liability "${l.label}" rate change must be non-negative integer bps`);
            }
        }
    }
    const withdrawal = input.withdrawalWaterfall;
    if (withdrawal !== undefined && withdrawal.steps.length === 0) {
        throw new RangeError('withdrawalWaterfall must have at least one step');
    }
    const assetIds = new Set(input.assets.map((a) => a.id));
    const propertyIds = new Set([
        ...input.assets.filter((a) => a.wrapper === 'property').map((a) => a.id),
        ...(input.plannedEvents ?? []).filter((e) => e.kind === 'propertyPurchase').map((e) => e.id),
    ]);
    const businessIds = new Set(input.assets.filter((a) => a.wrapper === 'business').map((a) => a.id));
    const purchaseYearById = new Map((input.plannedEvents ?? [])
        .filter((e) => e.kind === 'propertyPurchase')
        .map((e) => [e.id, e.year]));
    const soldPropertyIds = new Set();
    const planIds = new Set();
    /** Non-negative integer bps, bounded — a rogue rate cannot fabricate value. */
    const assertPlanBps = (plan, label, v, max) => {
        if (v == null)
            return;
        if (!Number.isInteger(v) || v < 0 || v > max) {
            throw new RangeError(`Planned event "${plan.label}" ${label} must be 0–${max} integer bps`);
        }
    };
    for (const plan of input.plannedEvents ?? []) {
        if (planIds.has(plan.id)) {
            throw new RangeError(`Duplicate planned-event id "${plan.id}"`);
        }
        planIds.add(plan.id);
        if (assetIds.has(plan.id)) {
            throw new RangeError(`Planned event id "${plan.id}" collides with an asset id`);
        }
        for (const [label, v] of Object.entries({
            ...('price' in plan ? { price: plan.price } : {}),
            ...('deposit' in plan ? { deposit: plan.deposit } : {}),
            ...('feePerTerm' in plan ? { feePerTerm: plan.feePerTerm } : {}),
            ...('annualTuition' in plan ? { annualTuition: plan.annualTuition } : {}),
            ...('annualMaintenance' in plan ? { annualMaintenance: plan.annualMaintenance } : {}),
            ...('annualCost' in plan ? { annualCost: plan.annualCost } : {}),
            ...('amount' in plan ? { amount: plan.amount } : {}),
            ...(plan.kind === 'propertyPurchase'
                ? { mortgageMonthlyPayment: plan.mortgageMonthlyPayment }
                : {}),
            ...(plan.kind === 'careerBreak' && plan.redundancyLumpSum !== undefined
                ? { redundancyLumpSum: plan.redundancyLumpSum }
                : {}),
        })) {
            if (!Number.isSafeInteger(v) || v < 0) {
                throw new RangeError(`Planned event "${plan.label}" ${label} must be non-negative integer pence`);
            }
        }
        if ('year' in plan && !Number.isInteger(plan.year)) {
            throw new RangeError(`Planned event "${plan.label}" year must be a whole calendar year`);
        }
        // A transaction dated before the projection window would otherwise leave
        // half-applied state (e.g. a mortgage with no property). Fail closed.
        if ('year' in plan && plan.year < clock.startYear) {
            throw new RangeError(`Planned event "${plan.label}" is dated ${plan.year}, before the projection starts (${clock.startYear})`);
        }
        if ('escalation' in plan && plan.escalation !== undefined) {
            assertEscalation(plan.escalation, `Planned event "${plan.label}" escalation`);
        }
        if (plan.kind === 'schoolFees') {
            assertPlanBps(plan, 'siblingDiscountBps', plan.siblingDiscountBps, 10_000);
            if (plan.termsPerYear !== undefined && ![1, 2, 3].includes(plan.termsPerYear)) {
                throw new RangeError(`School fees "${plan.label}" termsPerYear must be 1, 2 or 3`);
            }
            if (plan.startYear != null && plan.endYear != null && plan.endYear < plan.startYear) {
                throw new RangeError(`School fees "${plan.label}" window ends before it starts`);
            }
            if (plan.startAge != null && plan.endAge != null && plan.endAge < plan.startAge) {
                throw new RangeError(`School fees "${plan.label}" window ends before it starts`);
            }
        }
        if (plan.kind === 'university') {
            assertPlanBps(plan, 'parentalContributionBps', plan.parentalContributionBps, 10_000);
            if (plan.durationYears != null &&
                (!Number.isInteger(plan.durationYears) || plan.durationYears < 1)) {
                throw new RangeError(`University "${plan.label}" duration must be a whole year ≥ 1`);
            }
        }
        if (plan.kind === 'propertyPurchase') {
            if (plan.ownerId !== undefined && plan.ownerId !== 'joint' && !personIds.has(plan.ownerId)) {
                throw new RangeError(`Purchase "${plan.label}" is owned by an absent person`);
            }
            if (plan.deposit > plan.price) {
                throw new RangeError(`Purchase "${plan.label}": deposit exceeds the price`);
            }
            assertPlanBps(plan, 'mortgageRateBps', plan.mortgageRateBps, 10_000);
            assertPlanBps(plan, 'rentalYieldBps', plan.rentalYieldBps, 10_000);
            if (plan.growthRateBps != null &&
                (!Number.isInteger(plan.growthRateBps) ||
                    plan.growthRateBps < -10_000 ||
                    plan.growthRateBps > 10_000)) {
                throw new RangeError(`Purchase "${plan.label}" growthRateBps must be integer bps in -10000–10000`);
            }
        }
        if (plan.kind === 'propertySale') {
            if (!propertyIds.has(plan.propertyId)) {
                throw new RangeError(`Sale "${plan.label}" names an unknown property "${plan.propertyId}"`);
            }
            if (soldPropertyIds.has(plan.propertyId)) {
                throw new RangeError(`Property "${plan.propertyId}" is sold more than once`);
            }
            soldPropertyIds.add(plan.propertyId);
            assertPlanBps(plan, 'sellingCostsBps', plan.sellingCostsBps, 10_000);
            const boughtIn = purchaseYearById.get(plan.propertyId);
            if (boughtIn !== undefined && plan.year < boughtIn) {
                throw new RangeError(`Sale "${plan.label}" is dated ${plan.year}, before the property is bought (${boughtIn})`);
            }
        }
        if (plan.kind === 'businessSale' && !businessIds.has(plan.assetId)) {
            throw new RangeError(`Sale "${plan.label}" names an unknown business "${plan.assetId}"`);
        }
        if (plan.kind === 'careerBreak' || plan.kind === 'care') {
            if (!personIds.has(plan.personId)) {
                throw new RangeError(`Planned event "${plan.label}" names an absent person`);
            }
            const anchors = [plan.startYear, plan.startAge].filter((a) => a != null);
            if (anchors.length !== 1) {
                throw new RangeError(`Planned event "${plan.label}" needs exactly one anchor: startYear or startAge`);
            }
            if (plan.startAge != null && (!Number.isInteger(plan.startAge) || plan.startAge < 0)) {
                throw new RangeError(`Planned event "${plan.label}" startAge must be a whole age`);
            }
            if (plan.startYear != null && !Number.isInteger(plan.startYear)) {
                throw new RangeError(`Planned event "${plan.label}" startYear must be a whole year`);
            }
            if (plan.startYear != null && plan.startYear < clock.startYear) {
                throw new RangeError(`Planned event "${plan.label}" is dated ${plan.startYear}, before the projection starts (${clock.startYear})`);
            }
            const duration = plan.durationYears;
            if ((plan.kind === 'careerBreak' || duration != null) &&
                (!Number.isInteger(duration) || duration < 1)) {
                throw new RangeError(`Planned event "${plan.label}" needs a whole-year duration of at least 1`);
            }
        }
        if (plan.kind === 'recurring') {
            if (!Number.isInteger(plan.everyNYears) || plan.everyNYears < 1) {
                throw new RangeError(`Recurring plan "${plan.label}" needs a whole-year interval`);
            }
            if (!Number.isInteger(plan.startYear) || plan.startYear < clock.startYear) {
                throw new RangeError(`Recurring plan "${plan.label}" must start in or after the projection start year (set the next occurrence)`);
            }
            if (plan.endYear != null && plan.endYear < plan.startYear) {
                throw new RangeError(`Recurring plan "${plan.label}" ends before it starts`);
            }
        }
    }
    // Market shocks: explicit stress inputs, validated as strictly as any other
    // rate. Cash is refused (its interest is tax-aligned at source — use a
    // growth override for a rate change); DB/state pensions hold no capital.
    const shockIds = new Set();
    for (const shock of input.marketShocks ?? []) {
        if (shockIds.has(shock.id)) {
            throw new RangeError(`Duplicate market shock id "${shock.id}"`);
        }
        shockIds.add(shock.id);
        if (shock.path !== undefined || shock.anchor !== undefined) {
            // C1.1 anchored signed path.
            if (shock.path === undefined || shock.anchor === undefined || shock.path.length === 0) {
                throw new RangeError(`Market shock "${shock.label}" needs both an anchor and a path`);
            }
            const anchor = shock.anchor;
            if ('year' in anchor) {
                if (!Number.isInteger(anchor.year)) {
                    throw new RangeError(`Market shock "${shock.label}" needs a whole calendar year`);
                }
            }
            else {
                if (!Number.isInteger(anchor.yearsAfterRetirement) || anchor.yearsAfterRetirement < 0) {
                    throw new RangeError(`Market shock "${shock.label}" years after retirement must be a whole number of years`);
                }
                const person = anchor.person;
                if (person !== 'firstToRetire' && !input.people.some((p) => p.id === person)) {
                    throw new RangeError(`Market shock "${shock.label}" anchors on "${person}", who is not on the plan`);
                }
            }
            const offsets = new Set();
            for (const step of shock.path) {
                if (!Number.isInteger(step.offsetYears) ||
                    step.offsetYears < 0 ||
                    offsets.has(step.offsetYears)) {
                    throw new RangeError(`Market shock "${shock.label}" path steps must be distinct whole years`);
                }
                offsets.add(step.offsetYears);
                const entries = Object.entries(step.returnDeltaBpsByWrapper);
                const replacements = Object.entries(step.returnBpsByWrapper ?? {});
                const economy = step.economy;
                const economyKeys = economy === undefined
                    ? []
                    : ['inflationBps', 'rpiBps', 'wageGrowthBps', 'cashRateBps'].filter((k) => economy[k] !== undefined);
                if (entries.length === 0 && replacements.length === 0 && economyKeys.length === 0) {
                    throw new RangeError(`Market shock "${shock.label}" names no wrappers`);
                }
                for (const [wrapper, total] of replacements) {
                    if (UNSHOCKABLE_WRAPPERS.has(wrapper)) {
                        throw new RangeError(`Market shock "${shock.label}" cannot target "${wrapperLabel(wrapper)}" — it holds no shockable capital`);
                    }
                    if (step.returnDeltaBpsByWrapper[wrapper] !== undefined) {
                        throw new RangeError(`Market shock "${shock.label}" both replaces and adjusts the return for "${wrapperLabel(wrapper)}" in one year — that would apply the year twice`);
                    }
                    if (!Number.isInteger(total) || total < -10_000 || total > 100_000) {
                        throw new RangeError(`Market shock "${shock.label}" total return for "${wrapperLabel(wrapper)}" must be −10,000..100,000 bps`);
                    }
                }
                if (economy !== undefined) {
                    for (const k of ['inflationBps', 'wageGrowthBps']) {
                        const v = economy[k];
                        if (v !== undefined && (!Number.isInteger(v) || v < -2000 || v > 5000)) {
                            throw new RangeError(`Market shock "${shock.label}" ${k} must be −2,000..5,000 bps (a sourced observation outside that range is refused, not clipped)`);
                        }
                    }
                    const rpi = economy.rpiBps;
                    if (rpi !== undefined &&
                        rpi !== null &&
                        (!Number.isInteger(rpi) || rpi < -2000 || rpi > 5000)) {
                        throw new RangeError(`Market shock "${shock.label}" rpiBps must be −2,000..5,000 bps`);
                    }
                    const cash = economy.cashRateBps;
                    if (cash !== undefined && (!Number.isInteger(cash) || cash < -500 || cash > 3000)) {
                        throw new RangeError(`Market shock "${shock.label}" cashRateBps must be −500..3,000 bps`);
                    }
                    if (cash !== undefined && replacements.some(([w]) => w === 'cash_isa')) {
                        throw new RangeError(`Market shock "${shock.label}" sets both a cash rate and a total return for cash ISAs in one year`);
                    }
                }
                for (const [wrapper, delta] of entries) {
                    if (UNSHOCKABLE_WRAPPERS.has(wrapper)) {
                        throw new RangeError(`Market shock "${shock.label}" cannot target "${wrapperLabel(wrapper)}" — it holds no shockable capital`);
                    }
                    if (!Number.isInteger(delta) || delta < -10_000 || delta > 10_000) {
                        throw new RangeError(`Market shock "${shock.label}" delta for "${wrapperLabel(wrapper)}" must be −10,000..10,000 bps`);
                    }
                }
            }
            const pathedLiabilities = new Set();
            for (const lp of shock.liabilityRatePaths ?? []) {
                const liability = input.liabilities.find((l) => l.id === lp.liabilityId);
                if (liability === undefined) {
                    throw new RangeError(`Market shock "${shock.label}" sets a rate path for a liability that is not on the plan`);
                }
                if (liability.interestRateBps === null) {
                    throw new RangeError(`Market shock "${shock.label}" sets a rate path for "${liability.label}", which has no interest rate and is not modelled`);
                }
                if (pathedLiabilities.has(lp.liabilityId)) {
                    throw new RangeError(`Market shock "${shock.label}" sets two rate paths for "${liability.label}"`);
                }
                pathedLiabilities.add(lp.liabilityId);
                if (lp.reamortise && liability.endYear === null && !liability.interestOnly) {
                    throw new RangeError(`Market shock "${shock.label}" recalculates payments on "${liability.label}", which has no end year to recalculate them over`);
                }
                const lpOffsets = new Set();
                if (lp.steps.length === 0) {
                    throw new RangeError(`Market shock "${shock.label}" rate path for "${liability.label}" is empty`);
                }
                for (const s of lp.steps) {
                    if (!Number.isInteger(s.offsetYears) ||
                        s.offsetYears < 0 ||
                        lpOffsets.has(s.offsetYears)) {
                        throw new RangeError(`Market shock "${shock.label}" rate path steps for "${liability.label}" must be distinct whole years`);
                    }
                    lpOffsets.add(s.offsetYears);
                    if (!Number.isInteger(s.interestRateBps) ||
                        s.interestRateBps < 0 ||
                        s.interestRateBps > 5000) {
                        throw new RangeError(`Market shock "${shock.label}" rate for "${liability.label}" must be 0–5,000 bps`);
                    }
                }
            }
            continue;
        }
        if (!Number.isInteger(shock.year)) {
            throw new RangeError(`Market shock "${shock.label}" needs a whole calendar year`);
        }
        const entries = Object.entries(shock.dropBpsByWrapper ?? {});
        if (entries.length === 0) {
            throw new RangeError(`Market shock "${shock.label}" names no wrappers`);
        }
        for (const [wrapper, dropBps] of entries) {
            if (UNSHOCKABLE_WRAPPERS.has(wrapper)) {
                throw new RangeError(`Market shock "${shock.label}" cannot target "${wrapperLabel(wrapper)}" — it holds no shockable capital`);
            }
            if (!Number.isInteger(dropBps) || dropBps < 1 || dropBps > 10_000) {
                throw new RangeError(`Market shock "${shock.label}" drop for "${wrapperLabel(wrapper)}" must be 1–10,000 bps`);
            }
        }
    }
    // Timed assumption windows: validated as strictly as the base assumptions.
    const scheduleIds = new Set();
    for (const step of input.assumptions.schedule ?? []) {
        const name = step.label ?? step.id;
        if (scheduleIds.has(step.id)) {
            throw new RangeError(`Duplicate timed assumption change id "${step.id}"`);
        }
        scheduleIds.add(step.id);
        if (!Number.isInteger(step.fromYear) ||
            (step.toYear != null && !Number.isInteger(step.toYear))) {
            throw new RangeError(`Timed change "${name}" needs whole calendar years`);
        }
        if (step.toYear != null && step.toYear < step.fromYear) {
            throw new RangeError(`Timed change "${name}" ends before it starts`);
        }
        for (const [key, v] of [
            ['inflationBps', step.inflationBps],
            ['wageGrowthBps', step.wageGrowthBps],
            ['rpiBps', step.rpiBps],
        ]) {
            if (v !== undefined && (!Number.isInteger(v) || v < 0 || v > 2000)) {
                throw new RangeError(`Timed change "${name}" ${key} must be 0–2,000 bps`);
            }
        }
        const growthEntries = Object.entries(step.growthDeltaBpsByWrapper ?? {});
        for (const [wrapper, delta] of growthEntries) {
            if (UNSHOCKABLE_WRAPPERS.has(wrapper)) {
                throw new RangeError(`Timed change "${name}" cannot adjust growth for "${wrapperLabel(wrapper)}" — it holds no adjustable capital`);
            }
            if (!Number.isInteger(delta) || delta < -10_000 || delta > 10_000) {
                throw new RangeError(`Timed change "${name}" growth delta for "${wrapperLabel(wrapper)}" must be ±10,000 bps`);
            }
        }
        if (step.inflationBps === undefined &&
            step.wageGrowthBps === undefined &&
            step.rpiBps === undefined &&
            growthEntries.length === 0) {
            throw new RangeError(`Timed change "${name}" sets no assumption`);
        }
    }
    // Every wrapper needs an explicit growth assumption — a missing key would
    // otherwise surface as NaN deep inside the loop. Fail loudly instead.
    for (const w of WRAPPER_VALUES) {
        const g = input.assumptions.growthBpsByWrapper[w];
        if (!Number.isInteger(g)) {
            throw new RangeError(`assumptions.growthBpsByWrapper is missing wrapper "${w}"`);
        }
    }
}
/** The liability terms in force for a given year, after any re-fix events. */
function liabilityTermsFor(l, year) {
    if (l.interestRateBps === null) {
        // Unreachable: null-rate liabilities are filtered out (with a warning)
        // before any state is built. Failing loud here keeps 0% impossible.
        throw new RangeError(`Liability "${l.label}" has no interest rate and cannot be amortised`);
    }
    let rateBps = l.interestRateBps;
    let monthlyPayment = l.monthlyPayment;
    let interestOnly = l.interestOnly;
    const changes = [...(l.rateChanges ?? [])].sort((a, b) => a.fromYear - b.fromYear);
    for (const change of changes) {
        if (change.fromYear > year)
            break;
        rateBps = change.interestRateBps;
        if (change.monthlyPayment != null)
            monthlyPayment = change.monthlyPayment;
        if (change.interestOnly !== undefined)
            interestOnly = change.interestOnly;
    }
    return { rateBps, monthlyPayment, interestOnly };
}
/**
 * How a student loan plan is NAMED to a client.
 *
 * `student-loan-year-not-stated` is a client-facing warning code, so it reaches
 * the Present stage and the report appendix. Interpolating the enum printed "A
 * plan2 student loan…" on a client's page. These are the fact-find's own
 * labels, minus the eligibility clauses that belong on a picker and not in a
 * sentence about one household.
 */
const STUDENT_LOAN_PLAN_NAMES = Object.freeze({
    plan1: 'Plan 1 student loan',
    plan2: 'Plan 2 student loan',
    plan5: 'Plan 5 student loan',
    postgrad: 'Postgraduate Loan',
});
export function projectHousehold(input, clock, rules) {
    validate(input, clock);
    // Pre-render validation gate: judge the plan BEFORE any figure is produced.
    // The findings are ATTACHED to the result rather than thrown, so a caller
    // cannot obtain numbers without also receiving the verdict on the inputs
    // they came from. Refusing is the job of the output boundaries (the API
    // routes and the report renderer, via `assertPlanProjectable`) — a
    // half-built plan must still be previewable inside the builder, but nothing
    // client-facing may be produced from one. Warnings are additionally folded
    // into the ordinary warnings array further down, once it exists.
    const planFindings = validatePlan(input, clock);
    const startYear = clock.startYear;
    const assumptions = input.assumptions;
    const growthOf = (wrapper, override) => override ?? assumptions.growthBpsByWrapper[wrapper];
    const yieldOf = (wrapper) => assumptions.yieldBpsByWrapper?.[wrapper] ?? 0;
    const feeOf = (wrapper) => assumptions.feeBpsByWrapper?.[wrapper] ?? 0;
    // ---- per-year scalar assumptions (timed schedule windows) ----------------
    // The base CPI/wage/RPI values apply everywhere except inside a schedule
    // step's window, where the step's value replaces them (last matching step
    // wins). With no schedule these collapse to the constants, and every
    // consumer below compounds year-by-year, so the no-schedule projection is
    // penny-identical to the pre-schedule engine.
    const schedule = assumptions.schedule ?? [];
    const stepCovers = (s, year) => year >= s.fromYear && (s.toYear == null || year <= s.toYear);
    const scheduledScalar = (year, pick) => {
        let v;
        for (const s of schedule) {
            const value = pick(s);
            if (value !== undefined && stepCovers(s, year))
                v = value;
        }
        return v;
    };
    // HS-3: a historical replay's economy for a year REPLACES the scheduled and
    // base values. Filled by the pre-pass below (before any consumer runs); with
    // no replay the map is empty and every resolver is exactly as before.
    const economyByYear = new Map();
    const cpiFor = (year) => economyByYear.get(year)?.economy.inflationBps ??
        scheduledScalar(year, (s) => s.inflationBps) ??
        assumptions.inflationBps;
    const wageFor = (year) => economyByYear.get(year)?.economy.wageGrowthBps ??
        scheduledScalar(year, (s) => s.wageGrowthBps) ??
        assumptions.wageGrowthBps;
    // RPI: an explicit step value wins; else the base RPI; else it follows the
    // (possibly scheduled) CPI — the same proxy rule as the constant engine.
    const rpiFor = (year) => {
        const replayed = economyByYear.get(year);
        if (replayed?.economy.rpiBps === null) {
            throw new RangeError(`RPI is not available for ${year} in "${replayed.shockLabel}", and this plan links an amount to RPI. Supply a declared proxy or leave RPI-linked items out of the comparison.`);
        }
        if (replayed?.economy.rpiBps !== undefined)
            return replayed.economy.rpiBps;
        return scheduledScalar(year, (s) => s.rpiBps) ?? assumptions.rpiBps ?? cpiFor(year);
    };
    const escCtxFor = (year) => {
        const ctx = {
            cpiBps: cpiFor(year),
            wageGrowthBps: wageFor(year),
            ...(assumptions.customIndices !== undefined
                ? { customIndices: assumptions.customIndices }
                : {}),
        };
        // RPI is read lazily: only an RPI-linked item asks for it, so a year with
        // no RPI refuses exactly the plans that need one.
        // Non-enumerable: spreading or logging the context never evaluates it.
        return Object.defineProperty(ctx, 'rpiBps', {
            enumerable: false,
            get: () => rpiFor(year),
        });
    };
    /** Scheduled growth DELTA for a wrapper in a year (0 outside any window). */
    const growthDeltaOf = (wrapper, year) => {
        let delta = 0;
        for (const s of schedule) {
            const v = s.growthDeltaBpsByWrapper?.[wrapper];
            if (v !== undefined && stepCovers(s, year))
                delta = v;
        }
        return delta;
    };
    const waterfall = input.waterfall ?? NO_SWEEP_WATERFALL;
    /** Input asset ids, so per-asset closing balances name only what the plan named. */
    const inputAssetIds = new Set(input.assets.map((a) => a.id));
    /**
     * IM-F5-01: whether any holding rebased to probate value on a death. Stated
     * in the modelling notes, because it is a number the adviser will be asked
     * about — a survivor's CGT bill visibly smaller than the gain on screen.
     */
    let probateUplifted = false;
    /** FG-F3-04: the adviser's stated survivor spending factor, if they gave one. */
    const survivorFactorBps = input.survivorSpendingFactorBps;
    /** True once any modelled death has happened — joint rows scale from then. */
    let someoneHasDied = false;
    /** FG-F6-04: a sole debt settled from the estate, named for the assumptions log. */
    /**
     * A2.5: what each person actually owned at the moment they died, captured
     * before their estate transfers. One entry per modelled death.
     */
    const deathEstates = [];
    const debtsSettledAtDeath = [];
    /** Gifts made by `spend` goal steps (isGift), for the inheritance-tax estimate's gift list. */
    const sweepGifts = [];
    /**
     * A2.7. Gifts made by a RECURRING spending line the adviser flagged — one
     * entry per year the line is active, at the amount actually given that year.
     * Collected in the spending step so the ledger figure is the same escalated
     * amount the wealth line spent, rather than the today's-value figure typed
     * into the plan.
     */
    const recurringGifts = [];
    const withdrawal = input.withdrawalWaterfall ?? DEFAULT_WITHDRAWAL_WATERFALL;
    const effAcc = new Map();
    const rateKey = (pid, w, shielded) => `${pid}:${w}:${shielded ? 1 : 0}`;
    const accumulateRate = (pid, w, shielded, value, g, y, f) => {
        const key = rateKey(pid, w, shielded);
        const a = effAcc.get(key) ?? { value: 0, gW: 0, yW: 0, fW: 0, count: 0, gS: 0, yS: 0, fS: 0 };
        a.value += value;
        a.gW += value * g;
        a.yW += value * y;
        a.fW += value * f;
        a.count += 1;
        a.gS += g;
        a.yS += y;
        a.fS += f;
        effAcc.set(key, a);
    };
    const effRate = (pid, w, shielded, pick, fallback) => {
        const a = effAcc.get(rateKey(pid, w, shielded));
        if (a === undefined)
            return fallback;
        const { weighted, sum } = pick(a);
        if (a.value > 0)
            return Math.trunc(weighted / a.value);
        if (a.count > 0)
            return Math.trunc(sum / a.count);
        return fallback;
    };
    const effGrowth = (pid, w, shielded) => effRate(pid, w, shielded, (a) => ({ weighted: a.gW, sum: a.gS }), growthOf(w, null));
    const effYield = (pid, w, shielded) => effRate(pid, w, shielded, (a) => ({ weighted: a.yW, sum: a.yS }), yieldOf(w));
    const effFee = (pid, w, shielded) => effRate(pid, w, shielded, (a) => ({ weighted: a.fW, sum: a.fS }), feeOf(w));
    const warnings = [];
    const warnedOnce = new Set();
    const warnOnce = (key, warning) => {
        if (!warnedOnce.has(key)) {
            warnedOnce.add(key);
            warnings.push(warning);
        }
    };
    // Non-blocking findings from the pre-render gate (computed at entry, before
    // any figure existed) join the ordinary warnings so they travel with the
    // result to the builder preview, the API and the report's assumptions page.
    // A selected surplus sweep is a BEHAVIOUR the projection assumes, so it is
    // stated in words on the assumptions page rather than left to be inferred
    // from contributions appearing in the year rows. Only raised when the sweep
    // actually places surplus into a tax wrapper — accumulating in cash is the
    // neutral default and asserts nothing.
    const placementPhrase = (step) => {
        if (step.target === 'jisa') {
            const dep = (input.dependents ?? []).find((d) => d.id === step.dependentId);
            const child = dep?.label ?? 'the child';
            const cap = step.annualCap === null
                ? `into ${child}'s Junior ISA up to the Junior ISA annual limit`
                : `up to ${gbpWhole(step.annualCap)} a year${step.escalation !== undefined ? ', rising each year,' : ''} into ${child}'s Junior ISA`;
            return `${cap} when there is room`;
        }
        if (step.target === 'spend') {
            const when = step.startYear !== undefined && step.startYear === step.endYear
                ? ` in ${step.startYear}`
                : ' a year';
            return `up to ${gbpWhole(step.annualCap ?? 0)}${when} towards "${step.label ?? 'a stated spend'}" when there is room`;
        }
        if (step.target === 'liability') {
            const l = input.liabilities.find((x) => x.id === step.liabilityId);
            return `up to ${gbpWhole(step.annualCap ?? 0)} a year overpaying "${l?.label ?? 'a debt'}" when there is room`;
        }
        if (step.target === 'pension' && step.personId !== undefined) {
            return `up to ${gbpWhole(step.annualCap ?? 0)} a year into the ${step.personId}'s pension when there is room`;
        }
        const capped = step.annualCap === null
            ? step.target === 'gia'
                ? ''
                : ' up to its statutory annual limit'
            : ` up to ${gbpWhole(step.annualCap)} a year`;
        return `${SWEEP_TARGET_WORDS[step.target] ?? step.target}${capped}`;
    };
    const sweepSteps = waterfall.steps.filter((s) => s.target !== 'cash');
    if (sweepSteps.length > 0) {
        const buffer = waterfall.steps.find((s) => s.target === 'cash' && s.targetBalanceMonthsOfSpending !== undefined);
        // Said per step, with each step's OWN cap: a goal-placed Junior ISA step
        // is capped at the goal's yearly amount, not the statutory limit, and
        // whatever the steps do not take stays in the account (the engine's
        // default) — so neither "invested each year" nor "each up to its
        // statutory limit" may be asserted of a plan that only has a goal step.
        warnOnce('surplus-sweep-active', {
            code: 'surplus-sweep-active',
            message: 'This projection assumes surplus cash flow is placed each year in this order: ' +
                (buffer !== undefined
                    ? `first building a cash buffer of ${buffer.targetBalanceMonthsOfSpending} months of spending, then `
                    : '') +
                `${sweepSteps.map(placementPhrase).join(', then ')}. Anything left stays in the account. ` +
                'This is a modelling choice that was selected for this plan, not a recommendation to do it.',
            year: null,
            itemLabel: null,
        });
    }
    for (const finding of planFindings) {
        // Blocking findings already threw above, so only the warning codes — which
        // are all members of HouseholdWarningCode — can reach this point.
        if (finding.severity !== 'warning')
            continue;
        warnOnce(`validation:${finding.code}:${finding.path}`, {
            code: finding.code,
            message: finding.message,
            year: null,
            itemLabel: finding.itemLabel,
        });
    }
    // ---- projection window ---------------------------------------------------
    const clientInput = input.people.find((p) => p.id === 'client');
    const maxYears = MAX_PROJECTION_AGE - clientInput.currentAge + 1;
    let horizonYears;
    if (assumptions.horizonYears !== undefined) {
        horizonYears = assumptions.horizonYears;
    }
    else if (input.window?.endYear != null) {
        horizonYears = input.window.endYear - startYear + 1;
    }
    else {
        const endAge = input.window?.endAtClientAge ?? DEFAULT_END_AT_CLIENT_AGE;
        horizonYears = endAge - clientInput.currentAge + 1;
    }
    if (horizonYears > maxYears) {
        horizonYears = maxYears;
        warnOnce('horizon-clamped', {
            code: 'horizon-clamped',
            message: `The projection window was clamped at the hard maximum client age of ${MAX_PROJECTION_AGE}.`,
            year: null,
            itemLabel: null,
        });
    }
    if (horizonYears < 1) {
        throw new RangeError('The projection window resolves to zero years');
    }
    // HS-3 pre-pass: a replay's per-year economy must be in place before the
    // first consumer of CPI/RPI/wages (planned-event expansion, just below).
    // Anchors resolve exactly as the market-shock block further down resolves
    // them; that block owns the warnings, this one only records values. Two
    // shocks setting the same economic value for the same year would stack one
    // history on another, so that is refused.
    const shockAnchorYear = (shock) => {
        const anchor = shock.anchor;
        if ('year' in anchor)
            return anchor.year;
        const years = input.people
            .filter((p) => anchor.person === 'firstToRetire' || p.id === anchor.person)
            .map((p) => startYear + (p.retirementAge - p.currentAge));
        return Math.max(startYear, Math.min(...years)) + anchor.yearsAfterRetirement;
    };
    for (const shock of input.marketShocks ?? []) {
        if (shock.path === undefined || shock.anchor === undefined)
            continue;
        const anchorYear = shockAnchorYear(shock);
        for (const step of shock.path) {
            if (step.economy === undefined)
                continue;
            const year = anchorYear + step.offsetYears;
            // One historical economy per year: a second shock setting ANY economic
            // value for the same year would splice two histories together.
            const existing = economyByYear.get(year);
            if (existing !== undefined) {
                throw new RangeError(`Market shocks "${existing.shockLabel}" and "${shock.label}" both set the ${year} economy — one historical path cannot be stacked on another`);
            }
            economyByYear.set(year, {
                shockId: shock.id,
                shockLabel: shock.label,
                economy: step.economy,
            });
        }
    }
    // ---- Stage 5 expansion: planned events become ordinary primitives -------
    const expanded = expandPlannedEvents(input, startYear, horizonYears, escCtxFor, rules);
    const allSpending = [...input.spending, ...expanded.spending];
    const allLifeEvents = [
        ...(input.lifeEvents ?? []),
        ...expanded.lifeEvents,
    ];
    const allIncomeStreams = [
        ...input.incomeStreams,
        ...expanded.incomeStreams,
    ];
    const suppressions = expanded.suppressions;
    const isSuppressed = (personId, year) => suppressions.some((w) => w.personId === personId && year >= w.fromYear && year <= w.toYear);
    for (const w of expanded.warnings) {
        warnOnce(w.key, { code: w.code, message: w.message, year: null, itemLabel: null });
    }
    // Plan-completeness prompts: flag a plan where nothing takes anything away
    // (a fact-find gap that makes projections look unrealistically high).
    // Computed purely from the raw inputs, the resolved window, and the
    // expanded planned-event spending (so event windows are judged precisely).
    for (const w of checkPlanCompleteness(input, startYear, startYear + horizonYears - 1, expanded.spending)) {
        warnOnce(`completeness:${w.code}`, w);
    }
    // ---- market shocks: per-year per-wrapper return reductions ---------------
    // Shocks landing in the same year stack additively; the growth sites clamp
    // the write-down so no pot goes below zero. A shock outside the projection
    // window would otherwise be silently ignored — warn instead.
    //
    // C1.1: an anchored signed path is resolved HERE, once — the anchor year
    // comes from the people as this projection sees them (a scenario overlay
    // that moved a retirement age has already been applied), and each step is
    // written into the same per-year map as a legacy fall, so the growth sites
    // never look up a retirement age. A legacy `year` + `dropBpsByWrapper` is
    // exactly a single step at offset 0 with the drop negated — byte-identical.
    const shocksByYear = new Map();
    const replacementsByYear = new Map();
    const ratePathByLiability = new Map();
    const lastYear = startYear + horizonYears - 1;
    const retirementYearOf = (person) => {
        const years = input.people
            .filter((p) => person === 'firstToRetire' || p.id === person)
            .map((p) => startYear + (p.retirementAge - p.currentAge));
        return Math.min(...years);
    };
    const marketShockWindows = [];
    for (const shock of input.marketShocks ?? []) {
        let resolvedAnchorYear = null;
        const steps = shock.path !== undefined && shock.anchor !== undefined
            ? (() => {
                let anchorYear;
                if ('year' in shock.anchor) {
                    anchorYear = shock.anchor.year;
                }
                else {
                    const retired = retirementYearOf(shock.anchor.person);
                    // A person already retired before the projection starts has no
                    // retirement year inside it: count from the first projected year
                    // and SAY so, rather than clamp silently.
                    if (retired < startYear) {
                        warnOnce(`market-shock-anchor-before-start:${shock.id}`, {
                            code: 'market-shock-anchor-before-start',
                            message: `Market shock "${shock.label}" is anchored to a retirement (${retired}) before the projection starts; it is counted from the first projected year, ${startYear}.`,
                            year: null,
                            itemLabel: shock.label,
                        });
                    }
                    anchorYear = Math.max(startYear, retired) + shock.anchor.yearsAfterRetirement;
                }
                resolvedAnchorYear = anchorYear;
                return [...shock.path]
                    .sort((a, b) => a.offsetYears - b.offsetYears)
                    .map((s) => ({
                    year: anchorYear + s.offsetYears,
                    deltaBpsByWrapper: s.returnDeltaBpsByWrapper,
                    ...(s.returnBpsByWrapper !== undefined
                        ? { replaceBpsByWrapper: s.returnBpsByWrapper }
                        : {}),
                }));
            })()
            : [
                {
                    year: shock.year ?? 0,
                    deltaBpsByWrapper: Object.fromEntries(Object.entries(shock.dropBpsByWrapper ?? {}).map(([w, d]) => [w, -d])),
                },
            ];
        if (resolvedAnchorYear !== null) {
            for (const lp of shock.liabilityRatePaths ?? []) {
                if (ratePathByLiability.has(lp.liabilityId)) {
                    throw new RangeError(`Two market shocks set a rate path for the same liability ("${shock.label}" and "${ratePathByLiability.get(lp.liabilityId).shockLabel}")`);
                }
                ratePathByLiability.set(lp.liabilityId, {
                    shockLabel: shock.label,
                    reamortise: lp.reamortise,
                    byYear: new Map(lp.steps.map((s) => [resolvedAnchorYear + s.offsetYears, s.interestRateBps])),
                });
            }
        }
        const inside = steps.filter((s) => s.year >= startYear && s.year <= lastYear);
        if (inside.length === 0) {
            const first = steps[0].year;
            const last = steps[steps.length - 1].year;
            warnOnce(`market-shock-outside-window:${shock.id}`, {
                code: 'market-shock-outside-window',
                message: steps.length === 1
                    ? `Market shock "${shock.label}" is dated ${first}, outside the projected window — it has no effect on this projection.`
                    : `Market shock "${shock.label}" runs ${first}–${last}, outside the projected window — it has no effect on this projection.`,
                year: null,
                itemLabel: shock.label,
            });
            continue;
        }
        if (inside.length < steps.length) {
            // A distinct code: this shock DOES take effect, and no consumer may
            // read it as "no effect" (the code above means exactly that).
            warnOnce(`market-shock-partly-outside-window:${shock.id}`, {
                code: 'market-shock-partly-outside-window',
                message: `Market shock "${shock.label}" runs partly outside the projected window — only the years ${inside[0].year}–${inside[inside.length - 1].year} take effect.`,
                year: null,
                itemLabel: shock.label,
            });
        }
        const isReplay = (shock.path ?? []).some((st) => st.returnBpsByWrapper !== undefined || st.economy !== undefined);
        marketShockWindows.push({
            id: shock.id,
            label: shock.label,
            years: inside.map((s) => s.year),
            ...(isReplay ? { replay: true } : {}),
        });
        for (const step of inside) {
            const forYear = shocksByYear.get(step.year) ?? {};
            for (const [wrapper, delta] of Object.entries(step.deltaBpsByWrapper)) {
                // Stored as the REDUCTION the growth sites subtract: a fall is a
                // positive reduction, a recovery year a negative one.
                forYear[wrapper] = (forYear[wrapper] ?? 0) - delta;
            }
            shocksByYear.set(step.year, forYear);
            for (const [wrapper, total] of Object.entries(step.replaceBpsByWrapper ?? {})) {
                const replaced = replacementsByYear.get(step.year) ?? {};
                if (replaced[wrapper] !== undefined) {
                    throw new RangeError(`Two market shocks replace the ${step.year} return for "${wrapperLabel(wrapper)}" — one historical path cannot be stacked on another`);
                }
                replaced[wrapper] = total;
                replacementsByYear.set(step.year, replaced);
                const economyOwner = economyByYear.get(step.year);
                if (economyOwner !== undefined && economyOwner.shockId !== shock.id) {
                    throw new RangeError(`"${shock.label}" replaces ${step.year} returns while "${economyOwner.shockLabel}" sets that year's economy — one historical path cannot be stacked on another`);
                }
            }
        }
    }
    // HS-3: a replaced return and an adjusted return for the same wrapper and
    // year would apply the year twice (a historical return plus a stress delta
    // on top), whichever shock came first — refused, never summed.
    for (const [year, replaced] of replacementsByYear) {
        for (const wrapper of Object.keys(replaced)) {
            if (shocksByYear.get(year)?.[wrapper] !== undefined) {
                throw new RangeError(`A historical return replaces the ${year} return for "${wrapperLabel(wrapper)}" and another market shock also adjusts it — the year would be applied twice`);
            }
        }
        if (replaced.cash_isa !== undefined &&
            economyByYear.get(year)?.economy.cashRateBps !== undefined) {
            throw new RangeError(`Cash ISA interest for ${year} is set both by a cash rate and by a total return`);
        }
    }
    const shockOf = (wrapper, year) => shocksByYear.get(year)?.[wrapper] ?? 0;
    /** HS-3: the year's replacement total return for a wrapper, if a replay sets one. */
    const replacementOf = (wrapper, year) => replacementsByYear.get(year)?.[wrapper];
    /**
     * HS-3: how far a replay's savings rate moves a cash wrapper's interest this
     * year — the replayed rate less the wrapper's base growth, so an account with
     * its own rate keeps its spread. 0 with no replay.
     */
    const cashRateShiftOf = (wrapper, year) => {
        const rate = economyByYear.get(year)?.economy.cashRateBps;
        return rate === undefined ? 0 : rate - assumptions.growthBpsByWrapper[wrapper];
    };
    // ---- initial state -------------------------------------------------------
    const people = new Map();
    for (const p of input.people) {
        people.set(p.id, {
            input: p,
            alive: true,
            open: emptyPots(),
            shielded: emptyPots(),
            dcPots: [],
            lsaUsed: 0,
            badrUsed: 0,
            mpaaTriggered: false,
            s24CarriedForward: 0,
            unusedAllowances: [],
        });
    }
    /** Client first, then partner — a stable, documented order. */
    const orderedPeople = [...people.values()].sort((a, b) => a.input.id === b.input.id ? 0 : a.input.id === 'client' ? -1 : 1);
    const survivorOf = (dead) => orderedPeople.find((s) => s.input.id !== dead && s.alive) ?? null;
    const aliveOwners = (ownerId) => {
        if (ownerId === 'joint')
            return orderedPeople.filter((p) => p.alive);
        const owner = people.get(ownerId);
        return owner && owner.alive ? [owner] : [];
    };
    /** Split `total` across holders; the first takes any odd pence remainder. */
    const splitAcross = (total, count, index) => {
        const each = Math.trunc(total / count);
        return index === 0 ? total - each * (count - 1) : each;
    };
    const ageIn = (p, year) => p.currentAge + (year - startYear);
    const properties = [];
    /** Properties created by house-purchase life events during the projection. */
    const eventProperties = [];
    /** Business interests and other assets, tracked one holding at a time. */
    const trackedHoldings = [];
    /** Junior ISAs — the CHILD's assets: household-funded, never drawn. */
    const jisaHoldings = [];
    const nsiMaxHolding = rules.nsi?.premiumBondMaxHolding ?? NSI_MAX_HOLDING_FALLBACK;
    const nmpa = rules.pension.normalMinimumPensionAge ?? 55;
    for (const asset of input.assets) {
        const gRate = asset.growthRateBps ?? growthOf(asset.wrapper, null);
        const yRate = asset.yieldBps ?? yieldOf(asset.wrapper);
        const fRate = asset.feeDragBps ?? feeOf(asset.wrapper);
        // A per-asset override above the plausibility ceiling is never silent:
        // compounded over a long horizon it dominates every other number on the
        // screen (docs/engine/projection-runaway.md). The contract hard-caps at
        // ±10,000 bps; this flags the 2,000–10,000 band for the adviser.
        // A GIA is the one wrapper where the income yield carries TAX, not just
        // return: dividends are taxable as they arise (step 5b), while capital
        // growth is an unrealised gain until disposal. Since the shipped presets
        // stopped supplying a yield (D-005) a GIA models NO dividend income unless
        // an adviser sets one — which silently understates the tax on a taxable
        // account, by ~£100k–£560k of lifetime tax across the golden fixtures that
        // hold one. Correct on the figures given, and not something a client report
        // may state without saying so.
        //
        // Scoped to the GIA deliberately: inside an ISA, LISA, JISA or pension the
        // split changes nothing at all, so warning there would be noise that trains
        // advisers to ignore the code.
        if (asset.wrapper === 'gia' && yRate === 0 && asset.currentValue > 0) {
            warnOnce(`gia-no-yield:${asset.id}`, {
                code: 'gia-no-dividend-yield',
                message: `"${asset.label}" models no dividend income, so no dividend tax is estimated on it. Capital growth is still taxed as a gain on disposal. If the holding pays dividends or interest, set an income yield on the asset so the tax is modelled.`,
                year: null,
                itemLabel: asset.label,
            });
        }
        if (gRate > PLAUSIBLE_GROWTH_CEILING_BPS) {
            warnOnce(`implausible-growth:${asset.id}`, {
                code: 'implausible-growth-rate',
                message: `"${asset.label}" is set to grow at ${(gRate / 100).toFixed(1)}% a year — above the ${PLAUSIBLE_GROWTH_CEILING_BPS / 100}% plausibility ceiling. Compounded over the plan this rate will dominate the projection; please confirm it is intentional.`,
                year: null,
                itemLabel: asset.label,
            });
        }
        // Contributions only flow into wrappers that model them; anything else
        // would silently vanish from the adviser's figures — warn instead.
        if (asset.contribution > 0 &&
            (asset.wrapper === 'property' ||
                asset.wrapper === 'business' ||
                asset.wrapper === 'other_asset' ||
                asset.wrapper === 'db_pension' ||
                asset.wrapper === 'state_pension')) {
            warnOnce(`contribution-ignored:${asset.id}`, {
                code: 'asset-not-modelled',
                message: `Regular contributions into "${asset.label}" (${wrapperLabel(asset.wrapper)}) are not modelled and were ignored — model additions to this asset as one-off events or adjust its value.`,
                year: null,
                itemLabel: asset.label,
            });
        }
        if (asset.wrapper === 'property') {
            properties.push({
                asset,
                value: asset.currentValue,
                baseCost: asset.costBasis ?? asset.currentValue,
                owner: asset.ownerId,
                active: true,
                sold: false,
            });
            continue;
        }
        if (asset.wrapper === 'db_pension' || asset.wrapper === 'state_pension') {
            // Income promises, not spendable capital: modelled via income streams.
            if (asset.currentValue > 0) {
                warnOnce(`value-ignored:${asset.id}`, {
                    code: 'asset-not-modelled',
                    message: `"${asset.label}" (${wrapperLabel(asset.wrapper)}) is an income promise, not spendable capital: its ${gbpWhole(asset.currentValue)} value is NOT counted in net worth. Model the benefit as an income stream.`,
                    year: null,
                    itemLabel: asset.label,
                });
            }
            continue;
        }
        if (asset.wrapper === 'business' || asset.wrapper === 'other_asset') {
            trackedHoldings.push({
                asset,
                owner: asset.ownerId,
                value: asset.currentValue,
                baseCost: asset.costBasis ?? null,
            });
            continue;
        }
        if (asset.wrapper === 'jisa') {
            jisaHoldings.push({
                asset,
                dependentId: asset.dependentId,
                value: asset.currentValue,
            });
            continue;
        }
        const holders = asset.ownerId === 'joint' ? orderedPeople : [people.get(asset.ownerId)];
        holders.forEach((h, i) => {
            const shielded = asset.neverDrawFrom === true;
            const set = shielded ? h.shielded : h.open;
            const value = splitAcross(asset.currentValue, holders.length, i);
            if (asset.wrapper === 'dc_pension') {
                // Each DC asset is its OWN pot with its own rates and access age —
                // EXCEPT an inherited one, which is drawable from the outset.
                const isBeneficiaryDrawdown = asset.dcSubType === 'beneficiary_drawdown';
                const accessAge = isBeneficiaryDrawdown ? 0 : (asset.accessAge ?? h.input.retirementAge);
                if (!isBeneficiaryDrawdown && accessAge < nmpa) {
                    warnOnce(`nmpa:${asset.id}`, {
                        code: 'dc-access-below-nmpa',
                        message: `"${asset.label}" is set to be accessed at ${accessAge}, below the normal minimum pension age of ${nmpa}. The projection honours the entered age, but this is only possible with a protected pension age or ill-health access.`,
                        year: null,
                        itemLabel: asset.label,
                    });
                }
                h.dcPots.push({
                    id: asset.id,
                    label: asset.label,
                    accessAge,
                    isBeneficiaryDrawdown,
                    shielded,
                    // An inherited pot is ALREADY crystallised, so its whole value sits
                    // in drawdown from the start. Placing it in `uncrystallised` would
                    // hand the beneficiary a second 25% tax-free lump sum the pot cannot
                    // pay — the money was crystallised on the original member's death.
                    uncrystallised: isBeneficiaryDrawdown ? 0 : value,
                    drawdown: isBeneficiaryDrawdown ? value : 0,
                    growthBps: gRate,
                    yieldBps: yRate,
                    feeBps: fRate,
                });
                return;
            }
            accumulateRate(h.input.id, asset.wrapper, shielded, value, gRate, yRate, fRate);
            switch (asset.wrapper) {
                case 'cash':
                    set.cash += value;
                    break;
                case 'cash_isa':
                    set.cashIsa += value;
                    break;
                case 'stocks_isa':
                    set.stocksIsa += value;
                    break;
                case 'lisa':
                    set.lisa += value;
                    break;
                case 'nsi':
                    set.nsi += value;
                    break;
                case 'gia':
                    set.gia.value += value;
                    set.gia.cost += splitAcross(asset.costBasis ?? asset.currentValue, holders.length, i);
                    break;
                default:
                    break;
            }
        });
    }
    // An opening premium-bond balance above the product maximum is honoured
    // (it is the client's factual holding) but flagged.
    for (const p of orderedPeople) {
        if (p.open.nsi + p.shielded.nsi > nsiMaxHolding) {
            warnOnce(`nsi-opening:${p.input.id}`, {
                code: 'nsi-cap-applied',
                message: `The ${p.input.id}'s NS&I premium-bond holdings start above the ${gbpWhole(nsiMaxHolding)} maximum per person; no further contributions or reinvested winnings will be added above the cap.`,
                year: null,
                itemLabel: null,
            });
        }
    }
    // A liability whose rate is still null is a fact-find row, not a modellable
    // debt: it enters no arithmetic (no interest, no payments, no
    // liabilitiesOutstanding) and instead stands as a warning until the adviser
    // provides the rate. Modelling it at 0% would silently flatter the plan.
    for (const l of input.liabilities) {
        if (l.interestRateBps !== null)
            continue;
        warnOnce(`rate-needed:${l.id}`, {
            code: 'liability-rate-needed',
            message: `An interest rate is needed for "${l.label}" — its ${gbpWhole(l.balance)} balance and its payments are not included in this projection until one is provided, so the figures shown do not yet reflect this debt.`,
            year: null,
            itemLabel: l.label,
        });
    }
    const liabilities = input.liabilities
        .filter((l) => l.interestRateBps !== null)
        .map((l) => {
        if (!Number.isSafeInteger(l.balance) || l.balance < 0) {
            throw new RangeError(`Liability "${l.label}" balance must be non-negative integer pence`);
        }
        return { input: l, balance: l.balance };
    });
    const liabilityStarted = (l, year) => l.input.startYear == null || year >= l.input.startYear;
    /**
     * HS-3: the terms in force once a historical replay's rate path is laid over
     * the liability's own. Path years take the path's rate; other years keep the
     * liability's terms. With `reamortise`, the payment is recalculated over the
     * years left to `endYear` whenever the rate in force changes (so a reset —
     * and the return to the contract's own rate afterwards — reaches the
     * payment); a liability the path never reached is untouched, byte for byte.
     */
    const replayPayment = new Map();
    const replayedTerms = (l, own, year) => {
        const path = ratePathByLiability.get(l.input.id);
        if (path === undefined)
            return own;
        const onPath = path.byYear.get(year);
        const rateBps = onPath ?? own.rateBps;
        const previous = replayPayment.get(l.input.id);
        if (onPath === undefined && previous === undefined)
            return own;
        if (!path.reamortise)
            return { ...own, rateBps };
        if (previous !== undefined && previous.rateBps === rateBps) {
            return { ...own, rateBps, monthlyPayment: previous.monthlyPayment };
        }
        const termYears = l.input.endYear === null ? 1 : l.input.endYear - year + 1;
        const payment = levelMonthlyPayment(pence(l.balance), rateBps, termYears, own.interestOnly) ??
            own.monthlyPayment;
        replayPayment.set(l.input.id, { rateBps, monthlyPayment: payment });
        return { ...own, rateBps, monthlyPayment: payment };
    };
    // Planned property purchases: the property (inactive until its purchase
    // year) and its mortgage (startYear machinery) are created up front; the
    // deposit + stamp-duty OUTFLOW was expanded into a life event.
    for (const plan of input.plannedEvents ?? []) {
        if (plan.kind !== 'propertyPurchase')
            continue;
        const owner = plan.ownerId ?? (input.people.length > 1 ? 'joint' : 'client');
        const mortgageBalance = plan.price - plan.deposit;
        const mortgageId = `${plan.id}:mortgage`;
        if (mortgageBalance > 0) {
            liabilities.push({
                input: {
                    id: mortgageId,
                    label: `${plan.label} — mortgage`,
                    kind: 'mortgage',
                    balance: pence(mortgageBalance),
                    interestRateBps: plan.mortgageRateBps,
                    monthlyPayment: plan.mortgageMonthlyPayment,
                    interestOnly: plan.mortgageInterestOnly ?? false,
                    endYear: plan.mortgageEndYear ?? null,
                    startYear: plan.year,
                },
                balance: mortgageBalance,
            });
        }
        properties.push({
            asset: {
                id: plan.id,
                label: plan.label,
                ownerId: owner,
                wrapper: 'property',
                currentValue: plan.price,
                contribution: pence(0),
                contributionFrequency: Frequency.Annually,
                growthRateBps: plan.growthRateBps ?? null,
                property: {
                    isBuyToLet: plan.isBuyToLet ?? false,
                    ...(plan.isPrimaryResidence !== undefined
                        ? { isPrimaryResidence: plan.isPrimaryResidence }
                        : {}),
                    rentalYieldBps: plan.rentalYieldBps ?? 0,
                    linkedLiabilityId: mortgageBalance > 0 ? mortgageId : null,
                },
            },
            value: plan.price,
            fromYear: plan.year,
            baseCost: plan.price,
            owner,
            active: false,
            sold: false,
        });
    }
    const streamStartYear = (stream) => {
        const owner = people.get(stream.ownerId).input;
        if (stream.startYear !== null)
            return stream.startYear;
        // §0.2b age anchor: start when the owner reaches `startAge`.
        if (stream.startAge != null)
            return startYear + Math.max(0, stream.startAge - owner.currentAge);
        if (stream.type === 'state_pension') {
            return startYear + Math.max(0, owner.statePensionAge - owner.currentAge);
        }
        if (stream.type === 'db_pension' || stream.type === 'dc_pension') {
            return startYear + Math.max(0, owner.retirementAge - owner.currentAge);
        }
        return startYear;
    };
    const streamGrowthFor = (stream, year) => {
        if (stream.growthRateBps !== null)
            return stream.growthRateBps;
        if (stream.type === 'employment' || stream.type === 'self_employment') {
            return wageFor(year);
        }
        if (stream.type === 'state_pension') {
            // Triple-lock proxy = max(CPI, earnings, 2.5%), unless overridden —
            // evaluated with THAT year's (possibly scheduled) CPI and earnings.
            return statePensionEscalationBps(assumptions.statePensionEscalation, {
                cpiBps: cpiFor(year),
                wageGrowthBps: wageFor(year),
            });
        }
        return cpiFor(year);
    };
    const streamActive = (stream, year) => {
        const from = Math.max(streamStartYear(stream), startYear);
        if (year < from || (stream.endYear !== null && year > stream.endYear))
            return false;
        if (stream.frequency === Frequency.Once && year !== from)
            return false;
        const owner = people.get(stream.ownerId);
        // §0.2b age anchor: stop once the owner is past `endAge`.
        if (stream.endAge != null && ageIn(owner.input, year) > stream.endAge)
            return false;
        if (stream.endsAtRetirement && ageIn(owner.input, year) >= owner.input.retirementAge) {
            return false;
        }
        // A career break pauses earnings (only) for the window.
        if ((stream.type === 'employment' || stream.type === 'self_employment') &&
            isSuppressed(stream.ownerId, year)) {
            return false;
        }
        return true;
    };
    const streamAmount = (stream, year) => {
        // Stream amounts are TODAY'S VALUE (household-types.ts), so escalation
        // compounds from the PROJECTION START — a state pension starting at SPA
        // in n years arrives n years of triple-lock higher, exactly as planned
        // events already price today's-value amounts (planned-events.ts). Each
        // elapsed year compounds at THAT year's rate so schedule windows apply.
        let acc = annualAmount(stream.amount, stream.frequency);
        for (let y = startYear + 1; y <= year; y += 1) {
            acc = compound(acc, streamGrowthFor(stream, y), 1);
        }
        return acc;
    };
    const years = [];
    const trace = [];
    let depletionYear = null;
    /** Running total of dropped-spending shortfalls — surfaced, never hidden. */
    let cumulativeShortfall = 0;
    /** End-of-year illiquid balances the first time spending goes unmet. */
    let illiquidAtFirstShortfall = null;
    /** Cumulative price deflator, parts-per-million; advances at each year end. */
    let deflatorPpm = DEFLATOR_PPM_START;
    const totalAssets = () => {
        let total = 0;
        for (const p of orderedPeople) {
            total += potSetTotal(p.open) + potSetTotal(p.shielded) + dcTotal(p);
        }
        for (const prop of properties) {
            if (prop.active)
                total += prop.value;
        }
        for (const prop of eventProperties)
            total += prop.value;
        for (const h of trackedHoldings)
            total += h.value;
        for (const j of jisaHoldings)
            total += j.value;
        return total;
    };
    // ---- annual loop ---------------------------------------------------------
    for (let offset = 0; offset < horizonYears; offset += 1) {
        const year = startYear + offset;
        const acc = new Map();
        for (const p of people.values())
            acc.set(p.input.id, newAcc());
        const accOf = (id) => acc.get(id);
        // Audit counters — every external flow this year, in pence.
        const openingAssets = totalAssets();
        let openingDebt = 0;
        let debtDrawn = 0;
        for (const l of liabilities) {
            if (l.settled === true || !liabilityStarted(l, year))
                continue;
            if (l.input.startYear != null && l.input.startYear === year)
                debtDrawn += l.balance;
            else
                openingDebt += l.balance;
        }
        let flowReliefTopUp = 0;
        let flowLifeEventIn = 0;
        let flowLifeEventOut = 0;
        let flowFees = 0;
        let flowGrowth = 0;
        let debtInterest = 0;
        let scheduledPensionGross = 0;
        // ---- Stage 7 trace collectors (pure recording — they never change a
        // number). Pot lines cover the per-person pooled wrappers + DC pensions;
        // property/business/other/JISA movements appear in the audit row and §5.
        const TRACE_WRAPPERS = [
            'cash',
            'cash_isa',
            'stocks_isa',
            'lisa',
            'nsi',
            'gia',
            'dc_pension',
        ];
        const personWrapperTotals = (p) => ({
            cash: p.open.cash + p.shielded.cash,
            cash_isa: p.open.cashIsa + p.shielded.cashIsa,
            stocks_isa: p.open.stocksIsa + p.shielded.stocksIsa,
            lisa: p.open.lisa + p.shielded.lisa,
            nsi: p.open.nsi + p.shielded.nsi,
            gia: p.open.gia.value + p.shielded.gia.value,
            dc_pension: dcTotal(p),
        });
        const traceIncome = new Map();
        const addIncomeLine = (pid, line) => {
            const list = traceIncome.get(pid) ?? [];
            list.push(line);
            traceIncome.set(pid, list);
        };
        const zeroByWrapper = () => ({
            cash: 0,
            cash_isa: 0,
            stocks_isa: 0,
            lisa: 0,
            nsi: 0,
            gia: 0,
            dc_pension: 0,
        });
        const traceDrawn = new Map();
        const traceDrawTax = new Map();
        /** Scheduled dc_pension income vs unscheduled (marginal-estimate) draws
         * are recorded separately so the trace never misattributes the tax. */
        const traceSchedDrawn = new Map();
        const addDrawn = (pid, w, gross, tax = 0, scheduled = false) => {
            if (gross <= 0)
                return;
            const rec = traceDrawn.get(pid) ?? zeroByWrapper();
            rec[w] += gross;
            traceDrawn.set(pid, rec);
            if (tax > 0)
                traceDrawTax.set(pid, (traceDrawTax.get(pid) ?? 0) + tax);
            if (scheduled)
                traceSchedDrawn.set(pid, (traceSchedDrawn.get(pid) ?? 0) + gross);
        };
        const traceContrib = new Map();
        const addTraceContrib = (pid, w, amount) => {
            if (amount <= 0)
                return;
            const rec = traceContrib.get(pid) ?? zeroByWrapper();
            rec[w] += amount;
            traceContrib.set(pid, rec);
        };
        const traceGrowthBy = new Map();
        const traceFeesBy = new Map();
        const addTraceGrowth = (pid, w, growth, fees) => {
            const g = traceGrowthBy.get(pid) ?? zeroByWrapper();
            g[w] += growth;
            traceGrowthBy.set(pid, g);
            const f = traceFeesBy.get(pid) ?? zeroByWrapper();
            f[w] += fees;
            traceFeesBy.set(pid, f);
        };
        const traceOpening = new Map();
        for (const p of orderedPeople)
            traceOpening.set(p.input.id, personWrapperTotals(p));
        // Household-level trace collectors for the non-pooled wrappers (property,
        // business, other assets, children's JISAs). Captured BEFORE planned
        // purchases activate, so a purchase shows up in otherMovements.
        const HH_TRACE_WRAPPERS = ['property', 'business', 'other_asset', 'jisa'];
        const householdWrapperTotals = () => {
            const t = {
                property: 0,
                business: 0,
                other_asset: 0,
                jisa: 0,
            };
            for (const prop of properties)
                if (prop.active)
                    t.property += prop.value;
            for (const prop of eventProperties)
                t.property += prop.value;
            for (const h of trackedHoldings) {
                if (h.asset.wrapper === 'business')
                    t.business += h.value;
                else
                    t.other_asset += h.value;
            }
            for (const j of jisaHoldings)
                t.jisa += j.value;
            return t;
        };
        const hhZero = () => ({
            property: 0,
            business: 0,
            other_asset: 0,
            jisa: 0,
        });
        const hhOpening = householdWrapperTotals();
        const hhContrib = hhZero();
        const hhGrowth = hhZero();
        const hhFees = hhZero();
        // 1. ages / status
        const clientAge = ageIn(people.get('client').input, year);
        // 1.5 planned transactions anchored to the START of the tax year:
        // property purchases activate (the asset appears; its deposit/SDLT
        // outflow is a same-year life event and its mortgage starts via the
        // liability startYear machinery), then property/business sales complete
        // BEFORE rent, interest and growth accrue for the year.
        let saleDebtCleared = 0;
        /** Per-liability payments this year (Stage 7 trace) — see TraceDebtLine. */
        const debtLines = [];
        for (const prop of properties) {
            if (prop.fromYear === year && !prop.sold) {
                prop.active = true;
                flowLifeEventIn += prop.value;
            }
        }
        for (const plan of input.plannedEvents ?? []) {
            if (plan.kind === 'propertySale' && plan.year === year) {
                const prop = properties.find((c) => c.asset.id === plan.propertyId);
                if (prop === undefined) {
                    throw new RangeError(`Sale "${plan.label}" names an unknown property "${plan.propertyId}"`);
                }
                if (!prop.active) {
                    // Defensive: sale-before-purchase and double sales are rejected in
                    // validate(), so this should be unreachable — but a planned sale
                    // must NEVER silently do nothing.
                    warnOnce(`sale-skipped:${plan.id}`, {
                        code: 'planned-sale-skipped',
                        message: `The planned sale "${plan.label}" (${year}) did not happen: the property is not held in that year.`,
                        year,
                        itemLabel: plan.label,
                    });
                    continue;
                }
                const sellingCosts = bpsOf(prop.value, plan.sellingCostsBps ?? 0);
                const netProceeds = prop.value - sellingCosts;
                flowFees += sellingCosts;
                // Clear the whole linked mortgage from the proceeds; a shortfall
                // (negative equity) is funded through the deficit machinery.
                let mortgageCleared = 0;
                const linkedId = prop.asset.property?.linkedLiabilityId ?? null;
                if (linkedId !== null) {
                    const linked = liabilities.find((l) => l.input.id === linkedId);
                    if (linked !== undefined && liabilityStarted(linked, year) && linked.balance > 0) {
                        mortgageCleared = linked.balance;
                        linked.balance = 0;
                        debtLines.push(Object.freeze({
                            liabilityId: linked.input.id,
                            label: linked.input.label,
                            opening: pence(mortgageCleared),
                            interest: pence(0),
                            paid: pence(mortgageCleared),
                            closing: pence(0),
                            cleared: 'sale',
                        }));
                    }
                }
                saleDebtCleared += mortgageCleared;
                // Full net proceeds arrive in cash; the cleared mortgage flows out
                // through debtService in this year's cashflow (negative equity is
                // then funded by the ordinary deficit machinery).
                const holder = orderedPeople.find((c) => c.alive);
                if (holder)
                    holder.open.cash += netProceeds;
                // Private residence relief: no CGT on the main home. Otherwise the
                // gain (net of selling costs) is a residential gain per owner.
                if (prop.asset.property?.isPrimaryResidence !== true) {
                    const gain = Math.max(0, prop.value - sellingCosts - prop.baseCost);
                    /**
                     * A2.5a review (adviser). This read `prop.asset.ownerId` — the
                     * IMMUTABLE input owner — which is the same bug class IM-F2-03 named
                     * on the rent path, left in place here. After a sole owner died,
                     * `aliveOwners('client')` returned nobody and the gain was allocated
                     * to no one: a let inherited by the survivor was tax-free forever,
                     * however much it grew after the death. The mutable owner is what the
                     * business-sale path already reads.
                     */
                    const holders = aliveOwners(prop.owner);
                    holders.forEach((h, i) => {
                        accOf(h.input.id).residentialGains += splitAcross(gain, holders.length, i);
                    });
                }
                prop.active = false;
                prop.sold = true;
            }
            if (plan.kind === 'businessSale' && plan.year === year) {
                const holding = trackedHoldings.find((c) => c.asset.id === plan.assetId);
                if (holding === undefined || holding.asset.wrapper !== 'business') {
                    throw new RangeError(`Sale "${plan.label}" names an unknown business "${plan.assetId}"`);
                }
                if (holding.value <= 0) {
                    // e.g. the holding shrank to nothing under a negative growth
                    // assumption — a planned sale must never silently do nothing.
                    warnOnce(`sale-skipped:${plan.id}`, {
                        code: 'planned-sale-skipped',
                        message: `The planned sale "${plan.label}" (${year}) did not happen: the business holding has no value in that year.`,
                        year,
                        itemLabel: plan.label,
                    });
                    continue;
                }
                const proceeds = holding.value;
                // A BLANK purchase cost is a fact-find gap, not a figure: the whole
                // proceeds become the gain (what the builder card already states) and
                // the model SAYS it assumed that. An explicit £0 is a stated figure
                // and is left silent. `== null` matches the `?? 0` the gain uses two
                // lines down, so a future nullable widening cannot keep the
                // 100%-of-proceeds gain while silently losing the warning.
                if (holding.baseCost == null) {
                    warnOnce(`cost-basis-missing:${plan.id}`, {
                        code: 'cost-basis-missing',
                        message: `The purchase price of "${holding.asset.label}" is not recorded in this plan, so the sale "${plan.label}" (${year}) treats the whole sale amount as profit. Any capital gains tax shown for that year is the most it could be; with the purchase price recorded, the model would use the actual profit instead.`,
                        year,
                        itemLabel: holding.asset.label,
                    });
                }
                const gain = Math.max(0, proceeds - (holding.baseCost ?? 0));
                const holders = aliveOwners(holding.owner);
                holders.forEach((h, i) => {
                    const share = splitAcross(gain, holders.length, i);
                    const a = accOf(h.input.id);
                    if (plan.badrQualifying === true)
                        a.badrGains += share;
                    else
                        a.realisedGains += share;
                });
                const holder = orderedPeople.find((c) => c.alive);
                if (holder) {
                    if ((plan.destination ?? 'cash') === 'gia') {
                        holder.open.gia.value += proceeds;
                        holder.open.gia.cost += proceeds; // acquired at market value
                    }
                    else {
                        holder.open.cash += proceeds;
                    }
                }
                holding.value = 0;
                holding.sold = { year, label: plan.label };
            }
        }
        // 2. DC crystallisation: each pot crystallises at ITS OWN access age —
        // 25% PCLS (LSA-capped, shared per person) to cash, residue to drawdown.
        for (const p of orderedPeople) {
            if (!p.alive)
                continue;
            const age = ageIn(p.input, year);
            for (const pot of p.dcPots) {
                if (age < pot.accessAge || pot.uncrystallised <= 0)
                    continue;
                const lump = taxFreeLumpSum(rules, {
                    crystallisedValue: pence(pot.uncrystallised),
                    lumpSumAllowanceUsed: pence(p.lsaUsed),
                });
                const set = pot.shielded ? p.shielded : p.open;
                set.cash += lump.taxFreeLumpSum;
                pot.drawdown += lump.taxableResidue;
                p.lsaUsed += lump.taxFreeLumpSum;
                pot.uncrystallised = 0;
            }
        }
        // 3. liabilities amortise (monthly interest accrual + payments; re-fix
        // terms and annual overpayments applied). A debt with a future startYear
        // does not exist yet: no interest, no payments, nothing outstanding.
        let debtService = saleDebtCleared;
        /**
         * A2.5a review round 2 (adviser). What the estate PAID to settle a debt at
         * a death, kept apart from ordinary debt service because it lands after the
         * year's net position was sized: without it the death year was the one row
         * in the table where income − spending − tax ≠ net cash flow, by exactly
         * the settled balance, and an adviser cannot read a row that does not add
         * up.
         */
        let settlementPaid = 0;
        const interestByLiability = new Map();
        for (const l of liabilities) {
            if (l.balance <= 0)
                continue;
            if (l.settled === true || !liabilityStarted(l, year))
                continue;
            if (l.input.endYear !== null && year > l.input.endYear) {
                // Whatever remains falls due (interest-only balloon).
                //
                // This is a large, single-year outflow — an interest-only mortgage's
                // whole capital lands in one year — and until feedback round 7 it
                // happened SILENTLY: the trace recorded `cleared: 'endOfTerm'`, but
                // nothing told the adviser that a six-figure repayment had just been
                // modelled, or that the plan assumes the client can meet it. A year's
                // spending jumping by the balance of a mortgage is exactly the kind of
                // unexplained figure this round was reported for.
                warnOnce(`balloon:${l.input.id}`, {
                    code: 'liability-balloon-at-term',
                    message: `"${l.input.label}" reaches the end of its term in ${l.input.endYear}, so the ${gbpWhole(l.balance)} still outstanding is modelled as repaid in full in ${year}. Confirm how the capital is actually to be repaid — the plan assumes it is met from available assets.`,
                    year,
                    itemLabel: l.input.label,
                });
                debtService += l.balance;
                interestByLiability.set(l.input.id, 0);
                debtLines.push(Object.freeze({
                    liabilityId: l.input.id,
                    label: l.input.label,
                    opening: pence(l.balance),
                    interest: pence(0),
                    paid: pence(l.balance),
                    closing: pence(0),
                    cleared: 'endOfTerm',
                }));
                l.balance = 0;
                continue;
            }
            const opening = l.balance;
            const ownTerms = liabilityTermsFor(l.input, year);
            const terms = replayedTerms(l, ownTerms, year);
            const r = amortiseYear(pence(l.balance), terms.rateBps, pence(terms.monthlyPayment), terms.interestOnly, l.input.annualOverpayment ?? 0);
            l.balance = r.closingBalance;
            debtService += r.totalPaid;
            debtInterest += r.interestAccrued;
            interestByLiability.set(l.input.id, r.interestAccrued);
            if (r.totalPaid > 0 || opening > 0 || r.closingBalance > 0) {
                debtLines.push(Object.freeze({
                    liabilityId: l.input.id,
                    label: l.input.label,
                    opening: pence(opening),
                    interest: pence(r.interestAccrued),
                    paid: pence(r.totalPaid),
                    closing: pence(r.closingBalance),
                    cleared: opening > 0 && r.closingBalance === 0 ? 'finalPayment' : 'no',
                }));
            }
            if (l.balance > opening && !terms.interestOnly && l.input.endYear === null) {
                warnOnce(`grow:${l.input.id}`, {
                    code: 'liability-never-repaid',
                    message: `Payments on "${l.input.label}" do not cover its interest and no end year is set; the balance grows indefinitely.`,
                    year,
                    itemLabel: l.input.label,
                });
            }
        }
        // 4. property rent — the rentalYield input is CONSUMED here.
        for (const prop of properties) {
            if (!prop.active)
                continue;
            const details = prop.asset.property;
            if (!details?.isBuyToLet || details.rentalYieldBps <= 0)
                continue;
            const rent = bpsOf(prop.value, details.rentalYieldBps);
            const interest = details.linkedLiabilityId !== null
                ? (interestByLiability.get(details.linkedLiabilityId) ?? 0)
                : 0;
            const holders = aliveOwners(prop.owner);
            holders.forEach((h, i) => {
                const a = accOf(h.input.id);
                // Section 24: rent is taxed WITHOUT an interest deduction; the
                // basic-rate credit arrives in step 9.
                a.nonSavings += splitAcross(rent, holders.length, i);
                a.rentalProfits += splitAcross(rent, holders.length, i);
                a.financeCosts += splitAcross(interest, holders.length, i);
                addIncomeLine(h.input.id, {
                    sourceId: prop.asset.id,
                    label: `${prop.asset.label} rent`,
                    amount: pence(splitAcross(rent, holders.length, i)),
                    taxable: true,
                });
            });
        }
        // 4.5 business dividends: a business interest's yield pays a CASH dividend
        // to its owner(s) (taxed as dividend income), unlike pooled wrappers where
        // yield is reinvested. Paid on the start-of-year value.
        for (const h of trackedHoldings) {
            if (h.asset.wrapper !== 'business')
                continue;
            const yBps = h.asset.yieldBps ?? 0;
            if (yBps <= 0 || h.value <= 0)
                continue;
            const dividend = bpsOf(h.value, yBps);
            const holders = aliveOwners(h.owner);
            holders.forEach((p, i) => {
                const share = splitAcross(dividend, holders.length, i);
                accOf(p.input.id).dividends += share;
                addIncomeLine(p.input.id, {
                    sourceId: h.asset.id,
                    label: `${h.asset.label} dividend`,
                    amount: pence(share),
                    taxable: true,
                });
            });
        }
        // Rent from a JOINTLY-held let passes to the surviving partner, exactly as
        // the property's own yield path does (`aliveOwners` of 'joint'); a solely
        // owned let's rent dies with its owner on both paths. This keeps a
        // converted legacy-yield plan's post-death years identical to what the
        // yield was already projecting.
        const linkedRentalRecipient = (stream) => {
            const owner = people.get(stream.ownerId);
            if (owner?.alive === true)
                return owner;
            if (stream.sourceAssetId == null)
                return null;
            const linked = properties.find((pr) => pr.asset.id === stream.sourceAssetId);
            if (linked === undefined || !linked.active)
                return null;
            /**
             * IM-F2-03. This tested `linked.asset.ownerId !== 'joint'` — the
             * IMMUTABLE input owner — so a solely-owned let stopped paying rent the
             * year its owner died, while the property itself passed to the survivor
             * and carried on growing on the chart. The survivor's income looked
             * £14,000 a year worse than it will be and the shortfall marker moved for
             * the wrong reason, all while a business shareholding in exactly the same
             * position kept paying its dividends. Reading the MUTABLE owner, which
             * the death step updates, is what the dividend path already did.
             */
            const survivor = survivorOf(stream.ownerId);
            if (survivor === null)
                return null;
            return linked.owner === survivor.input.id ? survivor : null;
        };
        // Section 24 for LINKED rental streams: the mortgage interest on the named
        // let is a finance cost of that rent, exactly as it is when the same rent
        // arrives via the property's own yield. Counted per property across the
        // streams that will actually be PAID this year, so two streams naming the
        // same let (a joint let split client/partner, or both halves flowing to
        // the survivor) share the interest instead of each claiming all of it.
        const linkedRentalCounts = new Map();
        for (const s of allIncomeStreams) {
            if (s.type !== 'rental' || s.sourceAssetId == null)
                continue;
            if (linkedRentalRecipient(s) === null || !streamActive(s, year))
                continue;
            linkedRentalCounts.set(s.sourceAssetId, (linkedRentalCounts.get(s.sourceAssetId) ?? 0) + 1);
        }
        const linkedRentalSeen = new Map();
        // 5. income streams (dc_pension streams handled in step 8) + cash interest
        for (const stream of allIncomeStreams) {
            if (stream.type === 'dc_pension')
                continue;
            const owner = people.get(stream.ownerId);
            let recipient = owner.alive ? owner : null;
            let fractionBps = 10_000;
            if (!owner.alive && stream.type === 'db_pension' && (stream.dbSpouseFractionBps ?? 0) > 0) {
                recipient = survivorOf(stream.ownerId);
                fractionBps = stream.dbSpouseFractionBps;
            }
            if (!owner.alive && stream.type === 'rental') {
                recipient = linkedRentalRecipient(stream);
            }
            if (recipient === null || !streamActive(stream, year))
                continue;
            // Income drawn FROM a business interest (an owner-director's salary or
            // dividends, linked in B1.2) cannot still be paid once that business has
            // been sold. It stops from the START of the sale year, the same rule a
            // let's rent follows — so the last year it is paid is the year BEFORE
            // the sale, and the message says that rather than leaving the reader to
            // reconcile "stops in 2028" with a £0 row for 2028.
            //
            // Two different things can be true here, and they read very differently
            // to a client: a stream that WAS paying has been cut short, and a stream
            // that would only have begun after the sale never pays at all. Saying
            // "it stops in 2028" about income that never started would be wrong.
            if (stream.sourceAssetId != null) {
                const sold = trackedHoldings.find((h) => h.asset.id === stream.sourceAssetId &&
                    h.asset.wrapper === 'business' &&
                    h.sold !== undefined);
                if (sold?.sold !== undefined) {
                    const saleYear = sold.sold.year;
                    // The LAST year this stream actually paid, found by asking the same
                    // predicate the projection uses rather than assuming `saleYear - 1`.
                    // `streamActive` clamps to the projection window, so a sale in the
                    // first projected year would make "was it active last year?" false
                    // for a salary the client draws TODAY — and the note would tell the
                    // client their current income is not paid at all. A career break or
                    // a retirement covering the year before the sale would mislead the
                    // same way. Bounded by the horizon and only reached by a stream
                    // linked to a business that has been sold.
                    let lastPaid = null;
                    for (let y = saleYear - 1; y >= startYear; y--) {
                        if (streamActive(stream, y)) {
                            lastPaid = y;
                            break;
                        }
                    }
                    warnOnce(`business-sale-stream-truncated:${stream.id}`, {
                        code: 'business-sale-stream-truncated',
                        message: lastPaid === null
                            ? `"${stream.label}" is drawn from "${sold.asset.label}", which the planned sale "${sold.sold.label}" recorded in this plan disposes of in ${saleYear}. It would not have begun before that sale, so it is not paid at all in this projection.`
                            : `"${stream.label}" is drawn from "${sold.asset.label}", so it stops from the start of ${saleYear}, when the planned sale "${sold.sold.label}" recorded in this plan happens. The last year it is paid in this projection is ${lastPaid}.`,
                        year: saleYear,
                        itemLabel: stream.label,
                    });
                    continue;
                }
            }
            // A rental stream tied to a NAMED property is paid only while that
            // property is held: a planned sale stops the rent from the start of the
            // sale year, and a planned purchase starts it only once bought. An id
            // matching no property means "source not recorded": the link is ignored
            // and SAID so, never silently honoured or silently dropped.
            if (stream.type === 'rental' && stream.sourceAssetId != null) {
                const linked = properties.find((p) => p.asset.id === stream.sourceAssetId);
                if (linked === undefined) {
                    warnOnce(`rental-source-missing:${stream.id}`, {
                        code: 'rental-source-missing',
                        message: `"${stream.label}" names a source asset that is not on the plan — the link is ignored and the rent is paid as entered.`,
                        year,
                        itemLabel: stream.label,
                    });
                }
                else if (!linked.active) {
                    continue;
                }
            }
            const escalated = streamAmount(stream, year);
            // A pension sharing order permanently removed this share of the member's
            // entitlement on divorce, so it is netted off BEFORE any survivor's
            // fraction — the survivor inherits a share of what is left, not of what
            // the member had before the court order.
            const debitBps = stream.pensionSharingDebitBps ?? 0;
            const afterDebit = debitBps > 0 ? escalated - bpsOf(escalated, debitBps) : escalated;
            const amount = fractionBps === 10_000 ? afterDebit : bpsOf(afterDebit, fractionBps);
            const a = accOf(recipient.input.id);
            if (amount > 0) {
                addIncomeLine(recipient.input.id, {
                    sourceId: stream.id,
                    label: stream.label,
                    amount: pence(amount),
                    taxable: stream.taxable,
                });
            }
            if (!stream.taxable) {
                a.untaxedInflow += amount;
                continue;
            }
            switch (stream.type) {
                case 'employment':
                    a.employment += amount;
                    a.nonSavings += amount;
                    break;
                case 'self_employment':
                    a.selfEmployment += amount;
                    a.nonSavings += amount;
                    break;
                case 'rental': {
                    a.nonSavings += amount;
                    a.rentalProfits += amount;
                    if (stream.sourceAssetId != null) {
                        const linked = properties.find((pr) => pr.asset.id === stream.sourceAssetId);
                        const details = linked?.asset.property;
                        // Credit the interest only when the property is not ALSO paying
                        // asset-side rent — step 4 already credits it for yielding lets.
                        if (linked !== undefined &&
                            linked.active &&
                            details?.linkedLiabilityId != null &&
                            !(details.isBuyToLet && details.rentalYieldBps > 0)) {
                            const interest = interestByLiability.get(details.linkedLiabilityId) ?? 0;
                            const share = linkedRentalCounts.get(stream.sourceAssetId) ?? 1;
                            const seen = linkedRentalSeen.get(stream.sourceAssetId) ?? 0;
                            linkedRentalSeen.set(stream.sourceAssetId, seen + 1);
                            a.financeCosts += splitAcross(interest, share, seen);
                        }
                    }
                    break;
                }
                case 'dividend':
                    a.dividends += amount;
                    break;
                case 'savings_interest':
                    a.savings += amount;
                    break;
                // db_pension and state_pension are pension income; 'other' taxable
                // streams are treated the same way.
                case 'db_pension':
                case 'state_pension':
                case 'other':
                default:
                    a.nonSavings += amount;
                    break;
            }
        }
        for (const p of orderedPeople) {
            if (!p.alive)
                continue;
            const cashShift = cashRateShiftOf('cash', year);
            const openInterest = bpsOf(p.open.cash, effGrowth(p.input.id, 'cash', false) + cashShift);
            const shieldedInterest = bpsOf(p.shielded.cash, effGrowth(p.input.id, 'cash', true) + cashShift);
            const a = accOf(p.input.id);
            a.cashInterest = openInterest + shieldedInterest;
            a.cashInterestShielded = shieldedInterest;
            a.cashInterestTaxable = Math.max(0, openInterest) + Math.max(0, shieldedInterest);
            if (a.cashInterest > 0) {
                addIncomeLine(p.input.id, {
                    sourceId: 'cash-interest',
                    label: 'Cash & savings interest',
                    amount: pence(a.cashInterest),
                    taxable: true,
                });
            }
        }
        // 5b. GIA income yield. A general investment account is the only OPEN,
        // TAXABLE pooled wrapper: its distributions are dividend income in the
        // year they arise, whereas an ISA/LISA/JISA is tax-free by wrapper, NS&I
        // premium-bond winnings are tax-free by statute, and a DC pension is
        // taxed on the way out rather than on the way round. So this split is
        // GIA-only by design, not by omission.
        //
        // Computed here on the START-OF-YEAR balance — the same basis as cash
        // interest — so the amount TAXED and the amount CREDITED to the pot in
        // step 11 can never diverge.
        for (const p of orderedPeople) {
            if (!p.alive)
                continue;
            const openYield = bpsOf(p.open.gia.value, effYield(p.input.id, 'gia', false));
            const shieldedYield = bpsOf(p.shielded.gia.value, effYield(p.input.id, 'gia', true));
            const a = accOf(p.input.id);
            a.giaYield = openYield + shieldedYield;
            a.giaYieldShielded = shieldedYield;
            // A negative (bear-case) yield is a value loss on the pot, never negative
            // dividend income — each pot floored at zero, exactly as cash interest is.
            a.giaYieldTaxable = Math.max(0, openYield) + Math.max(0, shieldedYield);
            if (a.giaYield > 0) {
                addIncomeLine(p.input.id, {
                    sourceId: 'gia-yield',
                    label: 'Investment account income',
                    amount: pence(a.giaYield),
                    taxable: true,
                });
            }
        }
        // 6. regular contributions inside each asset's window (paid from cash
        // flow), plus employer DC contributions (paid gross by the employer —
        // never household cashflow, but they count toward the annual allowance).
        let contributionsPaid = 0;
        let flowEmployerConts = 0;
        /** The contribution due this year after the asset's escalation. */
        const scheduledAmount = (asset, base, from) => escalate(base, asset.contributionEscalation, () => 0, from, year, escCtxFor);
        const contributionWindow = (asset) => {
            const from = Math.max(asset.contributionStartYear ?? startYear, startYear);
            if (year < from)
                return null;
            if (asset.contributionEndYear != null && year > asset.contributionEndYear)
                return null;
            return from;
        };
        // 6a. employer DC contributions (independent of any member contribution)
        for (const asset of input.assets) {
            if (asset.wrapper !== 'dc_pension')
                continue;
            const employer = asset.employerContribution ?? 0;
            if (employer <= 0)
                continue;
            const from = contributionWindow(asset);
            if (from === null)
                continue;
            const amountThisYear = scheduledAmount(asset, employer, from);
            const holders = aliveOwners(asset.ownerId);
            holders.forEach((h, i) => {
                if (asset.contributionEndYear == null && ageIn(h.input, year) >= h.input.retirementAge) {
                    return;
                }
                const perHolder = splitAcross(annualAmount(amountThisYear, asset.employerContributionFrequency ?? Frequency.Annually), holders.length, i);
                if (perHolder <= 0)
                    return;
                const pot = h.dcPots.find((c) => c.id === asset.id);
                if (pot === undefined)
                    return;
                pot.uncrystallised += perHolder;
                accOf(h.input.id).employerPensionContributions += perHolder;
                flowEmployerConts += perHolder;
                addTraceContrib(h.input.id, 'dc_pension', perHolder);
            });
        }
        // 6b. member contributions (household cashflow)
        /** JISA subscriptions so far this year, per dependent (the per-child cap). */
        const jisaSubscribedByDependent = new Map();
        for (const asset of input.assets) {
            if (asset.contribution <= 0)
                continue;
            if (asset.wrapper === 'property' ||
                asset.wrapper === 'db_pension' ||
                asset.wrapper === 'business' ||
                asset.wrapper === 'other_asset') {
                continue;
            }
            const from = contributionWindow(asset);
            if (from === null)
                continue;
            const contributionThisYear = scheduledAmount(asset, asset.contribution, from);
            if (asset.wrapper === 'jisa') {
                // Household-funded but the CHILD's asset: capped per dependent per
                // year; paid whenever any funder is alive; payments are only
                // possible before the child turns 18.
                const funder = orderedPeople.find((p) => p.alive);
                if (funder === undefined)
                    continue;
                const holding = jisaHoldings.find((j) => j.asset.id === asset.id);
                if (holding === undefined)
                    continue;
                const dep = (input.dependents ?? []).find((d) => d.id === holding.dependentId);
                if (dep !== undefined && ageAtTaxYearStart(dep.dateOfBirth, year) >= 18) {
                    warnOnce(`jisa-18:${asset.id}`, {
                        code: 'jisa-cap-applied',
                        message: `Contributions to "${asset.label}" stop from the tax year the child turns 18 — a Junior ISA cannot be paid into after that.`,
                        year,
                        itemLabel: asset.label,
                    });
                    continue;
                }
                const subscribed = jisaSubscribedByDependent.get(holding.dependentId) ?? 0;
                const room = jisaSubscriptionRoom(rules, pence(subscribed));
                const wanted = annualAmount(contributionThisYear, asset.contributionFrequency);
                const allowed = Math.min(wanted, room);
                if (allowed < wanted) {
                    warnOnce(`jisa:${asset.id}`, {
                        code: 'jisa-cap-applied',
                        message: `Contributions to "${asset.label}" were capped at the ${gbpWhole(rules.isa.jisaAnnualLimit ?? 0)} Junior ISA annual limit for that child.`,
                        year,
                        itemLabel: asset.label,
                    });
                }
                if (allowed <= 0)
                    continue;
                holding.value += allowed;
                jisaSubscribedByDependent.set(holding.dependentId, subscribed + allowed);
                contributionsPaid += allowed;
                hhContrib.jisa += allowed;
                continue;
            }
            const holders = aliveOwners(asset.ownerId);
            holders.forEach((h, i) => {
                // An explicit end year keeps contributions running past retirement;
                // otherwise they stop when the holder retires (nothing is "forever").
                if (asset.contributionEndYear == null && ageIn(h.input, year) >= h.input.retirementAge) {
                    return;
                }
                const set = asset.neverDrawFrom ? h.shielded : h.open;
                const perHolder = splitAcross(annualAmount(contributionThisYear, asset.contributionFrequency), holders.length, i);
                if (perHolder <= 0)
                    return;
                const a = accOf(h.input.id);
                switch (asset.wrapper) {
                    case 'dc_pension': {
                        // Relief at source: the member pays 80% of the gross amount.
                        const gross = perHolder;
                        const net = gross - bpsOf(gross, rules.incomeTax.basicRateBps);
                        const pot = h.dcPots.find((c) => c.id === asset.id);
                        if (pot === undefined)
                            return;
                        pot.uncrystallised += gross;
                        a.grossPensionContributions += gross;
                        contributionsPaid += net;
                        flowReliefTopUp += gross - net;
                        addTraceContrib(h.input.id, 'dc_pension', gross);
                        break;
                    }
                    case 'cash_isa':
                    case 'stocks_isa': {
                        const room = isaSubscriptionRoom(rules, pence(a.isaSubscribed));
                        const allowed = Math.min(perHolder, room);
                        if (allowed < perHolder) {
                            warnOnce(`isa:${asset.id}:${h.input.id}`, {
                                code: 'isa-cap-applied',
                                message: `Contributions to "${asset.label}" were capped at the ISA annual subscription limit, aggregated across every ISA held by the ${h.input.id}.`,
                                year,
                                itemLabel: asset.label,
                            });
                        }
                        if (asset.wrapper === 'cash_isa')
                            set.cashIsa += allowed;
                        else
                            set.stocksIsa += allowed;
                        a.isaSubscribed += allowed;
                        contributionsPaid += allowed;
                        addTraceContrib(h.input.id, asset.wrapper, allowed);
                        break;
                    }
                    case 'lisa': {
                        // Own £4,000 cap inside the shared allowance; payments stop at 50;
                        // a 25% government bonus lands in the pot alongside the payment.
                        if (ageIn(h.input, year) >= (rules.isa.lisaMaxContributionAge ?? 50)) {
                            warnOnce(`lisa-age:${asset.id}:${h.input.id}`, {
                                code: 'lisa-cap-applied',
                                message: `Contributions to "${asset.label}" stop at age ${rules.isa.lisaMaxContributionAge ?? 50} (Lifetime ISA payment age limit).`,
                                year,
                                itemLabel: asset.label,
                            });
                            break;
                        }
                        const room = lisaSubscriptionRoom(rules, pence(a.lisaSubscribed), pence(a.isaSubscribed));
                        const allowed = Math.min(perHolder, room);
                        if (allowed < perHolder) {
                            warnOnce(`lisa:${asset.id}:${h.input.id}`, {
                                code: 'lisa-cap-applied',
                                message: `Contributions to "${asset.label}" were capped at the ${gbpWhole(rules.isa.lisaAnnualLimit ?? 0)} Lifetime ISA annual payment limit (within the shared ${gbpWhole(rules.isa.annualSubscriptionLimit)} ISA allowance).`,
                                year,
                                itemLabel: asset.label,
                            });
                        }
                        if (allowed <= 0)
                            break;
                        const bonus = lisaBonus(rules, pence(allowed));
                        set.lisa += allowed + bonus;
                        a.lisaSubscribed += allowed;
                        a.isaSubscribed += allowed;
                        contributionsPaid += allowed;
                        flowReliefTopUp += bonus;
                        addTraceContrib(h.input.id, 'lisa', allowed + bonus);
                        break;
                    }
                    case 'nsi': {
                        // Premium-bond holdings are capped at the product maximum.
                        const held = h.open.nsi + h.shielded.nsi;
                        const allowed = Math.min(perHolder, Math.max(0, nsiMaxHolding - held));
                        if (allowed < perHolder) {
                            warnOnce(`nsi:${asset.id}:${h.input.id}`, {
                                code: 'nsi-cap-applied',
                                message: `Contributions to "${asset.label}" were capped at the ${gbpWhole(nsiMaxHolding)} NS&I premium-bond maximum holding per person.`,
                                year,
                                itemLabel: asset.label,
                            });
                        }
                        if (allowed <= 0)
                            break;
                        set.nsi += allowed;
                        contributionsPaid += allowed;
                        addTraceContrib(h.input.id, 'nsi', allowed);
                        break;
                    }
                    case 'gia':
                        set.gia.value += perHolder;
                        set.gia.cost += perHolder;
                        contributionsPaid += perHolder;
                        addTraceContrib(h.input.id, 'gia', perHolder);
                        break;
                    case 'cash':
                        set.cash += perHolder;
                        contributionsPaid += perHolder;
                        addTraceContrib(h.input.id, 'cash', perHolder);
                        break;
                    default:
                        break;
                }
            });
        }
        // 7. spending
        let spending = 0;
        /** Per-item amounts for this year — the trace's "why is spending £X" lines. */
        const spendingLines = [];
        if (orderedPeople.some((p) => p.alive)) {
            for (const item of allSpending) {
                const from = Math.max(item.startYear ?? startYear, startYear);
                if (year < from || (item.endYear !== null && year > item.endYear))
                    continue;
                if (item.frequency === Frequency.Once && year !== from)
                    continue;
                /**
                 * FG-F3-04. A row belonging to one person stops when they die; a joint
                 * row scales by the household's STATED survivor factor once anyone
                 * has. "The plan keeps spending the couple's full £48,000 a year for
                 * the twelve years David is on his own. It won't — one person doesn't
                 * run two cars and two phones and eat for two."
                 *
                 * With no factor stated the joint figure carries on exactly as before
                 * and `survivor-spending-unmodelled` is raised instead. A default here
                 * would be the tool inventing the survivor's cost of living.
                 */
                const rowOwner = item.ownerId ?? 'joint';
                if (rowOwner !== 'joint' && rowOwner !== 'household') {
                    const owner = people.get(rowOwner);
                    if (owner !== undefined && !owner.alive)
                        continue;
                }
                const base = annualAmount(item.amount, item.frequency);
                // `escalation` (first-class) overrides the legacy `inflationLinked` flag;
                // `escalate` handles stepped schedules year-by-year, constant kinds in
                // one shot. Amounts are today's value, so escalation compounds from
                // the PROJECTION START (matching planned events): a future-start item
                // arrives already escalated to its first year.
                let amount = escalate(base, item.escalation, (y) => (item.inflationLinked ? cpiFor(y) : 0), startYear, year, escCtxFor);
                /**
                 * Only a JOINT LIVING-COST row scales. An expanded planned event is
                 * marked `household` and is never touched (A2.5a review).
                 *
                 * A2.5a review round 2 (tech-admin). A gift is not a living cost, and
                 * scaling one moves an inheritance-tax figure off a spending
                 * assumption — precisely what `planned-events.ts` marks its own
                 * expansions `household` to prevent. Measured on the reviewer's probe:
                 * entering a 70% factor rewrote the recorded seven-year gifts from
                 * £20,000 to £14,000 in every post-death year, silently. The recipient
                 * of a gift does not receive less because the donor died; the plan can
                 * stop making it, but that is the adviser ending the row, not the tool
                 * shrinking it.
                 */
                const survivorScaled = rowOwner === 'joint' &&
                    someoneHasDied &&
                    survivorFactorBps !== undefined &&
                    item.isGift !== true
                    ? bpsOf(amount, survivorFactorBps)
                    : amount;
                amount = survivorScaled;
                spending += amount;
                if (amount !== 0) {
                    spendingLines.push(Object.freeze({ label: item.label, amount: pence(amount) }));
                    // A2.7: the flag records what the money WAS; it adds nothing to and
                    // takes nothing from the spending figure computed directly above.
                    if (item.isGift === true) {
                        recurringGifts.push({
                            year,
                            value: amount,
                            label: item.label,
                            ...(item.giftRecipient !== undefined ? { recipient: item.giftRecipient } : {}),
                            ...(item.exemptionClaimed !== undefined
                                ? { exemptionClaimed: item.exemptionClaimed }
                                : {}),
                        });
                    }
                }
            }
        }
        // 7.5 life events: untaxed one-off inflows/outflows in their year.
        let lifeEventNeed = 0;
        /** One-off outflow lines for this year's trace (gifts, lump-sum costs). */
        const lifeEventOutflowLines = [];
        const firstAlive = orderedPeople.find((p) => p.alive) ?? null;
        /** The pot engine-directed pension amounts land in: the person's first
         * open pot, else an auto pot created at wrapper-assumption rates. */
        const autoDcPot = (p) => {
            // An INHERITED pot can never receive a contribution, so it is not a
            // candidate here — directing engine-placed pension money into one would
            // model a payment the pot cannot legally accept.
            const existing = p.dcPots.find((c) => !c.shielded && !c.isBeneficiaryDrawdown);
            if (existing !== undefined)
                return existing;
            const created = {
                id: `auto:${p.input.id}`,
                // Adviser-facing: this label reaches the year breakdown, so it says
                // where the pot came from rather than which component made it (C6).
                label: 'Pension pot (added by this plan)',
                accessAge: p.input.retirementAge,
                isBeneficiaryDrawdown: false,
                shielded: false,
                uncrystallised: 0,
                drawdown: 0,
                growthBps: growthOf('dc_pension', null),
                yieldBps: yieldOf('dc_pension'),
                feeBps: feeOf('dc_pension'),
            };
            p.dcPots.push(created);
            return created;
        };
        for (const event of allLifeEvents) {
            if (event.year !== year || event.direction === 'none' || firstAlive === null)
                continue;
            if (event.direction === 'inflow') {
                flowLifeEventIn += event.amount;
                switch (event.wrapper) {
                    case 'cash_isa':
                        firstAlive.open.cashIsa += event.amount;
                        break;
                    case 'stocks_isa':
                        firstAlive.open.stocksIsa += event.amount;
                        break;
                    case 'nsi':
                        firstAlive.open.nsi += event.amount;
                        break;
                    case 'gia':
                        firstAlive.open.gia.value += event.amount;
                        firstAlive.open.gia.cost += event.amount;
                        break;
                    case 'dc_pension':
                        autoDcPot(firstAlive).uncrystallised += event.amount;
                        break;
                    case 'property':
                        // A house purchase: the event's value becomes a property holding
                        // that grows at the property assumption (pair it with a deposit
                        // outflow and a liability with a matching startYear).
                        eventProperties.push({ label: event.label, value: event.amount });
                        break;
                    case 'lisa':
                    case 'jisa':
                    case 'business':
                    case 'other_asset':
                        // These wrappers cannot absorb a one-off inflow (subscription
                        // limits / non-fungible holdings): land in cash, and SAY so.
                        warnOnce(`event-wrapper:${event.id}`, {
                            code: 'event-wrapper-defaulted',
                            message: `The inflow "${event.label}" was directed at a ${wrapperLabel(event.wrapper)}, which one-off amounts cannot be paid into; it was placed in cash instead.`,
                            year,
                            itemLabel: event.label,
                        });
                        firstAlive.open.cash += event.amount;
                        break;
                    default:
                        // cash, pensions-in-payment and null all land in cash.
                        firstAlive.open.cash += event.amount;
                        break;
                }
                continue;
            }
            // Outflow: take from the named wrapper first…
            if (event.wrapper === 'lisa' ||
                event.wrapper === 'jisa' ||
                event.wrapper === 'business' ||
                event.wrapper === 'other_asset') {
                warnOnce(`event-wrapper:${event.id}`, {
                    code: 'event-wrapper-defaulted',
                    message: `The outflow "${event.label}" was directed at a ${wrapperLabel(event.wrapper)}, which one-off amounts cannot be drawn from; it was funded from cash and the standard draw order instead.`,
                    year,
                    itemLabel: event.label,
                });
            }
            flowLifeEventOut += event.amount;
            if (event.amount !== 0) {
                lifeEventOutflowLines.push(Object.freeze({ label: event.label, amount: event.amount }));
            }
            let remaining = event.amount;
            for (const p of orderedPeople) {
                if (remaining <= 0 || !p.alive)
                    break;
                const set = p.open;
                switch (event.wrapper) {
                    case 'cash_isa': {
                        const take = Math.min(set.cashIsa, remaining);
                        set.cashIsa -= take;
                        remaining -= take;
                        break;
                    }
                    case 'stocks_isa': {
                        const take = Math.min(set.stocksIsa, remaining);
                        set.stocksIsa -= take;
                        remaining -= take;
                        break;
                    }
                    case 'nsi': {
                        const take = Math.min(set.nsi, remaining);
                        set.nsi -= take;
                        remaining -= take;
                        break;
                    }
                    case 'gia': {
                        const take = Math.min(set.gia.value, remaining);
                        if (take > 0) {
                            const allowableCost = take === set.gia.value
                                ? set.gia.cost
                                : Math.trunc((set.gia.cost * take) / set.gia.value);
                            set.gia.value -= take;
                            set.gia.cost -= allowableCost;
                            accOf(p.input.id).realisedGains += take - allowableCost;
                            remaining -= take;
                        }
                        break;
                    }
                    default: {
                        const take = Math.min(set.cash, remaining);
                        set.cash -= take;
                        remaining -= take;
                        break;
                    }
                }
            }
            // …anything left flows through the standard deficit machinery.
            lifeEventNeed += remaining;
        }
        // 8. scheduled dc_pension drawdown: taxable income drawn from the owner's
        // open (non-shielded) crystallised pots, in the order the pots were given.
        for (const stream of allIncomeStreams) {
            if (stream.type !== 'dc_pension')
                continue;
            const owner = people.get(stream.ownerId);
            if (!owner.alive || !streamActive(stream, year))
                continue;
            let wanted = streamAmount(stream, year);
            let drawn = 0;
            for (const pot of owner.dcPots) {
                if (wanted <= 0)
                    break;
                if (pot.shielded || pot.drawdown <= 0)
                    continue;
                const take = Math.min(pot.drawdown, wanted);
                pot.drawdown -= take;
                wanted -= take;
                drawn += take;
            }
            if (drawn <= 0)
                continue;
            owner.mpaaTriggered = true;
            scheduledPensionGross += drawn;
            accOf(owner.input.id).nonSavings += drawn;
            addIncomeLine(owner.input.id, {
                sourceId: stream.id,
                label: stream.label,
                amount: pence(drawn),
                taxable: true,
            });
            addDrawn(owner.input.id, 'dc_pension', drawn, 0, true);
        }
        // 9. per-person tax
        const personTax = new Map();
        for (const p of orderedPeople) {
            if (!p.alive)
                continue;
            const a = accOf(p.input.id);
            const result = incomeTax(rules, {
                nonSavings: pence(a.nonSavings),
                savings: pence(a.savings + a.cashInterestTaxable),
                dividends: pence(a.dividends + a.giaYieldTaxable),
                grossPensionContributions: pence(a.grossPensionContributions),
            });
            const totalIncome = a.nonSavings + a.savings + a.cashInterestTaxable + a.dividends + a.giaYieldTaxable;
            const s24 = section24Relief(rules, {
                financeCosts: pence(a.financeCosts + p.s24CarriedForward),
                propertyProfits: pence(a.rentalProfits),
                adjustedTotalIncomeAbovePersonalAllowance: pence(Math.max(0, totalIncome - result.personalAllowance)),
            });
            p.s24CarriedForward = s24.unrelievedFinanceCosts;
            const ni = nationalInsurance(rules, {
                employmentEarnings: pence(a.employment),
                selfEmploymentProfits: pence(a.selfEmployment),
            });
            // Threshold income deducts the member's own (relief-at-source) gross
            // contributions; adjusted income ADDS employer contributions. The AA is
            // then tested against member + employer contributions together.
            const allowance = effectiveAnnualAllowance(rules, {
                thresholdIncome: pence(Math.max(0, totalIncome - a.grossPensionContributions)),
                adjustedIncome: pence(totalIncome + a.employerPensionContributions),
                mpaaTriggered: p.mpaaTriggered,
            });
            // Carry-forward (HMRC: current year's allowance first, then the oldest
            // of the previous three years' unused). Tracked from the projection's
            // own years; once the MPAA is triggered carry-forward can no longer be
            // used against DC contributions, so the buffer is bypassed and cleared.
            const carried = p.mpaaTriggered ? [] : p.unusedAllowances;
            const available = annualAllowanceWithCarryForward(rules, allowance, carried.map((v) => pence(v)));
            const totalContributions = a.grossPensionContributions + a.employerPensionContributions;
            const excess = annualAllowanceExcess(rules, pence(totalContributions), available);
            // Spend carry-forward oldest-first on the overflow above this year's
            // own allowance, then roll the window: this year's unused joins the
            // buffer and anything older than three years drops off.
            let overflow = Math.max(0, Math.min(totalContributions, available) - allowance);
            const spent = carried.map((v) => {
                const used = Math.min(v, overflow);
                overflow -= used;
                return v - used;
            });
            p.unusedAllowances = p.mpaaTriggered
                ? []
                : [...spent, Math.max(0, allowance - totalContributions)].slice(-3);
            let aaCharge = 0;
            if (excess > 0) {
                aaCharge = bpsOf(excess, MARGINAL_BPS[result.marginalBand]);
                warnOnce(`aa:${p.input.id}`, {
                    code: 'annual-allowance-exceeded',
                    message: p.mpaaTriggered
                        ? `Pension contributions for the ${p.input.id} exceed the reduced annual allowance in ${year} (the MPAA applies after flexible access, and carry-forward cannot be used); an annual allowance charge is estimated at the marginal rate.`
                        : `Pension contributions for the ${p.input.id} exceed the available annual allowance in ${year} (including unused allowance carried forward within the plan); an annual allowance charge is estimated at the marginal rate.`,
                    year,
                    itemLabel: null,
                });
                warnOnce('aa-carry-forward', {
                    code: 'aa-carry-forward-pre-plan-nil',
                    message: 'Carry-forward of unused annual allowance is tracked from the plan start onward; unused allowance from tax years before the plan begins is treated as nil, so early-year annual allowance charges may be overstated.',
                    year: null,
                    itemLabel: null,
                });
            }
            // Relief at source is only available up to the greater of relevant UK
            // earnings and the £3,600 basic amount. Adviser-entered contributions
            // are taken as given, but an overstated HMRC top-up must be visible.
            const relievableCeiling = Math.max(a.employment + a.selfEmployment, rules.pension.nonEarnerAnnualAllowanceFloor ?? 0);
            if (a.grossPensionContributions > relievableCeiling) {
                warnOnce(`relief:${p.input.id}`, {
                    code: 'pension-relief-overstated',
                    message: `The ${p.input.id}'s gross pension contributions in ${year} exceed the amount relievable against their relevant UK earnings; the modelled relief-at-source top-up may be overstated.`,
                    year,
                    itemLabel: null,
                });
            }
            // 9b. student loan repayments — a DEBT REPAYMENT, not tax.
            //
            // The base is the SAME earnings base National Insurance takes above:
            // employment earnings + self-employment profits. Unearned income
            // (savings, dividends, rent) is excluded, because HMRC brings that in
            // only above a statutory limit through Self Assessment and that limit is
            // an open owner question — an invented rule here would be worse than a
            // stated boundary.
            //
            // Everything below stays OUT of `personTax`'s tax totals. `totalTax` must
            // be penny-identical with and without a loan, or the tool would disagree
            // with the client's P60.
            const slEarnings = pence(a.employment + a.selfEmployment);
            let studentLoanTotal = 0;
            const studentLoanLines = [];
            for (const loan of p.input.studentLoans ?? []) {
                /**
                 * FEEDBACK 2026-09-09: the plan can be on file before the year is.
                 * Everything this loop does is anchored to that April — when
                 * repayments start and, through the plan's term, when they stop — so
                 * with no year there is no honest way to model the loan. It is skipped
                 * and SAID, never guessed: an invented anchor would either run
                 * repayments for ever or stop them early, and stopping early
                 * overstates take-home pay, wealth and the estate at once.
                 */
                if (loan.firstRepaymentDueYear === null) {
                    /**
                     * CLIENT-FACING WORDING. This code is in
                     * `CLIENT_FACING_WARNING_CODES`, so it reaches the Present stage and
                     * the report appendix — where the first cut interpolated the raw enum
                     * and printed "A plan2 student loan…" / "A postgrad student loan…".
                     * Its two siblings never name the plan at all; this one does, because
                     * a household can hold two loans and only one of them may be missing
                     * its year. So it names it the way the fact-find does.
                     */
                    warnOnce(`student-loan-year-not-stated:${loan.plan}`, {
                        code: 'student-loan-year-not-stated',
                        message: `A ${STUDENT_LOAN_PLAN_NAMES[loan.plan]} is recorded without the April its ` +
                            `repayments first fell due, so no repayment on it is modelled. Take-home pay in ` +
                            `this projection is higher than it would be if that year were recorded.`,
                        year: null,
                        itemLabel: null,
                    });
                    continue;
                }
                // Income-contingent AND time-bounded: nothing repays before the first
                // April the loan fell due, and nothing repays from the write-off year
                // onwards. A year below the threshold repays £0 and never catches up —
                // there is no carry-forward of a missed year anywhere in this loop.
                const writeOffYear = studentLoanWriteOffYear(rules, loan.plan, loan.firstRepaymentDueYear);
                if (year < loan.firstRepaymentDueYear || year >= writeOffYear)
                    continue;
                const sl = studentLoanRepayment(rules, { plan: loan.plan, earnings: slEarnings });
                studentLoanTotal += sl.repayment;
                studentLoanLines.push(Object.freeze({
                    plan: loan.plan,
                    threshold: sl.threshold,
                    rateBps: sl.rateBps,
                    earnings: slEarnings,
                    writeOffYear,
                    repayment: sl.repayment,
                }));
                // Two boundaries the adviser has to be able to say out loud, raised
                // once per projection rather than per year.
                warnOnce('student-loan-threshold', {
                    code: 'student-loan-threshold-held-constant',
                    message: 'Repayment threshold held at the 2026/27 figure for the whole projection. ' +
                        'Thresholds are reviewed annually by government; holding them flat shows more ' +
                        'repaid, and repayment finishing earlier, than a rising threshold would.',
                    year: null,
                    itemLabel: null,
                });
                warnOnce('student-loan-balance', {
                    code: 'student-loan-balance-not-modelled',
                    message: 'Student loan repayments are modelled from earnings only. The outstanding balance ' +
                        'and the interest charged on it are not modelled, so repayments are shown as ' +
                        'ending on the plan’s write-off term rather than when a balance reaches nil.',
                    year: null,
                    itemLabel: null,
                });
            }
            personTax.set(p.input.id, {
                result,
                ni: ni.total,
                s24: Math.min(s24.taxReduction, result.totalTax),
                aaCharge,
                cgt: 0,
                studentLoan: studentLoanTotal,
                studentLoanLines,
            });
        }
        // 9.5 GIA annual CGT realisation (toggle): crystallise each GIA pool's
        // unrealised gain this year, seed it into the year's realised gains and
        // rebase the cost to current value. A baseline CGT is then computed below
        // and funded like any other tax; if the year runs into deficit, the
        // deficit loop extends the same running CGT total for any further draws.
        if (input.giaCgtMode === 'annually') {
            for (const p of orderedPeople) {
                if (!p.alive)
                    continue;
                const a = accOf(p.input.id);
                for (const set of [p.open, p.shielded]) {
                    const gain = set.gia.value - set.gia.cost;
                    if (gain > 0) {
                        a.realisedGains += gain;
                        set.gia.cost = set.gia.value; // rebased: future disposals start from here
                    }
                }
            }
        }
        /**
         * A person's total CGT for the year across the three gain buckets, in a
         * documented order: STANDARD gains use the annual exempt amount and any
         * basic-rate headroom first, then RESIDENTIAL gains (residential rates),
         * then BADR gains at the relief rate up to the person's remaining
         * lifetime limit (the excess at the standard higher rate).
         */
        const totalCgtFor = (p, t, a) => {
            const aea = rules.capitalGains.annualExemptAmount;
            // Gains stack on top of TOTAL taxable income — including any
            // unscheduled pension draws taken this year, which fill the bands and
            // shrink the basic-rate CGT headroom.
            const taxableIncome = a.unscheduledGross > 0
                ? incomeTax(rules, {
                    nonSavings: pence(a.nonSavings + a.unscheduledGross),
                    savings: pence(a.savings + a.cashInterestTaxable),
                    dividends: pence(a.dividends + a.giaYieldTaxable),
                    grossPensionContributions: pence(a.grossPensionContributions),
                }).taxableIncome
                : t.result.taxableIncome;
            const headroom = Math.max(0, t.result.extendedBasicBandTop - taxableIncome);
            let total = 0;
            const std = capitalGainsTax(rules, {
                netGains: pence(Math.max(0, a.realisedGains)),
                basicRateHeadroom: pence(headroom),
            });
            total += std.totalTax;
            let aeaUsed = Math.min(aea, Math.max(0, a.realisedGains));
            if (a.residentialGains > 0) {
                const res = capitalGainsTax(rules, {
                    netGains: pence(a.residentialGains),
                    residential: true,
                    basicRateHeadroom: pence(Math.max(0, headroom - std.taxableGain)),
                    annualExemptAmountUsed: pence(aeaUsed),
                });
                total += res.totalTax;
                aeaUsed = Math.min(aea, aeaUsed + a.residentialGains);
            }
            if (a.badrGains > 0) {
                const taxable = Math.max(0, a.badrGains - Math.max(0, aea - aeaUsed));
                const limitLeft = Math.max(0, (rules.capitalGains.badrLifetimeLimit ?? 0) - p.badrUsed);
                const atReliefRate = Math.min(taxable, limitLeft);
                total += bpsOf(atReliefRate, rules.capitalGains.badrRateBps ?? rules.capitalGains.higherRateBps);
                total += bpsOf(taxable - atReliefRate, rules.capitalGains.higherRateBps);
            }
            return total;
        };
        const hasGains = (a) => a.realisedGains > 0 || a.residentialGains > 0 || a.badrGains > 0;
        // Baseline CGT on gains realised so far this year (annual-mode
        // crystallisation, property/business sales). One AEA per person.
        for (const p of orderedPeople) {
            const t = personTax.get(p.input.id);
            if (!t)
                continue;
            const a = accOf(p.input.id);
            if (!hasGains(a))
                continue;
            t.cgt = totalCgtFor(p, t, a);
        }
        // 10. surplus / deficit
        let inflow = 0;
        let grossIncome = 0;
        for (const p of orderedPeople) {
            const a = accOf(p.input.id);
            inflow += a.nonSavings + a.savings + a.dividends + a.untaxedInflow;
            grossIncome +=
                a.nonSavings +
                    a.savings +
                    a.dividends +
                    a.untaxedInflow +
                    a.cashInterestTaxable +
                    a.giaYieldTaxable;
        }
        let taxSoFar = 0;
        // DELIBERATELY separate from `taxSoFar`: the loan repayment reduces the
        // cash the household has, exactly as tax does, but it is not tax and must
        // never be summed into a figure called tax.
        let studentLoanSoFar = 0;
        for (const t of personTax.values()) {
            taxSoFar += t.result.totalTax - t.s24 + t.ni + t.aaCharge + t.cgt;
            studentLoanSoFar += t.studentLoan;
        }
        const net = inflow -
            taxSoFar -
            studentLoanSoFar -
            spending -
            debtService -
            contributionsPaid -
            lifeEventNeed;
        /**
         * Draw `needed` through the configured withdrawal waterfall (open pots
         * only), realising GIA gains and grossing up pension draws at the owner's
         * marginal-rate estimate. Returns what is still missing.
         */
        const drawWaterfall = (needed) => {
            const drawCash = (p) => {
                const take = Math.min(p.open.cash, needed);
                p.open.cash -= take;
                needed -= take;
                addDrawn(p.input.id, 'cash', take);
            };
            const drawCashIsa = (p) => {
                const take = Math.min(p.open.cashIsa, needed);
                p.open.cashIsa -= take;
                needed -= take;
                addDrawn(p.input.id, 'cash_isa', take);
            };
            const drawStocksIsa = (p) => {
                const take = Math.min(p.open.stocksIsa, needed);
                p.open.stocksIsa -= take;
                needed -= take;
                addDrawn(p.input.id, 'stocks_isa', take);
            };
            // NS&I premium bonds are cash-like: drawn with the cash step.
            const drawNsi = (p) => {
                const take = Math.min(p.open.nsi, needed);
                p.open.nsi -= take;
                needed -= take;
                addDrawn(p.input.id, 'nsi', take);
            };
            // LISA joins the ISA step only from the penalty-free age (60); before
            // that the engine never draws it (the 25% withdrawal charge is not
            // modelled — a deliberate, documented boundary).
            const drawLisa = (p) => {
                if (ageIn(p.input, year) < (rules.isa.lisaPenaltyFreeAge ?? 60))
                    return;
                const take = Math.min(p.open.lisa, needed);
                p.open.lisa -= take;
                needed -= take;
                addDrawn(p.input.id, 'lisa', take);
            };
            const drawGia = (p) => {
                const pool = p.open.gia;
                const take = Math.min(pool.value, needed);
                if (take <= 0)
                    return;
                const allowableCost = take === pool.value ? pool.cost : Math.trunc((pool.cost * take) / pool.value);
                pool.value -= take;
                pool.cost -= allowableCost;
                accOf(p.input.id).realisedGains += take - allowableCost;
                needed -= take;
                addDrawn(p.input.id, 'gia', take);
            };
            const drawPension = (p) => {
                const age = ageIn(p.input, year);
                const t = personTax.get(p.input.id);
                if (!t)
                    return;
                // Draw across every accessible, non-shielded crystallised pot in order.
                let available = 0;
                const accessible = p.dcPots.filter((pot) => !pot.shielded && age >= pot.accessAge && pot.drawdown > 0);
                for (const pot of accessible)
                    available += pot.drawdown;
                if (available <= 0)
                    return;
                const a = accOf(p.input.id);
                // An unscheduled draw is pension income: it is taxed through the HMRC
                // bands ON TOP of everything else the person is taxed on this year
                // (the step-9 inputs plus any draws already taken), so a draw inside
                // the unused personal allowance pays nothing and a large draw pays
                // the banded blend — never a flat pre-draw marginal estimate.
                const taxWithDraws = (gross) => incomeTax(rules, {
                    nonSavings: pence(a.nonSavings + a.unscheduledGross + gross),
                    savings: pence(a.savings + a.cashInterestTaxable),
                    dividends: pence(a.dividends + a.giaYieldTaxable),
                    grossPensionContributions: pence(a.grossPensionContributions),
                }).totalTax;
                const baseTax = taxWithDraws(0);
                // Smallest gross draw whose net (after the banded tax delta) covers
                // the need — integer bisection, capped at what the pots hold.
                let lo = 0;
                let hi = available;
                while (lo < hi) {
                    const mid = lo + Math.trunc((hi - lo) / 2);
                    if (mid - (taxWithDraws(mid) - baseTax) >= needed)
                        hi = mid;
                    else
                        lo = mid + 1;
                }
                let gross = lo;
                if (gross <= 0)
                    return;
                // Guard: the net-of-tax curve is not strictly monotone — the
                // personal savings allowance steps DOWN as a draw pushes the person
                // across a band boundary, so the bisection can land where the net
                // still falls short. Walk forward by the remaining shortfall until
                // the net truly covers the need or the pots are exhausted (each step
                // advances ≥ 1p, so this terminates deterministically).
                let netOf = gross - (taxWithDraws(gross) - baseTax);
                while (netOf < needed && gross < available) {
                    gross = Math.min(available, gross + Math.max(1, needed - netOf));
                    netOf = gross - (taxWithDraws(gross) - baseTax);
                }
                const taxOnDraw = taxWithDraws(gross) - baseTax;
                let remainingGross = gross;
                for (const pot of accessible) {
                    if (remainingGross <= 0)
                        break;
                    const take = Math.min(pot.drawdown, remainingGross);
                    pot.drawdown -= take;
                    remainingGross -= take;
                }
                p.mpaaTriggered = true;
                addDrawn(p.input.id, 'dc_pension', gross, taxOnDraw);
                a.drawTax += taxOnDraw;
                a.unscheduledGross += gross;
                const netReceived = gross - taxOnDraw;
                // Integer tax steps can deliver a penny or two more net than was
                // needed; bank the excess as cash so nothing evaporates
                // (conservation identities are asserted to the penny).
                const used = Math.min(netReceived, needed);
                p.open.cash += netReceived - used;
                needed -= used;
            };
            const handlers = {
                cash: [drawCash, drawNsi],
                cash_isa: [drawCashIsa],
                stocks_isa: [drawStocksIsa],
                isa: [drawCashIsa, drawStocksIsa, drawLisa],
                gia: [drawGia],
                pension: [drawPension],
            };
            for (const step of withdrawal.steps) {
                for (const handler of handlers[step.target]) {
                    for (const p of orderedPeople) {
                        if (needed <= 0)
                            return 0;
                        if (p.alive)
                            handler(p);
                    }
                }
            }
            return Math.max(0, needed);
        };
        let unmetShortfall = 0;
        // The sweep's own ledger for the year (client goals, 2026-09-03): one line
        // per configured step, whatever happened to it, so a fund-if-surplus goal's
        // "funded in N of M years" is READ from the engine rather than rebuilt.
        const sweepLines = [];
        const sweepLine = (step, desired, placed, limit) => {
            sweepLines.push(Object.freeze({
                target: step.target,
                ...(step.dependentId !== undefined ? { dependentId: step.dependentId } : {}),
                ...(step.goalId !== undefined ? { goalId: step.goalId } : {}),
                ...(step.personId !== undefined ? { personId: step.personId } : {}),
                ...(step.liabilityId !== undefined ? { liabilityId: step.liabilityId } : {}),
                desired: desired === null ? null : pence(desired),
                placed: pence(placed),
                limit,
            }));
        };
        /** A `jisa` step's yearly amount THIS year — escalated from the plan start
         *  like a contribution (flat when no escalation is set); null = uncapped. */
        const jisaDesired = (step) => step.annualCap === null
            ? null
            : escalate(step.annualCap, step.escalation, () => 0, startYear, year, escCtxFor);
        /** Why a goal step is not active this year at all, if it is not. */
        const jisaInactiveReason = (step) => {
            if (step.startYear !== undefined && year < step.startYear)
                return 'inactive';
            if (step.endYear !== undefined && year > step.endYear)
                return 'inactive';
            if (step.target === 'jisa') {
                const dep = (input.dependents ?? []).find((d) => d.id === step.dependentId);
                if (dep !== undefined && ageAtTaxYearStart(dep.dateOfBirth, year) >= 18) {
                    return 'childOver18';
                }
            }
            return null;
        };
        const isGoalStep = (step) => step.target === 'jisa' ||
            step.target === 'spend' ||
            step.target === 'liability' ||
            (step.target === 'pension' && step.goalId !== undefined);
        /** Overpayments placed by `liability` steps this year — they reduce the year's net position. */
        let sweepOverpaid = 0;
        /** Goal spends placed this year — they left the household, so they reduce the year's net position too. */
        let sweepSpent = 0;
        if (net >= 0) {
            // Surplus waterfall — adviser-configurable, statutory caps enforced.
            let surplus = net;
            for (const step of waterfall.steps) {
                const desired = isGoalStep(step) ? jisaDesired(step) : step.annualCap;
                const inactive = isGoalStep(step) ? jisaInactiveReason(step) : null;
                if (inactive !== null) {
                    sweepLine(step, desired, 0, inactive);
                    continue;
                }
                if (surplus <= 0) {
                    sweepLine(step, desired, 0, surplus < net ? 'earlierSteps' : 'noSurplus');
                    continue;
                }
                const surplusBefore = surplus;
                let stepRoom = (isGoalStep(step) ? desired : step.annualCap) ?? Number.MAX_SAFE_INTEGER;
                let limit = 'none';
                switch (step.target) {
                    case 'isa': {
                        for (const p of orderedPeople) {
                            if (surplus <= 0 || stepRoom <= 0 || !p.alive)
                                continue;
                            const a = accOf(p.input.id);
                            const room = isaSubscriptionRoom(rules, pence(a.isaSubscribed));
                            const put = Math.min(surplus, stepRoom, room);
                            if (put <= 0)
                                continue;
                            p.open.stocksIsa += put;
                            a.isaSubscribed += put;
                            addTraceContrib(p.input.id, 'stocks_isa', put);
                            surplus -= put;
                            stepRoom -= put;
                        }
                        break;
                    }
                    case 'pension': {
                        // Net cash into a personal pension with relief at source: the pot
                        // receives net × 100/80. Higher-rate relief on waterfall
                        // contributions is NOT reclaimed here (conservative, documented).
                        // The engine's OWN placements are capped at the greater of relevant
                        // UK earnings (employment + self-employment) and the £3,600 gross
                        // non-earner basic amount, and stop at age 75 — so a low/non-earner
                        // can still get the £3,600 relief but the model never shows an HMRC
                        // top-up the person could not claim.
                        for (const p of orderedPeople) {
                            if (surplus <= 0 || stepRoom <= 0 || !p.alive)
                                continue;
                            // A goal's "extra into MY pension" names the person.
                            if (step.personId !== undefined && p.input.id !== step.personId)
                                continue;
                            if (ageIn(p.input, year) >= p.input.retirementAge || ageIn(p.input, year) >= 75) {
                                // Past retirement (or 75) a pension takes no more. For a GOAL
                                // step that is the honest reason — never "the annual limit".
                                if (step.goalId !== undefined)
                                    limit = 'retired';
                                continue;
                            }
                            const a = accOf(p.input.id);
                            const personTotalIncome = a.nonSavings + a.savings + a.cashInterestTaxable + a.dividends + a.giaYieldTaxable;
                            const relevantEarnings = a.employment + a.selfEmployment;
                            const allowance = effectiveAnnualAllowance(rules, {
                                thresholdIncome: pence(Math.max(0, personTotalIncome - a.grossPensionContributions)),
                                adjustedIncome: pence(personTotalIncome),
                                mpaaTriggered: p.mpaaTriggered,
                            });
                            const relievable = relievableContributionLimit(rules, pence(relevantEarnings), allowance);
                            const grossRoom = Math.max(0, relievable - a.grossPensionContributions);
                            const netRoom = grossRoom - bpsOf(grossRoom, rules.incomeTax.basicRateBps);
                            const putNet = Math.min(surplus, stepRoom, netRoom);
                            if (putNet <= 0)
                                continue;
                            const putGross = Math.trunc((putNet * 10_000) / (10_000 - rules.incomeTax.basicRateBps));
                            autoDcPot(p).uncrystallised += putGross;
                            a.grossPensionContributions += putGross;
                            addTraceContrib(p.input.id, 'dc_pension', putGross);
                            flowReliefTopUp += putGross - putNet;
                            surplus -= putNet;
                            stepRoom -= putNet;
                        }
                        break;
                    }
                    case 'gia': {
                        const holder = orderedPeople.find((p) => p.alive);
                        if (holder) {
                            const put = Math.min(surplus, stepRoom);
                            holder.open.gia.value += put;
                            holder.open.gia.cost += put;
                            addTraceContrib(holder.input.id, 'gia', put);
                            surplus -= put;
                        }
                        break;
                    }
                    case 'cash': {
                        const holder = orderedPeople.find((p) => p.alive);
                        if (!holder)
                            break;
                        // A cash step may be a BUFFER: fill only up to the target
                        // balance (months of this year's spending, or an absolute
                        // figure). No target = absorb without limit.
                        let room = Math.min(surplus, stepRoom);
                        const monthsTarget = step.targetBalanceMonthsOfSpending != null
                            ? Math.trunc((spending * step.targetBalanceMonthsOfSpending) / 12)
                            : null;
                        const target = step.targetBalance ?? monthsTarget;
                        if (target != null) {
                            let householdOpenCash = 0;
                            for (const p of orderedPeople) {
                                if (p.alive)
                                    householdOpenCash += p.open.cash;
                            }
                            room = Math.min(room, Math.max(0, target - householdOpenCash));
                        }
                        if (room > 0) {
                            holder.open.cash += room;
                            addTraceContrib(holder.input.id, 'cash', room);
                            surplus -= room;
                        }
                        break;
                    }
                    case 'jisa': {
                        // Fund-if-surplus goal step (client goals, 2026-09-03). The child's
                        // Junior ISA is credited from what is LEFT after tax, all spending,
                        // debt service, student-loan repayments and every unconditional
                        // step-6 contribution. The per-child statutory limit is shared with
                        // step 6 through the same subscribed map; the holding, the
                        // household contribution bucket and the audit identities are
                        // updated exactly as step 6 updates them. Never funded by drawing
                        // on a pot: this whole branch runs only when `net >= 0`.
                        const funder = orderedPeople.find((p) => p.alive);
                        if (funder === undefined) {
                            limit = 'funderDeceased';
                            break;
                        }
                        const holding = jisaHoldings.find((j) => j.dependentId === step.dependentId);
                        if (holding === undefined) {
                            limit = 'noRoom';
                            break;
                        }
                        const subscribed = jisaSubscribedByDependent.get(holding.dependentId) ?? 0;
                        const capRoom = jisaSubscriptionRoom(rules, pence(subscribed));
                        const put = Math.min(surplus, stepRoom, capRoom);
                        if (put <= 0) {
                            limit = capRoom <= 0 ? 'statutoryLimit' : 'annualCap';
                            break;
                        }
                        holding.value += put;
                        jisaSubscribedByDependent.set(holding.dependentId, subscribed + put);
                        hhContrib.jisa += put;
                        surplus -= put;
                        stepRoom -= put;
                        if (desired !== null && put >= desired)
                            limit = 'none';
                        else if (put === capRoom && (desired === null || capRoom < desired)) {
                            limit = 'statutoryLimit';
                        }
                        else if (desired === null)
                            limit = 'none';
                        else
                            limit = surplusBefore < net ? 'earlierSteps' : 'noSurplus';
                        break;
                    }
                    case 'spend': {
                        // A goal spend (the yearly holiday, regular support, a one-off
                        // deposit gift): paid out of what is LEFT, never from a pot. It
                        // leaves the household through the life-event outflow bucket and
                        // is itemised on the trace under the goal's own words; a gift also
                        // joins the inheritance-tax gift list exactly as a gifted life
                        // event does.
                        const funder = orderedPeople.find((p) => p.alive);
                        if (funder === undefined) {
                            limit = 'funderDeceased';
                            break;
                        }
                        const put = Math.min(surplus, stepRoom);
                        if (put <= 0) {
                            limit = 'annualCap';
                            break;
                        }
                        flowLifeEventOut += put;
                        lifeEventOutflowLines.push({ label: step.label ?? 'Goal spend', amount: pence(put) });
                        if (step.isGift === true)
                            sweepGifts.push({ year, value: put });
                        sweepSpent += put;
                        surplus -= put;
                        stepRoom -= put;
                        limit =
                            desired !== null && put >= desired
                                ? 'none'
                                : surplusBefore < net
                                    ? 'earlierSteps'
                                    : 'noSurplus';
                        break;
                    }
                    case 'liability': {
                        // An overpayment on a named debt out of surplus. The balance falls,
                        // the debt line records it, and it counts as debt service for the
                        // year — so the year's net position and both conservation
                        // identities (assets and debt) carry it.
                        const funder = orderedPeople.find((p) => p.alive);
                        if (funder === undefined) {
                            limit = 'funderDeceased';
                            break;
                        }
                        const l = liabilities.find((x) => x.input.id === step.liabilityId && liabilityStarted(x, year));
                        if (l === undefined) {
                            limit = 'noRoom';
                            break;
                        }
                        if (l.balance <= 0) {
                            limit = 'debtCleared';
                            break;
                        }
                        const put = Math.min(surplus, stepRoom, l.balance);
                        if (put <= 0) {
                            limit = 'annualCap';
                            break;
                        }
                        const opening = l.balance;
                        l.balance -= put;
                        sweepOverpaid += put;
                        debtLines.push(Object.freeze({
                            liabilityId: l.input.id,
                            label: `${l.input.label} — overpayment from surplus`,
                            opening: pence(opening),
                            interest: pence(0),
                            paid: pence(put),
                            closing: pence(l.balance),
                            cleared: l.balance === 0 ? 'finalPayment' : 'no',
                        }));
                        surplus -= put;
                        stepRoom -= put;
                        limit =
                            l.balance === 0 && (desired === null || put < desired)
                                ? 'debtCleared'
                                : desired !== null && put >= desired
                                    ? 'none'
                                    : surplusBefore < net
                                        ? 'earlierSteps'
                                        : 'noSurplus';
                        break;
                    }
                    default:
                        break;
                }
                const placed = surplusBefore - surplus;
                if (step.target !== 'jisa' &&
                    step.target !== 'spend' &&
                    step.target !== 'liability' &&
                    !(limit === 'retired' && placed === 0)) {
                    if (desired !== null && placed >= desired)
                        limit = 'none';
                    else if (surplus <= 0) {
                        limit = desired === null ? 'none' : surplusBefore < net ? 'earlierSteps' : 'noSurplus';
                    }
                    else if (desired === 0)
                        limit = 'annualCap';
                    else
                        limit = step.target === 'cash' ? 'noRoom' : 'statutoryLimit';
                }
                sweepLine(step, desired, placed, limit);
            }
            if (surplus > 0) {
                // Whatever the configured steps did not absorb defaults to cash.
                const holder = orderedPeople.find((p) => p.alive);
                if (holder) {
                    holder.open.cash += surplus;
                    addTraceContrib(holder.input.id, 'cash', surplus);
                }
            }
        }
        else {
            // A deficit year: the surplus loop never runs, so every step — and every
            // fund-if-surplus goal — is simply unfunded, and the ledger says so.
            for (const step of waterfall.steps) {
                const desired = isGoalStep(step) ? jisaDesired(step) : step.annualCap;
                const inactive = isGoalStep(step) ? jisaInactiveReason(step) : null;
                sweepLine(step, desired, 0, inactive ?? 'noSurplus');
            }
            // Deficit: draw down through the withdrawal waterfall, then settle the
            // CGT the draws realised. Extra draws can themselves realise more
            // gains, so the loop RE-COMPUTES the owed CGT after every drawing round
            // and only stops once a round both needed nothing and realised no new
            // gains (geometric convergence; the cap is a backstop, and any residual
            // after it lands in the unmet shortfall rather than being dropped).
            const owedCgtDelta = () => {
                let delta = 0;
                for (const p of orderedPeople) {
                    const t = personTax.get(p.input.id);
                    if (!t)
                        continue;
                    const a = accOf(p.input.id);
                    if (!hasGains(a))
                        continue;
                    const cgt = totalCgtFor(p, t, a);
                    delta += cgt - t.cgt;
                    t.cgt = cgt;
                }
                return Math.max(0, delta);
            };
            let needed = drawWaterfall(-net);
            for (let round = 0; round < 12; round += 1) {
                const cgtOwed = owedCgtDelta();
                if (cgtOwed <= 0 && needed <= 0)
                    break;
                needed = drawWaterfall(needed + cgtOwed);
            }
            // Backstop: any CGT the capped loop did not settle is drawn for here, so
            // it is neither silently dropped NOR mistaken for a shortfall.
            //
            // At penny scale the loop above has a fixed point it can never leave:
            // drawing 1p from a pool whose allowable cost sits below its value
            // realises ~1p of gain, whose CGT rounds straight back up to 1p. The
            // round cap is then always reached with 1p outstanding even though every
            // `drawWaterfall` call returned 0 — i.e. it met each draw in full and was
            // never exhausted. Adding that residue straight to `unmetShortfall` used
            // to declare a household with six figures of drawable assets depleted and
            // insolvent, and raised the client-facing "could not meet all spending"
            // warning, over one penny.
            //
            // So make one final draw instead of asserting a shortfall. If the
            // waterfall still has capacity it settles the residue and `needed` falls
            // to 0; if the household genuinely is exhausted the draw comes back short
            // and that real shortfall flows through untouched.
            needed = drawWaterfall(needed + owedCgtDelta());
            if (needed > 0) {
                unmetShortfall = needed;
                if (depletionYear === null)
                    depletionYear = year;
                for (const p of orderedPeople) {
                    if (!p.alive)
                        continue;
                    const lisaHeld = p.open.lisa + p.shielded.lisa;
                    if (lisaHeld > 0 && ageIn(p.input, year) < (rules.isa.lisaPenaltyFreeAge ?? 60)) {
                        warnOnce(`lisa-locked:${p.input.id}`, {
                            code: 'lisa-locked',
                            message: `A shortfall occurred in ${year} while ${gbpWhole(lisaHeld)} sat in the ${p.input.id}'s Lifetime ISA — the model does not draw a Lifetime ISA before age ${rules.isa.lisaPenaltyFreeAge ?? 60} (the 25% early-withdrawal charge is not modelled).`,
                            year,
                            itemLabel: null,
                        });
                    }
                }
                warnOnce(`depleted:${year}`, {
                    code: 'depleted',
                    // Deliberately year-less wording: the year travels in the `year`
                    // field, so the notes UI can collapse a 40-year shortfall into ONE
                    // ranged note instead of 40 identical sentences (IFA review, aspect
                    // 5 "post-apply noise").
                    message: `The household could not meet all spending from any modelled source; the shortfall is shown on each year's row.`,
                    year,
                    itemLabel: null,
                });
            }
        }
        // BADR lifetime usage advances once the year's gains are final.
        for (const p of orderedPeople) {
            const a = acc.get(p.input.id);
            if (!a || a.badrGains <= 0)
                continue;
            const aea = rules.capitalGains.annualExemptAmount;
            const aeaUsedByOther = Math.min(aea, Math.max(0, a.realisedGains) + Math.max(0, a.residentialGains));
            const taxable = Math.max(0, a.badrGains - Math.max(0, aea - aeaUsedByOther));
            const limitLeft = Math.max(0, (rules.capitalGains.badrLifetimeLimit ?? 0) - p.badrUsed);
            if (taxable > limitLeft) {
                warnOnce(`badr-limit:${p.input.id}:${year}`, {
                    code: 'badr-limit-exhausted',
                    // W1.2 made this client-facing. It used to interpolate the raw role
                    // id ("the client's"), which every other line in the report resolves
                    // to a name, and stated no amount so it could not be reconciled to
                    // the tax figure it qualifies. The engine holds no names (see W1.8),
                    // so the person rides `itemLabel` for any surface that can resolve
                    // one, and the sentence itself does not guess.
                    message: `In ${year}, ${gbpWhole(taxable - limitLeft)} of qualifying gains are above the remaining lifetime limit for business asset disposal relief. That excess is taxed at the standard higher rate of capital gains tax rather than the relief rate.`,
                    year,
                    itemLabel: p.input.id,
                });
            }
            p.badrUsed += Math.min(taxable, limitLeft);
        }
        // 11. growth (capital + income yield), then fee drag (an outflow), on
        // end-of-year balances. Cash is credited EXACTLY the interest that was
        // taxed in step 5 (start-of-year basis, per pot set) so the taxed and
        // received amounts can never diverge; a cash fee (if any) is then charged.
        for (const p of orderedPeople) {
            const pid = p.input.id;
            const a = accOf(pid);
            // Negative (bear-case) interest can at most write a pot down to zero:
            // the rate was computed on the start-of-year balance, but draws happen
            // first, so an unclamped negative credit could push an emptied pot to
            // -1p (found by the Stage 3 invariant harness). The growth bucket
            // records what actually moved so the conservation identity holds.
            const openCashCredit = Math.max(a.cashInterest - a.cashInterestShielded, -p.open.cash);
            const shieldedCashCredit = Math.max(a.cashInterestShielded, -p.shielded.cash);
            p.open.cash += openCashCredit;
            p.shielded.cash += shieldedCashCredit;
            flowGrowth += openCashCredit + shieldedCashCredit;
            for (const [set, shielded] of [
                [p.open, false],
                [p.shielded, true],
            ]) {
                // Total return = capital growth + income yield (reinvested), less any
                // market shock landing this year (clamped: a shock can at most write a
                // pot down to zero). NS&I premium-bond winnings are modelled as
                // TAX-FREE growth on the pot.
                //
                // This rolled-up form is correct ONLY for wrappers whose income is not
                // taxable as it arises — the ISAs, the LISA and NS&I. The GIA is
                // handled separately below, because rolling its yield into growth is
                // what let a taxable distribution compound untaxed.
                const retOf = (w) => replacementOf(w, year) ??
                    effGrowth(pid, w, shielded) +
                        effYield(pid, w, shielded) +
                        growthDeltaOf(w, year) -
                        shockOf(w, year) +
                        (w === 'cash_isa' ? cashRateShiftOf('cash_isa', year) : 0);
                const cashIsaGrowth = Math.max(bpsOf(set.cashIsa, retOf('cash_isa')), -set.cashIsa);
                const stocksIsaGrowth = Math.max(bpsOf(set.stocksIsa, retOf('stocks_isa')), -set.stocksIsa);
                const lisaGrowth = Math.max(bpsOf(set.lisa, retOf('lisa')), -set.lisa);
                const nsiGrowth = Math.max(bpsOf(set.nsi, retOf('nsi')), -set.nsi);
                set.cashIsa += cashIsaGrowth;
                set.stocksIsa += stocksIsaGrowth;
                set.lisa += lisaGrowth;
                // Premium-bond winnings cannot be reinvested above the holding cap:
                // the excess is paid out as cash, as NS&I does in reality.
                set.nsi += nsiGrowth;
                if (set.nsi > nsiMaxHolding) {
                    set.cash += set.nsi - nsiMaxHolding;
                    set.nsi = nsiMaxHolding;
                }
                // The GIA is split, unlike the tax-free wrappers above. CAPITAL growth
                // accrues as an UNREALISED gain (value rises, pooled cost does not),
                // taxed as CGT only on disposal. The income YIELD was already taxed as
                // dividend income in step 5b, so reinvesting it lifts the cost basis by
                // the same amount — otherwise that pound would be taxed twice, once as
                // a dividend now and again as a gain on disposal later.
                // HS-3: a replayed TOTAL return already contains the distribution taxed
                // in step 5b, so the capital leg is the total less that yield — never
                // the total plus it.
                const giaReplaced = replacementOf('gia', year);
                const giaCapitalRate = giaReplaced !== undefined
                    ? giaReplaced - effYield(pid, 'gia', shielded)
                    : effGrowth(pid, 'gia', shielded) + growthDeltaOf('gia', year) - shockOf('gia', year);
                const giaCapitalGrowth = Math.max(bpsOf(set.gia.value, giaCapitalRate), -set.gia.value);
                // The exact amount taxed in step 5b is the amount credited here, so the
                // taxed and received figures can never diverge (the cash-interest rule).
                const giaYieldCredit = Math.max(shielded ? a.giaYieldShielded : a.giaYield - a.giaYieldShielded, -(set.gia.value + giaCapitalGrowth));
                set.gia.value += giaCapitalGrowth + giaYieldCredit;
                if (giaYieldCredit > 0)
                    set.gia.cost += giaYieldCredit;
                const giaGrowth = giaCapitalGrowth + giaYieldCredit;
                flowGrowth += cashIsaGrowth + stocksIsaGrowth + lisaGrowth + nsiGrowth + giaGrowth;
                addTraceGrowth(pid, 'cash_isa', cashIsaGrowth, 0);
                addTraceGrowth(pid, 'stocks_isa', stocksIsaGrowth, 0);
                addTraceGrowth(pid, 'lisa', lisaGrowth, 0);
                addTraceGrowth(pid, 'nsi', nsiGrowth, 0);
                addTraceGrowth(pid, 'gia', giaGrowth, 0);
                // Fee drag: charged on the grown balance of each pot, leaves the
                // household. GIA fees are a pro-rata part-disposal so the pooled cost
                // basis stays consistent (no CGT is modelled on this micro-disposal).
                const feeCash = bpsOf(set.cash, effFee(pid, 'cash', shielded));
                const feeCashIsa = bpsOf(set.cashIsa, effFee(pid, 'cash_isa', shielded));
                const feeStocksIsa = bpsOf(set.stocksIsa, effFee(pid, 'stocks_isa', shielded));
                const feeLisa = bpsOf(set.lisa, effFee(pid, 'lisa', shielded));
                const feeNsi = bpsOf(set.nsi, effFee(pid, 'nsi', shielded));
                const feeGia = bpsOf(set.gia.value, effFee(pid, 'gia', shielded));
                set.cash -= feeCash;
                set.cashIsa -= feeCashIsa;
                set.stocksIsa -= feeStocksIsa;
                set.lisa -= feeLisa;
                set.nsi -= feeNsi;
                if (feeGia > 0 && set.gia.value > 0) {
                    const costPart = feeGia >= set.gia.value
                        ? set.gia.cost
                        : Math.trunc((set.gia.cost * feeGia) / set.gia.value);
                    set.gia.value -= feeGia;
                    set.gia.cost -= costPart;
                }
                flowFees += feeCash + feeCashIsa + feeStocksIsa + feeLisa + feeNsi + feeGia;
                addTraceGrowth(pid, 'cash', 0, feeCash);
                addTraceGrowth(pid, 'cash_isa', 0, feeCashIsa);
                addTraceGrowth(pid, 'stocks_isa', 0, feeStocksIsa);
                addTraceGrowth(pid, 'lisa', 0, feeLisa);
                addTraceGrowth(pid, 'nsi', 0, feeNsi);
                addTraceGrowth(pid, 'gia', 0, feeGia);
            }
            // Cash interest was credited above (start-of-year basis) — count it as
            // the cash pot's growth in the trace.
            addTraceGrowth(pid, 'cash', a.cashInterest, 0);
            // DC pots: growth + reinvested yield, then fees — per pot, at each
            // pot's own resolved rates (true per-pot divergence).
            for (const pot of p.dcPots) {
                const ret = replacementOf('dc_pension', year) ??
                    pot.growthBps +
                        pot.yieldBps +
                        growthDeltaOf('dc_pension', year) -
                        shockOf('dc_pension', year);
                const gU = Math.max(bpsOf(pot.uncrystallised, ret), -pot.uncrystallised);
                const gD = Math.max(bpsOf(pot.drawdown, ret), -pot.drawdown);
                pot.uncrystallised += gU;
                pot.drawdown += gD;
                flowGrowth += gU + gD;
                const fU = bpsOf(pot.uncrystallised, pot.feeBps);
                const fD = bpsOf(pot.drawdown, pot.feeBps);
                pot.uncrystallised -= fU;
                pot.drawdown -= fD;
                flowFees += fU + fD;
                addTraceGrowth(pid, 'dc_pension', gU + gD, fU + fD);
            }
        }
        // Tracked holdings (business / other assets): own growth rate, which may
        // be negative (depreciation) but never takes a holding below zero.
        for (const h of trackedHoldings) {
            const hWrapper = h.asset.wrapper === 'business' ? 'business' : 'other_asset';
            const rate = replacementOf(hWrapper, year) ??
                (h.asset.growthRateBps ?? growthOf(h.asset.wrapper, null)) +
                    growthDeltaOf(hWrapper, year) -
                    shockOf(hWrapper, year);
            if (!Number.isSafeInteger(h.value)) {
                throw new RangeError(`"${h.asset.label}" left the exact-integer pence range in ${year} — ` +
                    'its growth rate compounds beyond what can be represented penny-exactly. ' +
                    'Check the asset for an implausible growth override.');
            }
            // Depreciation can at most write the holding down to zero.
            const g = Math.max(bpsOf(h.value, rate), -h.value);
            h.value += g;
            flowGrowth += g;
            const fee = bpsOf(h.value, h.asset.feeDragBps ?? feeOf(h.asset.wrapper));
            h.value -= fee;
            flowFees += fee;
            const hhW = h.asset.wrapper === 'business' ? 'business' : 'other_asset';
            hhGrowth[hhW] += g;
            hhFees[hhW] += fee;
        }
        // Junior ISAs grow tax-free at their own (or wrapper) rate.
        for (const j of jisaHoldings) {
            const ret = replacementOf('jisa', year) ??
                (j.asset.growthRateBps ?? growthOf('jisa', null)) +
                    (j.asset.yieldBps ?? yieldOf('jisa')) +
                    growthDeltaOf('jisa', year) -
                    shockOf('jisa', year);
            const g = Math.max(bpsOf(j.value, ret), -j.value);
            j.value += g;
            flowGrowth += g;
            const fee = bpsOf(j.value, j.asset.feeDragBps ?? feeOf('jisa'));
            j.value -= fee;
            flowFees += fee;
            hhGrowth.jisa += g;
            hhFees.jisa += fee;
        }
        for (const prop of properties) {
            if (!prop.active)
                continue;
            const g = Math.max(bpsOf(prop.value, replacementOf('property', year) ??
                growthOf('property', prop.asset.growthRateBps) - shockOf('property', year)), -prop.value);
            prop.value += g;
            flowGrowth += g;
            const feeBps = prop.asset.feeDragBps ?? feeOf('property');
            const fee = bpsOf(prop.value, feeBps);
            prop.value -= fee;
            flowFees += fee;
            hhGrowth.property += g;
            hhFees.property += fee;
        }
        for (const prop of eventProperties) {
            const g = Math.max(bpsOf(prop.value, replacementOf('property', year) ??
                growthOf('property', null) +
                    growthDeltaOf('property', year) -
                    shockOf('property', year)), -prop.value);
            prop.value += g;
            flowGrowth += g;
            hhGrowth.property += g;
        }
        // 12. death processing (end of year): spouse-exempt transfers
        for (const p of orderedPeople) {
            if (!p.alive || p.input.deathAge === null)
                continue;
            if (ageIn(p.input, year) >= p.input.deathAge) {
                p.alive = false;
                someoneHasDied = true;
                /**
                 * A2.5 review (all three personas). The first-death assessment read the
                 * household's `totalNetWorth`, so a plan where the partner solely owned
                 * £1.2m and the client died first printed "Estate assessed £1,110,000,
                 * estimated inheritance tax £314,000" on the client's own report — tax
                 * on money the deceased never owned. The single-estate simplification
                 * is defensible at the LAST survivor's death, where there is nobody
                 * left to own anything separately; at the first it is not.
                 *
                 * Captured HERE because this is the only point the deceased's own
                 * holdings still exist as theirs: the transfer to the survivor happens
                 * on the next lines, and by the time the year row is recorded every
                 * asset already belongs to whoever survived. A joint holding counts at
                 * half — only one of the two halves passes on a death.
                 */
                const shareOf = (owner) => owner === p.input.id ? 1 : (owner ?? 'joint') === 'joint' ? 0.5 : 0;
                const own = personWrapperTotals(p);
                let deathProperty = 0;
                let deathBusiness = 0;
                let deathOther = 0;
                for (const prop of properties) {
                    if (!prop.active)
                        continue;
                    deathProperty += Math.round(prop.value * shareOf(prop.owner));
                }
                let deathBusinessQualifying = 0;
                for (const h of trackedHoldings) {
                    const v = Math.round(h.value * shareOf(h.owner));
                    if (h.asset.wrapper === 'business') {
                        deathBusiness += v;
                        /**
                         * A2.5 review round 2 (tech-admin). Business relief was applied at
                         * the plan's HOUSEHOLD-weighted rate, so the survivor's own company
                         * moved the deceased's estate: a client owning a £500k trading
                         * company at 100% relief, beside a partner's £500k investment
                         * company at 0%, was assessed on £250,000 and passed on 23% of the
                         * band — and deleting the PARTNER's company changed the CLIENT's
                         * estate to £0. The same class as the defect this round fixed, one
                         * level down. Each person's relief is now weighted on what they own.
                         */
                        deathBusinessQualifying += bpsOf(v, h.asset.bprRateBps ?? 0);
                    }
                    else
                        deathOther += v;
                }
                let deathDebts = 0;
                for (const l of liabilities) {
                    if (l.settled === true || !liabilityStarted(l, year) || l.balance <= 0)
                        continue;
                    deathDebts += Math.round(l.balance * shareOf(l.input.ownerId));
                }
                deathEstates.push({
                    personId: p.input.id,
                    year,
                    // Junior ISAs are the children's and are not in anybody's estate, so
                    // they are simply not gathered here.
                    liquid: own.cash + own.cash_isa + own.stocks_isa + own.lisa + own.nsi + own.gia,
                    pension: own.dc_pension,
                    property: deathProperty,
                    business: deathBusiness,
                    businessQualifying: deathBusinessQualifying,
                    other: deathOther,
                    debts: deathDebts,
                });
                const survivor = survivorOf(p.input.id);
                /**
                 * GOAL 2026-09-10 item 12 — survivor cashflow made explicit. Two
                 * things stop at this death on facts nobody recorded, and both used to
                 * stop in silence:
                 *  - a defined-benefit pension with NO survivor's share recorded ends
                 *    with the member. A recorded 0% is the adviser's stated fact and
                 *    raises nothing; an absent field is a question never asked.
                 *  - salary or dividends drawn from a business that passes, on these
                 *    very lines, to the survivor: the business carries on under the
                 *    survivor while the income it paid vanishes.
                 * Neither is changed here — the plan does not invent a survivor's
                 * pension or keep paying a dead person's salary — but each is SAID.
                 */
                if (survivor) {
                    const whoDied = p.input.id === 'client' ? 'the client' : 'the partner';
                    const whoSurvives = survivor.input.id === 'client' ? 'the client' : 'the partner';
                    for (const stream of allIncomeStreams) {
                        if (stream.ownerId !== p.input.id || stream.amount <= 0)
                            continue;
                        if (stream.frequency === Frequency.Once)
                            continue;
                        const paysPastDeath = streamStartYear(stream) <= year + 1 &&
                            (stream.endYear === null || stream.endYear > year);
                        if (!paysPastDeath)
                            continue;
                        if (stream.type === 'db_pension' && stream.dbSpouseFractionBps === undefined) {
                            warnOnce(`db-survivor-unrecorded:${stream.id}`, {
                                code: 'db-pension-survivor-fraction-unrecorded',
                                message: `${stream.label}: this defined-benefit pension stops at ${whoDied}'s death in ${year} because no survivor's share is recorded for it. Most schemes pay a survivor's pension — commonly a half or two-thirds — but the plan holds no figure, so ${whoSurvives} receives nothing from it. Record the scheme's survivor share on the income line if one exists.`,
                                year,
                                itemLabel: stream.label,
                            });
                        }
                        if (stream.sourceAssetId != null && stream.type !== 'rental') {
                            const holding = trackedHoldings.find((h) => h.asset.id === stream.sourceAssetId &&
                                h.asset.wrapper === 'business' &&
                                h.sold === undefined &&
                                h.value > 0);
                            if (holding !== undefined) {
                                warnOnce(`business-income-stops:${stream.id}`, {
                                    code: 'business-income-stops-at-death',
                                    message: `${stream.label}: this income is drawn from ${holding.asset.label}, which passes to ${whoSurvives} at ${whoDied}'s death in ${year}. The business stays on the balance sheet at its full value, but the income it paid stops with ${whoDied} — the plan does not know whether ${whoSurvives} would draw from it, sell it, or wind it up. Record the survivor's position on the business if it is known.`,
                                    year,
                                    itemLabel: stream.label,
                                });
                            }
                        }
                    }
                    survivor.open.cash += p.open.cash;
                    survivor.open.cashIsa += p.open.cashIsa; // additional permitted subscription
                    survivor.open.stocksIsa += p.open.stocksIsa;
                    // LISA balances pass via the APS into the survivor's S&S ISA
                    // (documented simplification: no LISA-to-LISA inheritance).
                    survivor.open.stocksIsa += p.open.lisa;
                    survivor.open.nsi += p.open.nsi;
                    if (survivor.open.nsi > nsiMaxHolding) {
                        // Merged holdings cannot exceed the product cap; the estate pays
                        // the excess out as cash.
                        survivor.open.cash += survivor.open.nsi - nsiMaxHolding;
                        survivor.open.nsi = nsiMaxHolding;
                        warnOnce(`nsi-death:${p.input.id}`, {
                            code: 'nsi-cap-applied',
                            message: `Inherited premium bonds took the survivor above the ${gbpWhole(nsiMaxHolding)} maximum; the excess is held as cash.`,
                            year,
                            itemLabel: null,
                        });
                    }
                    /**
                     * IM-F5-01 — probate uplift. The deceased's share of every
                     * CGT-bearing holding rebases to market value at death, so the
                     * survivor is not taxed on gains that died with them. This carried
                     * the ORIGINAL cost across: a £600,000 joint account with a £250,000
                     * cost billed the widow CGT on the whole £350,000 gain as she sold
                     * down — "tens of thousands of pounds of tax she will never pay, in
                     * the year we're modelling her husband's death".
                     */
                    survivor.open.gia.value += p.open.gia.value;
                    survivor.open.gia.cost += p.open.gia.value;
                    // A2.5a review: only when something actually rebased. The note fired
                    // on couples holding nothing but ISAs and pensions, asserting a
                    // rebasing that had not happened and a CGT figure that had not moved.
                    if (p.open.gia.value > p.open.gia.cost)
                        probateUplifted = true;
                    if (p.shielded.gia.value > p.shielded.gia.cost)
                        probateUplifted = true;
                    survivor.shielded.cash += p.shielded.cash;
                    survivor.shielded.cashIsa += p.shielded.cashIsa;
                    survivor.shielded.stocksIsa += p.shielded.stocksIsa + p.shielded.lisa;
                    survivor.shielded.nsi += p.shielded.nsi;
                    survivor.shielded.gia.value += p.shielded.gia.value;
                    survivor.shielded.gia.cost += p.shielded.gia.value;
                    // DC pots pass whole to the survivor. They keep their identity and
                    // rates; access follows the SURVIVOR's retirement age (or now, if
                    // the survivor has already reached it) — mirroring the previous
                    // pooled-transfer behaviour.
                    for (const pot of p.dcPots) {
                        survivor.dcPots.push({ ...pot, accessAge: survivor.input.retirementAge });
                    }
                    p.dcPots = [];
                    p.open = emptyPots();
                    p.shielded = emptyPots();
                    // Solely-owned tracked holdings pass to the survivor (spouse
                    // exemption); joint holdings continue under the survivor alone.
                    /**
                     * A2.5a review (adviser). Only the DECEASED's share rebases.
                     *
                     * The first cut set `baseCost = value` on the whole holding, so a
                     * jointly-owned let sold after a first death showed £0 of capital
                     * gains tax where the true bill was about half the gain — measured at
                     * £0 against £33,018 on a £200k-cost, £500k-at-death let. The GIA
                     * escaped this only because it is split into per-person buckets at
                     * setup, so its transfer already touches one half. A property and a
                     * tracked holding are single objects carrying `owner: 'joint'`, so
                     * the survivor's own half was being rebased too — a wrong figure in
                     * the flattering direction, underneath a note pre-explaining why it
                     * looked low.
                     */
                    const rebasedShare = (baseCost, value, owner) => owner === 'joint' ? Math.round(baseCost / 2 + value / 2) : value;
                    for (const h of trackedHoldings) {
                        if (h.owner === p.input.id || h.owner === 'joint') {
                            const wasJoint = h.owner === 'joint';
                            h.owner = survivor.input.id;
                            if (h.baseCost !== null && h.value > h.baseCost)
                                probateUplifted = true;
                            h.baseCost =
                                h.baseCost === null
                                    ? null
                                    : rebasedShare(h.baseCost, h.value, wasJoint ? 'joint' : 'sole');
                        }
                    }
                    /**
                     * IM-F2-03 + IM-F5-01. Properties transfer too — the rent follows
                     * the mutable owner, and the base cost rebases so a later sale is not
                     * taxed on the deceased's gain.
                     */
                    for (const prop of properties) {
                        if (!prop.active)
                            continue;
                        if (prop.owner === p.input.id || prop.owner === 'joint') {
                            const wasJoint = prop.owner === 'joint';
                            prop.owner = survivor.input.id;
                            // A2.5a review: the main home bears no CGT, so a couple owning
                            // only their home was getting a note asserting a rebasing that
                            // changes nothing and a tax figure that never existed.
                            if (prop.asset.property?.isPrimaryResidence !== true && prop.value > prop.baseCost) {
                                probateUplifted = true;
                            }
                            prop.baseCost = rebasedShare(prop.baseCost, prop.value, wasJoint ? 'joint' : 'sole');
                        }
                    }
                    // JISAs are the CHILD's — nothing to transfer.
                }
                /**
                 * FG-F6-04. A solely-owned debt goes in front of an explicit choice
                 * recorded on the row. Default: settled from the estate for a sole
                 * debt, continues for a joint one — and the engine SAYS which it
                 * applied, because "the plan keeps paying that loan out of the widow's
                 * income for another four years" and clearing it silently would be the
                 * same failure in the other direction.
                 */
                for (const l of liabilities) {
                    const owner = l.input.ownerId ?? 'joint';
                    // A2.5a review: an un-started liability is not an estate debt — a
                    // 2032 loan on a plan whose owner dies in 2027 was being wiped and
                    // named in the note as settled.
                    if (!liabilityStarted(l, year))
                        continue;
                    if (l.balance <= 0)
                        continue;
                    if (owner !== p.input.id) {
                        /**
                         * A2.5a review: a JOINT debt with an explicit `settledFromEstate`
                         * was silently ignored, because the owner test can never match
                         * 'joint'. The contract accepted an instruction the engine dropped.
                         * Honoured now — the adviser said it, and half-honouring an
                         * instruction is worse than refusing it.
                         */
                        if (!(owner === 'joint' && l.input.onDeath === 'settledFromEstate'))
                            continue;
                    }
                    const treatment = l.input.onDeath ?? 'settledFromEstate';
                    if (treatment === 'settledFromEstate') {
                        /**
                         * A2.5a review (adviser). "Settled from the estate" has to be PAID,
                         * not forgiven. The first cut zeroed the balance with no cash
                         * outflow anywhere, and since the estate's assets had already
                         * transferred to the survivor at full value her net worth simply
                         * rose by the outstanding balance — a windfall, permanently, on a
                         * default. The estate discharges its debts before distributing, so
                         * the money comes out of what she inherits.
                         */
                        // With no survivor the estate is being wound up entirely and
                        // there is no inherited pot to charge; the debt still ends.
                        /**
                         * A2.5a review (tech-admin). Booked as a REPAYMENT, not a
                         * disappearance. Zeroing the balance without it broke the engine's
                         * own debt identity — closingDebt = openingDebt + drawn + interest
                         * − repaid — by the whole balance, which is money created. The
                         * asset side then expects the pots to fall by the same amount,
                         * which is what the draw below does, so both identities hold.
                         */
                        /**
                         * A2.5a review round 2 (adviser + tech-admin, independently, on
                         * their own probes). The first fix booked `debtService += balance`
                         * unconditionally and then drew what it could, so on an estate that
                         * could not meet the debt the shortfall was FORGIVEN and reported
                         * as paid: a widow with a £400k flat, a £300k interest-only
                         * mortgage and £20k of cash gained £280,000 the year her husband
                         * died. Round 1's defect, narrowed to the illiquid case — which is
                         * the commonest UK shape there is — and still on a default.
                         *
                         * Only what a pot actually gives up is booked. Anything the estate
                         * cannot meet stays OUTSTANDING on the liability and is named, and
                         * the conservation identity holds either way.
                         */
                        const payer = survivor ?? p;
                        let owing = l.balance;
                        // Illiquid assets are never auto-liquidated to pay a debt — the
                        // same rule the withdrawal waterfall follows. A plan that needs the
                        // flat sold needs the adviser to say so.
                        const takeFrom = (get, put) => {
                            if (owing <= 0)
                                return;
                            const take = Math.min(owing, get());
                            if (take <= 0)
                                return;
                            put(get() - take);
                            owing -= take;
                        };
                        /**
                         * In the plan's OWN configured order, not a hard-coded one. Both
                         * `docs/CALCULATION_METHOD.md` and the annual operation order said
                         * "the ordinary withdrawal order" while the code walked a literal
                         * list that put ISAs ahead of the general account — so an executor
                         * broke the survivor's ISA wrapper to avoid realising a gain, and
                         * two documents asserted something that was not happening.
                         */
                        for (const step of withdrawal.steps) {
                            if (owing <= 0)
                                break;
                            const set = payer.open;
                            switch (step.target) {
                                case 'cash':
                                    takeFrom(() => set.cash, (v) => (set.cash = v));
                                    // NS&I premium bonds are cash-like, as in the ordinary draw.
                                    takeFrom(() => set.nsi, (v) => (set.nsi = v));
                                    break;
                                case 'cash_isa':
                                    takeFrom(() => set.cashIsa, (v) => (set.cashIsa = v));
                                    break;
                                case 'stocks_isa':
                                    takeFrom(() => set.stocksIsa, (v) => (set.stocksIsa = v));
                                    break;
                                case 'isa':
                                    takeFrom(() => set.cashIsa, (v) => (set.cashIsa = v));
                                    takeFrom(() => set.stocksIsa, (v) => (set.stocksIsa = v));
                                    // The LISA joins the ISA step only from the penalty-free age,
                                    // exactly as the ordinary draw does: the 25% early-withdrawal
                                    // charge is not modelled, so the engine must not spend one.
                                    // The first cut raided a 58-year-old's LISA ahead of a
                                    // £200,000 general account and charged her nothing for it.
                                    if (ageIn(payer.input, year) >= (rules.isa.lisaPenaltyFreeAge ?? 60)) {
                                        takeFrom(() => set.lisa, (v) => (set.lisa = v));
                                    }
                                    break;
                                case 'gia': {
                                    const pool = set.gia;
                                    const take = Math.min(owing, pool.value);
                                    if (take > 0) {
                                        const allowableCost = take === pool.value ? pool.cost : Math.trunc((pool.cost * take) / pool.value);
                                        pool.value -= take;
                                        pool.cost -= allowableCost;
                                        owing -= take;
                                    }
                                    break;
                                }
                                // A pension is not an estate asset to be raided for a debt, and
                                // an unscheduled draw is taxable income the death step runs too
                                // late to tax. Skipped deliberately: see the carried limitation
                                // in docs/CALCULATION_METHOD.md.
                                case 'pension':
                                    break;
                            }
                        }
                        /**
                         * A2.5a review round 3. A pot that no waterfall step NAMES was
                         * invisible to the executor: a plan whose withdrawal order is
                         * pension-only left £500,000 of cash untouched and reported the
                         * whole debt unfunded. The waterfall says what to draw FIRST when
                         * funding a shortfall; it is not a list of what the estate owns.
                         * So once the configured steps are exhausted, the remaining
                         * drawable pots are reached in the ordinary executor order.
                         *
                         * Pots the adviser has ring-fenced (`neverDrawFrom`, held in the
                         * shielded set) are still never touched, and neither is a LISA
                         * below the penalty-free age — those are deliberate positions, and
                         * the unfunded note names them as things to check.
                         */
                        if (owing > 0) {
                            const set = payer.open;
                            takeFrom(() => set.cash, (v) => (set.cash = v));
                            takeFrom(() => set.nsi, (v) => (set.nsi = v));
                            takeFrom(() => set.cashIsa, (v) => (set.cashIsa = v));
                            takeFrom(() => set.stocksIsa, (v) => (set.stocksIsa = v));
                            if (ageIn(payer.input, year) >= (rules.isa.lisaPenaltyFreeAge ?? 60)) {
                                takeFrom(() => set.lisa, (v) => (set.lisa = v));
                            }
                            const pool = set.gia;
                            const take = Math.min(owing, pool.value);
                            if (take > 0) {
                                const allowableCost = take === pool.value ? pool.cost : Math.trunc((pool.cost * take) / pool.value);
                                pool.value -= take;
                                pool.cost -= allowableCost;
                                owing -= take;
                            }
                        }
                        const paid = l.balance - owing;
                        debtService += paid;
                        settlementPaid += paid;
                        /**
                         * Itemised as a DEBT line, not a spending line. The first cut
                         * pushed it onto `spendingLines` while it was also inside
                         * `debtService`, and both client surfaces print those side by side,
                         * so the £100,000 appeared twice under a total that counted it once
                         * (review round 3, measured on 10 of 10 settlement shapes). A debt
                         * paid at a death is a payment against that debt: it belongs under
                         * the debt-payments total, as the child that explains it.
                         */
                        if (paid > 0) {
                            const line = Object.freeze({
                                liabilityId: l.input.id,
                                label: l.input.label,
                                opening: pence(0),
                                interest: pence(0),
                                paid: pence(paid),
                                closing: pence(owing),
                                cleared: (owing <= 0 ? 'estateSettlement' : 'no'),
                            });
                            const at = debtLines.findIndex((d) => d.liabilityId === l.input.id);
                            if (at >= 0) {
                                // It already paid this year before the death; fold the
                                // settlement into the same row rather than printing the debt
                                // twice under one heading.
                                const prior = debtLines[at];
                                debtLines[at] = Object.freeze({
                                    ...prior,
                                    paid: pence(prior.paid + paid),
                                    closing: pence(owing),
                                    cleared: line.cleared,
                                });
                            }
                            else {
                                debtLines.push(line);
                            }
                        }
                        l.balance = owing;
                        l.settled = owing <= 0;
                        debtsSettledAtDeath.push({
                            label: l.input.label,
                            paid,
                            unfunded: owing,
                            joint: owner === 'joint',
                        });
                    }
                }
            }
        }
        // 13. record the year
        const balances = {};
        for (const w of WRAPPER_VALUES)
            balances[w] = pence(0);
        for (const p of orderedPeople) {
            for (const set of [p.open, p.shielded]) {
                balances.cash = pence(balances.cash + set.cash);
                balances.cash_isa = pence(balances.cash_isa + set.cashIsa);
                balances.stocks_isa = pence(balances.stocks_isa + set.stocksIsa);
                balances.lisa = pence(balances.lisa + set.lisa);
                balances.nsi = pence(balances.nsi + set.nsi);
                balances.gia = pence(balances.gia + set.gia.value);
            }
            balances.dc_pension = pence(balances.dc_pension + dcTotal(p));
        }
        for (const prop of properties) {
            if (prop.active)
                balances.property = pence(balances.property + prop.value);
        }
        for (const prop of eventProperties) {
            balances.property = pence(balances.property + prop.value);
        }
        for (const h of trackedHoldings) {
            if (h.asset.wrapper === 'business')
                balances.business = pence(balances.business + h.value);
            else
                balances.other_asset = pence(balances.other_asset + h.value);
        }
        for (const j of jisaHoldings) {
            balances.jisa = pence(balances.jisa + j.value);
        }
        // Client goals: closing balance per individually-tracked asset, keyed by
        // the input asset id and sorted by it so the row is order-stable across a
        // death that moves a pot from one person to the other. Pooled per-person
        // wrappers have no asset identity here (see the type).
        const byAsset = {};
        for (const p of orderedPeople) {
            for (const pot of p.dcPots) {
                if (!inputAssetIds.has(pot.id))
                    continue;
                byAsset[pot.id] = (byAsset[pot.id] ?? 0) + pot.uncrystallised + pot.drawdown;
            }
        }
        for (const prop of properties) {
            if (!inputAssetIds.has(prop.asset.id))
                continue;
            byAsset[prop.asset.id] = prop.active ? prop.value : 0;
        }
        for (const h of trackedHoldings)
            byAsset[h.asset.id] = h.value;
        for (const j of jisaHoldings)
            byAsset[j.asset.id] = j.value;
        const balancesByAsset = {};
        for (const id of Object.keys(byAsset).sort())
            balancesByAsset[id] = pence(byAsset[id] ?? 0);
        // Overpayments placed by goal steps are debt service for the year: they
        // join the audit's `debtService` / `debtRepaid` buckets, the year's debt
        // payments, and reduce the year's net position — all AFTER the surplus
        // that funded them was sized, exactly like any other placement.
        debtService += sweepOverpaid;
        const netAfterOverpayments = net - sweepOverpaid - sweepSpent - settlementPaid;
        let liabilitiesOutstanding = 0;
        const byLiability = {};
        for (const l of liabilities) {
            // A debt that has not started yet is not outstanding.
            if (l.settled === true || !liabilityStarted(l, year))
                continue;
            liabilitiesOutstanding += l.balance;
            byLiability[l.input.id] = l.balance;
        }
        const balancesByLiability = {};
        for (const id of Object.keys(byLiability).sort()) {
            balancesByLiability[id] = pence(byLiability[id] ?? 0);
        }
        const taxRows = [];
        let totalTax = 0;
        let studentLoanTotalForYear = 0;
        for (const p of orderedPeople) {
            const t = personTax.get(p.input.id);
            if (!t)
                continue;
            const a = accOf(p.input.id);
            const incomeTaxTotal = t.result.totalTax - t.s24 + t.aaCharge + a.drawTax;
            // NOTE the absence: `t.studentLoan` is NOT a term here, and this is the
            // pin the tests hold. A loan repayment on a tax row would put the tool at
            // odds with the client's P60 and would move every golden master.
            const personTotal = incomeTaxTotal + t.ni + t.cgt;
            totalTax += personTotal;
            studentLoanTotalForYear += t.studentLoan;
            taxRows.push(Object.freeze({
                personId: p.input.id,
                grossIncome: pence(a.nonSavings + a.savings + a.dividends + a.untaxedInflow + a.cashInterest + a.giaYield),
                incomeTax: pence(incomeTaxTotal),
                nationalInsurance: pence(t.ni),
                capitalGainsTax: pence(t.cgt),
                section24Reduction: pence(t.s24),
                totalTax: pence(personTotal),
                // OMITTED when nil so a person with no loan serialises byte-for-byte
                // as they did before this field existed.
                ...(t.studentLoan > 0 ? { studentLoanRepayment: pence(t.studentLoan) } : {}),
            }));
        }
        let invested = 0;
        for (const w of WRAPPER_VALUES)
            invested += balances[w];
        if (!Number.isSafeInteger(invested)) {
            throw new RangeError(`Total wealth left the exact-integer pence range in ${year} — a rate is ` +
                'compounding beyond what can be represented penny-exactly. Check for an ' +
                'implausible growth override.');
        }
        const audit = Object.freeze({
            openingAssets: pence(openingAssets),
            income: pence(inflow - scheduledPensionGross),
            pensionReliefTopUp: pence(flowReliefTopUp),
            employerContributions: pence(flowEmployerConts),
            lifeEventInflows: pence(flowLifeEventIn),
            spending: pence(spending),
            debtService: pence(debtService),
            lifeEventOutflows: pence(flowLifeEventOut),
            tax: pence(totalTax),
            // Its own bucket in the conservation identity. Adding it to `tax` would
            // balance the books just as well and lie about the client's tax bill.
            ...(studentLoanTotalForYear > 0
                ? { studentLoanRepayment: pence(studentLoanTotalForYear) }
                : {}),
            fees: pence(flowFees),
            growth: pence(flowGrowth),
            unmetShortfall: pence(unmetShortfall),
            closingAssets: pence(invested),
            openingDebt: pence(openingDebt),
            debtDrawn: pence(debtDrawn),
            debtInterest: pence(debtInterest),
            debtRepaid: pence(debtService),
            closingDebt: pence(liabilitiesOutstanding),
        });
        cumulativeShortfall += unmetShortfall;
        if (unmetShortfall > 0 && illiquidAtFirstShortfall === null) {
            // End-of-year value of everything the model deliberately does not sell:
            // the "paper-rich, cash-poor" figure behind a rising net-worth line
            // shown next to "depleted" (projection-runaway.md, defect A).
            illiquidAtFirstShortfall = balances.property + balances.business + balances.other_asset;
            if (illiquidAtFirstShortfall > 0) {
                warnOnce(`insolvent-illiquid:${year}`, {
                    code: 'insolvent-with-illiquid-assets',
                    message: `From ${year} the plan cannot cover all of its spending from cash, ISAs, investments or pensions, while ${gbpWhole(illiquidAtFirstShortfall)} sits in property, business interests or other assets. This projection assumes those assets are never sold; net worth after the uncovered spending is shown alongside the headline figure.`,
                    year,
                    itemLabel: null,
                });
            }
        }
        years.push(Object.freeze({
            year,
            taxYearLabel: taxYearLabel(year),
            clientAge,
            balancesByWrapper: Object.freeze(balances),
            liabilitiesOutstanding: pence(liabilitiesOutstanding),
            totalNetWorth: pence(invested - liabilitiesOutstanding),
            grossIncome: pence(grossIncome),
            spending: pence(spending + debtService),
            debtService: pence(debtService),
            tax: Object.freeze(taxRows),
            totalTax: pence(totalTax),
            ...(studentLoanTotalForYear > 0
                ? { studentLoanRepayment: pence(studentLoanTotalForYear) }
                : {}),
            netCashFlow: pence(netAfterOverpayments),
            unmetShortfall: pence(unmetShortfall),
            cumulativeUnmetShortfall: pence(cumulativeShortfall),
            netWorthAfterShortfall: pence(invested - liabilitiesOutstanding - cumulativeShortfall),
            deflatorPpm,
            // Both ADDITIVE and omitted when they have nothing to say, so a plan
            // that predates them serialises as it always did.
            ...(Object.keys(balancesByAsset).length > 0
                ? { balancesByAsset: Object.freeze(balancesByAsset) }
                : {}),
            ...(Object.keys(balancesByLiability).length > 0
                ? { balancesByLiability: Object.freeze(balancesByLiability) }
                : {}),
            ...(input.waterfall !== undefined ? { sweepSteps: Object.freeze(sweepLines) } : {}),
            audit,
        }));
        // Stage 7: assemble the per-person calculation trace for this year from
        // the collectors — pure recording of numbers already computed above.
        const tracePeople = [];
        for (const p of orderedPeople) {
            const t = personTax.get(p.input.id);
            if (!t)
                continue;
            const a = accOf(p.input.id);
            const pid = p.input.id;
            const drawn = traceDrawn.get(pid) ?? zeroByWrapper();
            const growthRec = traceGrowthBy.get(pid) ?? zeroByWrapper();
            const feesRec = traceFeesBy.get(pid) ?? zeroByWrapper();
            const contribRec = traceContrib.get(pid) ?? zeroByWrapper();
            const opening = traceOpening.get(pid);
            const closing = personWrapperTotals(p);
            const drawTaxTotal = traceDrawTax.get(pid) ?? 0;
            const schedDrawn = traceSchedDrawn.get(pid) ?? 0;
            const unschedDrawn = drawn.dc_pension - schedDrawn;
            const withdrawals = [];
            for (const w of TRACE_WRAPPERS) {
                if (w === 'dc_pension' || drawn[w] <= 0)
                    continue;
                withdrawals.push(Object.freeze({
                    wrapper: w,
                    method: 'waterfall',
                    gross: pence(drawn[w]),
                    taxWithheld: pence(0),
                }));
            }
            if (schedDrawn > 0) {
                // Scheduled pension income is taxed through the bands at step 9.
                withdrawals.push(Object.freeze({
                    wrapper: 'dc_pension',
                    method: 'scheduled',
                    gross: pence(schedDrawn),
                    taxWithheld: pence(0),
                }));
            }
            if (unschedDrawn > 0) {
                withdrawals.push(Object.freeze({
                    wrapper: 'dc_pension',
                    method: 'unscheduled',
                    gross: pence(unschedDrawn),
                    taxWithheld: pence(drawTaxTotal),
                }));
            }
            const pots = TRACE_WRAPPERS.map((w) => Object.freeze({
                wrapper: w,
                opening: pence(opening[w]),
                contributions: pence(contribRec[w]),
                growthAndYield: pence(growthRec[w]),
                fees: pence(feesRec[w]),
                withdrawn: pence(drawn[w]),
                otherMovements: pence(closing[w] - opening[w] - contribRec[w] - growthRec[w] + feesRec[w] + drawn[w]),
                closing: pence(closing[w]),
            })).filter((line) => line.opening !== 0 ||
                line.closing !== 0 ||
                line.contributions !== 0 ||
                line.growthAndYield !== 0 ||
                line.withdrawn !== 0 ||
                line.otherMovements !== 0);
            const rIt = rules.incomeTax;
            const slicesOf = (sl, dividend) => [
                ['basic', sl.basic, dividend ? rIt.dividendBasicRateBps : rIt.basicRateBps],
                ['higher', sl.higher, dividend ? rIt.dividendHigherRateBps : rIt.higherRateBps],
                [
                    'additional',
                    sl.additional,
                    dividend ? rIt.dividendAdditionalRateBps : rIt.additionalRateBps,
                ],
            ]
                .filter(([, amount]) => amount > 0)
                .map(([band, amount, rateBps]) => Object.freeze({
                band,
                rateBps,
                amount: pence(amount),
                tax: pence(bpsOf(amount, rateBps)),
            }));
            tracePeople.push(Object.freeze({
                personId: pid,
                income: Object.freeze(traceIncome.get(pid) ?? []),
                taxableIncome: Object.freeze({
                    nonSavings: pence(a.nonSavings),
                    savings: pence(a.savings + a.cashInterestTaxable),
                    dividends: pence(a.dividends + a.giaYieldTaxable),
                }),
                incomeTax: Object.freeze({
                    personalAllowance: t.result.personalAllowance,
                    nonSavingsTax: t.result.nonSavingsTax,
                    savingsTax: t.result.savingsTax,
                    dividendTax: t.result.dividendTax,
                    bands: Object.freeze({
                        nonSavings: Object.freeze(slicesOf(t.result.bands.nonSavings, false)),
                        savings: Object.freeze({
                            atStartingRate: t.result.bands.savings.atStartingRate,
                            atPersonalSavingsAllowance: t.result.bands.savings.atPersonalSavingsAllowance,
                            slices: Object.freeze(slicesOf(t.result.bands.savings, false)),
                        }),
                        dividends: Object.freeze({
                            atDividendAllowance: t.result.bands.dividends.atDividendAllowance,
                            slices: Object.freeze(slicesOf(t.result.bands.dividends, true)),
                        }),
                    }),
                    marginalBand: t.result.marginalBand,
                    section24Reduction: pence(t.s24),
                    annualAllowanceCharge: pence(t.aaCharge),
                    unscheduledDrawTax: pence(a.drawTax),
                    total: pence(t.result.totalTax - t.s24 + t.aaCharge + a.drawTax),
                }),
                nationalInsurance: pence(t.ni),
                ...(t.studentLoanLines.length > 0
                    ? { studentLoan: Object.freeze(t.studentLoanLines) }
                    : {}),
                capitalGains: Object.freeze({
                    standardGains: pence(Math.max(0, a.realisedGains)),
                    residentialGains: pence(a.residentialGains),
                    businessGains: pence(a.badrGains),
                    tax: pence(t.cgt),
                }),
                withdrawals: Object.freeze(withdrawals),
                pots: Object.freeze(pots),
            }));
        }
        // Household-level lines for the non-pooled wrappers. Purchases, sales and
        // death re-ownership are the residual `otherMovements` by construction.
        const hhClosing = householdWrapperTotals();
        const householdPots = HH_TRACE_WRAPPERS.map((w) => Object.freeze({
            wrapper: w,
            opening: pence(hhOpening[w]),
            contributions: pence(hhContrib[w]),
            growthAndYield: pence(hhGrowth[w]),
            fees: pence(hhFees[w]),
            withdrawn: pence(0),
            otherMovements: pence(hhClosing[w] - hhOpening[w] - hhContrib[w] - hhGrowth[w] + hhFees[w]),
            closing: pence(hhClosing[w]),
        })).filter((line) => line.opening !== 0 ||
            line.closing !== 0 ||
            line.contributions !== 0 ||
            line.growthAndYield !== 0 ||
            line.otherMovements !== 0);
        trace.push(Object.freeze({
            year,
            taxYearLabel: taxYearLabel(year),
            people: Object.freeze(tracePeople),
            spendingLines: Object.freeze(spendingLines),
            lifeEventOutflows: Object.freeze(lifeEventOutflowLines),
            debtLines: Object.freeze(debtLines),
            householdPots: Object.freeze(householdPots),
        }));
        // Advance the cumulative price deflator for next year's row: the start
        // year is today's value by definition (deflatorPpm = 1,000,000), each
        // later row deflates by one more year of the CPI assumption.
        deflatorPpm = Math.trunc((deflatorPpm * (10_000 + cpiFor(year))) / 10_000);
    }
    // Provenance snapshot: echo the caller's if provided, else synthesise a
    // minimal `custom` one from the resolved assumptions so the field is never
    // empty. (`resolveAssumptions` is the front door that builds the rich one.)
    // ...then reconciled against the rates the projection ACTUALLY applied, so
    // the assumptions page can never publish a figure the engine did not use.
    // A per-asset override is legitimate (DECISIONS.md D-001); publishing the
    // wrapper preset while compounding the override is not.
    const assumptionsSnapshot = reconcileSnapshotToApplied(input.assumptionsSnapshot ?? synthesiseSnapshot(assumptions), appliedRatesByWrapper(input, { growth: (w) => growthOf(w, null), yield: yieldOf, fee: feeOf }));
    /**
     * IM-F5-01. Stated, because it is a number the adviser will be asked about:
     * a survivor's capital-gains bill visibly smaller than the gain on screen.
     */
    if (probateUplifted) {
        warnOnce('death-probate-uplift', {
            code: 'death-probate-uplift',
            message: `On the death modelled in this plan, the investments passing to the survivor are re-based to their value at that point. Gains built up before then are not taxed on a later sale — which is why the estimated capital-gains tax after that year is lower than the growth on screen suggests.`,
            year: null,
            itemLabel: null,
        });
    }
    /**
     * FG-F6-04. Which treatment was applied, named — never a silent clearance.
     */
    if (debtsSettledAtDeath.length > 0) {
        const named = [...new Set(debtsSettledAtDeath.map((d) => d.label))].join(', ');
        const paidTotal = debtsSettledAtDeath.reduce((n, d) => n + d.paid, 0);
        const unfunded = debtsSettledAtDeath.reduce((n, d) => n + d.unfunded, 0);
        const anyStillPaying = debtsSettledAtDeath.some((d) => d.unfunded > 0);
        /**
         * A2.5a review round 2 (adviser). The message asserted "held in the sole
         * name of the person who dies" on every row — including a JOINT debt the
         * adviser had explicitly marked as settled, which the engine honours. It
         * told the adviser the opposite of what they had recorded.
         */
        const anyJoint = debtsSettledAtDeath.some((d) => d.joint);
        const basis = anyJoint
            ? 'recorded as settled from the estate on the debt itself'
            : 'held in the sole name of the person who dies in this plan, so it is treated as settled from the estate';
        /**
         * A2.5a review round 3. Two sentences here were false in reachable shapes.
         * "takes no further payments": a PARTLY funded debt stays live and keeps
         * amortising — measured, a £200,000 loan at 6% left the survivor paying
         * £9,600 a year for five years, as unmet shortfall, under a note telling
         * the adviser it was settled. And where nothing at all could be paid, the
         * note still asserted that "£0 … leaves the plan", which is a movement of
         * money that did not happen; that case is now reported by the unfunded
         * note alone.
         */
        if (paidTotal > 0) {
            const outcome = anyStillPaying
                ? `${formatGbpWhole(pence(paidTotal))} is paid out of what the survivor inherits at that point — the money leaves the plan, it is not written off — and what the estate could not meet stays on the debt, which carries on being paid`
                : `${formatGbpWhole(pence(paidTotal))} is paid out of what the survivor inherits at that point — the money leaves the plan, it is not written off — and the debt takes no further payments`;
            warnOnce('death-debt-settled', {
                code: 'death-debt-settled',
                message: `${named}: ${basis}. ${outcome}. Record it as continuing on the debt itself if the survivor would remain liable.`,
                year: null,
                itemLabel: named,
            });
        }
        /**
         * A2.5a review round 2. Where the estate could not meet it, the balance
         * stays outstanding rather than vanishing, and it is SAID — an estate that
         * cannot pay its debts without selling the house is the single fact an
         * adviser most needs off this run.
         */
        if (unfunded > 0) {
            /**
             * A2.5a review round 3. The reason was hard-coded to "property and
             * business interests are never sold automatically", and was measured
             * firing on three plans holding NEITHER — a ring-fenced pot, a waterfall
             * naming no liquid step, a shielded account — telling the adviser to
             * record the sale of an asset that does not exist while liquid money sat
             * in the plan. The note now names the reason that actually applied.
             */
            const hasIlliquid = properties.some((pr) => pr.active && pr.value > 0) ||
                trackedHoldings.some((h) => h.value > 0);
            const why = hasIlliquid
                ? 'property and business interests are never sold automatically, so clearing it by selling something has to be recorded as a planned sale'
                : 'the estate holds no property or business interest to sell, so the money to clear it is not in this plan — check whether any pot is set never to be drawn from, and whether the withdrawal order reaches the ones that hold it';
            warnOnce('death-debt-unfunded', {
                code: 'death-debt-unfunded',
                message: `The estate could not meet ${formatGbpWhole(pence(unfunded))} of the debts recorded as settled at that death from the money it holds. That balance is still outstanding in this plan and carries on being paid: ${why}.`,
                year: null,
                itemLabel: named,
            });
        }
    }
    /**
     * FG-F3-04. The survivor's spending is the couple's until somebody says
     * otherwise, and saying nothing is not the same as saying it is unchanged.
     */
    /**
     * A2.5a review (adviser): gated on a death that actually HAPPENS in the
     * window, not on an assumed age at death being set. The modeller sets an
     * assumed death age as a matter of course, so the first cut fired on most
     * couple plans — including a couple of 45 and 44 with a ten-year horizon —
     * saying "this plan models a death" when none occurs. A guard that fires
     * everywhere is a guard an adviser learns to scroll past, and this one is
     * what the whole survivor gate leans on.
     */
    if (input.survivorSpendingFactorBps === undefined &&
        someoneHasDied &&
        input.people.length > 1 &&
        // A2.5a review: and only where there is a joint row for a factor to scale.
        allSpending.some((sp) => (sp.ownerId ?? 'joint') === 'joint')) {
        warnOnce('survivor-spending-unmodelled', {
            code: 'survivor-spending-unmodelled',
            message: `This plan models a death, and the household's joint spending carries on at the same level afterwards. One person does not usually spend what two did — record what the survivor's spending would be so the years after the death are not overstated.`,
            year: null,
            itemLabel: null,
        });
    }
    /**
     * A2.5a review round 2 (client). The guard was inverted: a BLANK factor
     * warned, and a factor the adviser typed — one that cut the survivor's
     * modelled spending by 44% for eighteen years — raised nothing at all. The
     * client read consecutive rows falling from £64,985 to £36,183 with no
     * assumption anywhere in the document to explain it. The loud case was the
     * silent one.
     */
    if (input.survivorSpendingFactorBps !== undefined && someoneHasDied && input.people.length > 1) {
        const pct = (input.survivorSpendingFactorBps / 100).toFixed(input.survivorSpendingFactorBps % 100 === 0 ? 0 : 1);
        warnOnce('survivor-spending-applied', {
            code: 'survivor-spending-applied',
            message: `From the year after the death modelled in this plan, the household's general living costs are taken at ${pct}% of what the couple spent together. That figure was recorded by the adviser — it is not calculated by this tool, and it is not a view on what one person needs. Costs attached to one person, and costs paid for somebody else, are not scaled by it.`,
            year: null,
            itemLabel: null,
        });
    }
    /**
     * B2.1 — the honest trust gate. RECORDED, and named, and stated as not
     * calculated.
     *
     * The wording of this message is provisional: **N-B3 is an open owner
     * decision** and only the settlor sentence and the section's name come from
     * the brief. Flagged rather than presented as settled.
     *
     * This is the one place the engine reads `trusts`, and it reads it only to
     * say the arrangement exists. Nothing below it, and no figure anywhere in the
     * projection, depends on the list — `trusts-output-neutral.test.ts` hashes
     * the whole result with and without it, because "not modelled" has to be true
     * rather than intended.
     */
    const trusts = input.trusts ?? [];
    if (trusts.length > 0) {
        const named = trusts
            .map((t) => `${t.label} (${TRUST_KIND_LABEL[t.kind]}, ${t.role})`)
            .join('; ');
        const one = trusts.length === 1;
        /**
         * B2.1 verification pass (tech-admin). The wording defects the client and
         * adviser reviewers found were fixed in the REPORT and left here — and
         * because the report now prints the box instead of this note, this is the
         * only arrangement text a client sees on Results and on Present. All three
         * survived on the surface that matters most for this gate.
         *
         * Fixed here, on the same conditions the box uses:
         *  - it no longer claims an absolute the tool cannot guarantee (an adviser
         *    can record a distribution as income today, and B2.3 plans to);
         *  - the periodic-charge clause appears only where such charges can arise —
         *    a bare trust bears none;
         *  - the settlor sentence appears only where somebody is recorded as a
         *    settlor. A client who is only a beneficiary was reading a sentence
         *    about another person's estate as being about theirs.
         */
        const anySettlor = trusts.some((t) => t.role === 'settlor');
        const anyPeriodic = trusts.some((t) => t.kind !== 'bare');
        const charges = anyPeriodic
            ? ' — including the charges that can fall due when a trust is set up, every ten years, and when money leaves it'
            : '';
        const settlor = anySettlor
            ? ' If the settlor retains a benefit the value may remain in the estate; this tool does not assess that.'
            : '';
        /**
         * GOAL 2026-09-10 item 12. A beneficiary of an interest in possession
         * trust can be treated as owning the underlying fund for inheritance tax.
         * The gate said the fund was left out; it did not say that leaving it out
         * can understate the beneficiary's own estate. Now it does.
         */
        const iipBeneficiary = trusts.some((t) => t.kind === 'interestInPossession' && t.role === 'beneficiary');
        const iip = iipBeneficiary
            ? ' Where the client is the beneficiary of an interest in possession trust, the trust fund can be treated as part of their estate for inheritance tax; this estimate does not include it, so the estate figure here may be lower than the one that would be assessed.'
            : '';
        warnOnce('arrangement-not-modelled', {
            code: 'arrangement-not-modelled',
            message: `This plan records ${one ? 'an arrangement' : `${trusts.length} arrangements`} and works out nothing for ${one ? 'it' : 'them'}: ${named}. The net worth, the year-by-year figures and the inheritance-tax estimate here are all worked out WITHOUT ${one ? 'it' : 'them'}, and any figure that relates to ${one ? 'it' : 'them'} — money received from ${one ? 'it' : 'them'}, for instance — is one the adviser entered rather than one this plan calculated. None of the tax that can apply is calculated${charges}, and no discount is applied.${settlor}${iip}`,
            year: null,
            itemLabel: null,
        });
        /**
         * GOAL 2026-09-10 item 12. There is no way to record a gift INTO a trust,
         * so a gift made on a plan that records a trust the client settled is
         * assessed as an outright gift to a person — a potentially exempt
         * transfer. A transfer into a discretionary trust is a chargeable lifetime
         * transfer, a different treatment the plan cannot represent. The gift is
         * not moved or reclassified; the difference is named.
         */
        const settled = trusts.filter((t) => t.role === 'settlor');
        const outrightGifts = (input.lifeEvents ?? []).filter((e) => e.isGift === true && e.direction === 'outflow' && e.amount > 0);
        if (settled.length > 0 && outrightGifts.length > 0) {
            const trustNames = settled.map((t) => t.label).join('; ');
            const giftNames = outrightGifts.map((g) => `${g.label} (${g.year})`).join('; ');
            warnOnce('gift-into-trust-not-modelled', {
                code: 'gift-into-trust-not-modelled',
                message: `This plan records a trust the client settled (${trustNames}) and also makes ${outrightGifts.length === 1 ? 'a gift' : `${outrightGifts.length} gifts`}: ${giftNames}. A gift into a trust cannot be recorded here, so every gift is assessed as an outright gift to a person — a potentially exempt transfer under the seven-year rule. A transfer into a trust the client settled can instead be a chargeable lifetime transfer, with tax possible at the time of the gift and a different treatment on death; the estimate does not distinguish the two, and it is not modelled.`,
                year: null,
                itemLabel: null,
            });
        }
    }
    /**
     * B2.3 — money coming OUT of a trust.
     *
     * Working the arithmetic through before writing the caveat changed what the
     * caveat should say, which is the habit B2.2 was parked for not having.
     *
     * A discretionary trust pays its beneficiary net of 45%, and the beneficiary
     * grosses it up, takes a 45% credit and reclaims the difference. Taxing the
     * GROSS at the recipient's own rates gives exactly the right answer at every
     * rate, because the credit offsets precisely the tax the trust paid:
     *
     *   basic      reality £8,000  |  gross entered £8,000  |  net entered £4,400
     *   higher     reality £6,000  |  gross entered £6,000  |  net entered £3,300
     *   additional reality £5,500  |  gross entered £5,500  |  net entered £3,025
     *
     * So this is not an approximation that is always a bit wrong. It is right if
     * the adviser enters the amount BEFORE the trust's tax, and materially wrong
     * if they enter what landed in the bank — and which of those they did is a
     * fact the plan cannot see. The note therefore tells them which to enter,
     * rather than telling them the figure is approximate and leaving them to
     * guess the direction.
     */
    /**
     * B2.3 review (both personas). The first cut filtered the INPUT, so it fired
     * for streams that pay nothing into the projection at all — a window ending
     * before it starts, a window beginning after it ends, an amount of zero — and
     * the client read a paragraph about the tax on money that appears nowhere in
     * their table. That is the B2.2 defect exactly, one item later, and it is why
     * this now asks whether the stream actually PAID.
     */
    const trustDistributions = input.incomeStreams.filter((s) => s.fromTrust === true && s.amount > 0 && years.some((y) => streamActive(s, y.year)));
    if (trustDistributions.length > 0) {
        const one = trustDistributions.length === 1;
        const named = [...new Set(trustDistributions.map((s) => s.label))].join('; ');
        /**
         * And the claim is made per CHARACTER, not universally. The gross-in-
         * gross-out identity — tax the gross at the recipient's own rates and land
         * exactly on the right answer — holds because the trust's credit offsets
         * precisely the tax it paid. That is true of a discretionary distribution,
         * which is non-savings income carrying a 45% credit.
         *
         * It is NOT true of an interest-in-possession trust, where the income keeps
         * its character: a dividend distribution recorded as non-savings is charged
         * 20% where 8.75% is due. Measured by the reviewer: £2,000 against £875 on
         * £10,000 at basic rate, understating the client even with a correct gross.
         * The plan taxes a `dividend` stream at dividend rates, so the fix is
         * guidance rather than arithmetic — record the character the trust
         * received — and the note now says that instead of asserting one rule.
         */
        const anyNonSavings = trustDistributions.some((s) => s.type !== 'dividend' && s.type !== 'savings_interest');
        /**
         * B2.3 verification pass. The divisor has to match the character the branch
         * has just concluded. The first cut printed "divided by 0.55" — the
         * discretionary gross-up — in the very branch where every flagged stream is
         * a dividend or savings, i.e. where it has decided the trust is NOT
         * discretionary. A trustee charge of 20% grossed up at 45% is simply a
         * different number.
         */
        const character = anyNonSavings
            ? ` A payment from a discretionary trust is non-savings income and carries a 45% credit, so recording it as "other" is right, and the gross is what arrived divided by 0.55. A payment from an interest-in-possession trust keeps the character of what the trust received — record dividends as dividends and interest as savings, or the rate applied here will be the wrong one.`
            : ` These are recorded with the character an interest-in-possession trust passes on. Where that is right, the gross is what arrived divided by 0.80 for interest and by 0.9125 for dividends. A discretionary trust is different: its distributions are non-savings income whatever the trust held, so if that is what this is, record it as "other" instead.`;
        warnOnce('trust-distribution-not-modelled', {
            code: 'trust-distribution-not-modelled',
            message: `${named}: ${one ? 'this is money paid out of a trust and is' : 'these are payments out of a trust and are'} taxed here at the recipient's own rates on the amount recorded. Where a UK trust has already taxed the money, the recipient is treated as having paid that tax and can reclaim any excess over their own rate — so the amount to record is the one BEFORE the trust's tax, and recording only what arrived understates what they have.${character} This plan does not model the trust's own tax, its tax pool, or any reclaim, and it does not model a non-UK trust, where no such credit arises.`,
            year: null,
            itemLabel: null,
        });
    }
    const estimatedIht = estimateIhtExposure(input, years, rules, warnOnce, sweepGifts, recurringGifts, deathEstates);
    return Object.freeze({
        engineVersion: ENGINE_VERSION,
        taxRulesVersion: rules.version,
        asOfDate: clock.asOfDate,
        years: Object.freeze(years),
        depletionYear,
        insolvency: depletionYear === null
            ? null
            : Object.freeze({
                insolventFromYear: depletionYear,
                cumulativeUnmetShortfall: pence(cumulativeShortfall),
                illiquidValueAtFirstShortfall: pence(illiquidAtFirstShortfall ?? 0),
            }),
        warnings: Object.freeze(warnings),
        validation: Object.freeze(planFindings),
        assumptionsSnapshot,
        estimatedIht,
        ...(marketShockWindows.length > 0 ? { marketShockWindows } : {}),
        trace: Object.freeze(trace),
    });
}
/**
 * Estimate the IHT exposure — an ILLUSTRATIVE figure, never advice. The estate
 * is assessed at the last modelled death (or the projection horizon when no
 * death is modelled). DC pensions are excluded from the estate by default.
 * Lifetime gift events within 7 years of the assessment year are treated as
 * failed PETs with taper relief.
 *
 * A1.3 (GOAL 2026-09-05): the same assessment is restated for every projection
 * year (`byYearOfDeath`) and per gift (`gifts`). Both are restatements of the
 * calls the headline already makes — one `inheritanceTax()` call per year on
 * the settings as applied at the headline — never an interpolation, and never
 * a ranking of years.
 */
/** B2.1 — how each recorded arrangement is named to the reader. */
const TRUST_KIND_LABEL = {
    bare: 'bare trust',
    discretionary: 'discretionary trust',
    interestInPossession: 'interest in possession trust',
    loanTrust: 'loan trust',
    discountedGiftTrust: 'discounted gift trust',
    other: 'arrangement',
};
function estimateIhtExposure(input, years, rules, warnOnce, 
/** Gifts made by `spend` goal steps out of surplus (client goals v2). */
sweepGifts = [], 
/** A2.7: one entry per active year of a gift-flagged recurring spending line. */
recurringGifts = [], 
/** A2.5: what each person owned at the moment they died, before it transferred. */
deathEstates = []) {
    if (years.length === 0)
        return null;
    const settings = input.iht ?? {};
    const deathYears = input.people
        .filter((p) => p.deathAge !== null)
        .map((p) => clockYearForDeath(p, years[0].year, years[0].clientAge, input));
    const lastYear = years[years.length - 1];
    const atHorizonEnd = deathYears.length === 0;
    const assessYear = atHorizonEnd
        ? lastYear.year
        : Math.min(Math.max(...deathYears), lastYear.year);
    // Estate inclusion of unused pension funds is a DATE-DRIVEN rule, read from
    // the versioned tax layer rather than hard-coded. A plan running to 2049
    // crosses 6 April 2027, so a death modelled before it is assessed on the old
    // treatment and one on or after it on the new — from the same rule set. An
    // explicit caller setting still wins, so an adviser can model a scheme the
    // rule does not describe.
    const inEstateFrom = rules.inheritanceTax.unusedPensionFundsInEstateFrom;
    const pensionsInEstateFromYear = inEstateFrom === undefined ? null : taxYearContaining(isoDate(inEstateFrom));
    // Business property relief on business interests: the estate's business
    // value is relieved at the input-weighted BPR rate (per-holding values at
    // the assessment year are not tracked in year rows — an estimate,
    // consistent with the figure's illustrative framing).
    let bprWeighted = 0;
    let businessTotal = 0;
    for (const a of input.assets) {
        if (a.wrapper !== 'business')
            continue;
        businessTotal += a.currentValue;
        bprWeighted += bpsOf(a.currentValue, a.bprRateBps ?? 0);
    }
    const effectiveBprBps = businessTotal > 0 ? Math.trunc((bprWeighted * 10_000) / businessTotal) : 0;
    // From 6 April 2026 the 100% relief is capped: qualifying property up to the
    // allowance is relieved in full, and the excess at 50%. Rule sets before that
    // carry no allowance, so they keep the uncapped behaviour untouched.
    const bprAllowance = rules.inheritanceTax.businessReliefAllowance;
    const bprExcessRateBps = rules.inheritanceTax.businessReliefExcessRateBps ?? 0;
    /**
     * GOAL 2026-09-16 IE-3. The residence value the band is measured against, in
     * ONE year, from the adviser's stated basis. A blank is never read as a
     * confirmed zero: `notEntered` is its own source, and says so.
     *
     * `recordedMainResidence` reads the plan's main residence at its PROJECTED
     * value that year, less the balance of the mortgage linked to it — the
     * value that actually passes — so a 2045 estimate never uses today's price.
     * A sold home reads £0 from the year it is sold. A home bought through a
     * planned purchase is not an input asset and is not read: the plan says so
     * through `mainResidenceNotRecorded`.
     */
    const residenceBasis = settings.residenceBasis ??
        (settings.residenceToDescendants !== undefined ? 'fixedAmount' : undefined);
    const mainResidence = input.assets.find((a) => a.wrapper === 'property' && a.property?.isPrimaryResidence === true);
    const residenceAt = (row) => {
        switch (residenceBasis) {
            case undefined:
                return { value: 0, source: 'notEntered', mainResidenceMissing: false };
            case 'doesNotApply':
                return { value: 0, source: 'doesNotApply', mainResidenceMissing: false };
            case 'fixedAmount':
                return {
                    value: settings.residenceToDescendants ?? 0,
                    source: 'fixedAmount',
                    mainResidenceMissing: false,
                };
            case 'recordedMainResidence': {
                if (mainResidence === undefined) {
                    return { value: 0, source: 'recordedMainResidence', mainResidenceMissing: true };
                }
                const gross = row.balancesByAsset?.[mainResidence.id] ?? 0;
                const linked = mainResidence.property?.linkedLiabilityId ?? null;
                const mortgage = linked === null ? 0 : (row.balancesByLiability?.[linked] ?? 0);
                return {
                    value: Math.max(0, gross - mortgage),
                    source: 'recordedMainResidence',
                    assetId: mainResidence.id,
                    mainResidenceMissing: false,
                };
            }
        }
    };
    const transferPercentagesRecorded = settings.transferredNilRateBandBps !== undefined ||
        settings.transferredResidenceNilRateBandBps !== undefined;
    // Every gift the plan makes, in plan order: gift-flagged outflow events,
    // then gifts a `spend` goal step actually made out of surplus (client goals
    // v2) — gifts like any other for the seven-year rule.
    const allGifts = [];
    for (const e of input.lifeEvents ?? []) {
        if (e.isGift !== true || e.direction !== 'outflow')
            continue;
        // A2.1: the marriage exemption applies only when the adviser has ASSERTED
        // the occasion. The giver's relation to the recipient sets the limit — a
        // gift to a child is from a parent, to a grandchild from a grandparent.
        const weddingGiftFrom = e.giftOccasion === 'wedding'
            ? e.giftRelationship === 'child'
                ? 'parent'
                : e.giftRelationship === 'grandchild' || e.giftRelationship === 'remoter-descendant'
                    ? // HMRC's £2,500 covers a grandparent "or remoter ancestor", so a
                        // great-grandchild's wedding draws the same limit, not £1,000.
                        'grandparent'
                    : 'other'
            : undefined;
        allGifts.push({
            year: e.year,
            value: e.amount,
            bprRateBps: e.bprRateBps ?? 0,
            label: e.label,
            ...(e.giftRecipient !== undefined ? { recipient: e.giftRecipient } : {}),
            ...(weddingGiftFrom !== undefined ? { weddingGiftFrom } : {}),
            ...(e.exemptionClaimed !== undefined ? { exemptionClaimed: e.exemptionClaimed } : {}),
        });
    }
    // A2.7: recurring gifts, already one entry per active year. They carry no
    // occasion — a marriage happens once, so a wedding gift is a one-off event —
    // and no business-property relief, which belongs to an asset being given.
    for (const g of recurringGifts) {
        allGifts.push({
            year: g.year,
            value: g.value,
            bprRateBps: 0,
            label: g.label,
            ...(g.recipient !== undefined ? { recipient: g.recipient } : {}),
            ...(g.exemptionClaimed !== undefined ? { exemptionClaimed: g.exemptionClaimed } : {}),
        });
    }
    for (const g of sweepGifts) {
        allGifts.push({ year: g.year, value: g.value, bprRateBps: 0, label: 'Gift out of surplus' });
    }
    // The spouse exemption needs a SURVIVING spouse. When the household models
    // a couple and both deaths fall inside the window, the estate is assessed
    // at the last survivor's death — the exemption cannot apply there; what the
    // first death leaves behind instead is the transferred nil-rate allowance.
    const modelledDeaths = input.people.filter((p) => p.deathAge !== null).length;
    const lastSurvivorAssessed = input.people.length === 2 &&
        modelledDeaths === 2 &&
        !atHorizonEnd &&
        Math.max(...deathYears) <= lastYear.year;
    /**
     * These stay OFF unless set. See DECISIONS.md D-002 and O-007 (closed under
     * D-009: the marital fact lives on the plan document and makes the reliefs
     * AVAILABLE; the adviser ticks to apply).
     *
     * The audited report charged a couple's whole £15.7m estate against a single
     * £325,000 band and printed £6,176,479 of tax, in a projection where the
     * engine had just moved every pot to the surviving partner. The reliefs that
     * would have prevented that are all supported here — they are simply never
     * set, because nothing upstream can set them.
     *
     * Defaulting them ON was tried and deliberately reverted: the spouse
     * exemption and the transferable nil-rate band depend on the couple being
     * MARRIED or civil partners, which this engine does not model. `people`
     * holds a "client" and a "partner", and a cohabiting partner qualifies for
     * neither relief. Assuming marriage would understate tax for unmarried
     * couples just as silently as the present default overstates it for married
     * ones — and it would do so for every plan already saved, on a fact nobody
     * stated. A wrong number in the safe-looking direction is still a wrong
     * number on a client's report.
     */
    const passesToSpouse = lastSurvivorAssessed ? false : (settings.passesToSpouse ?? false);
    // IE-3: the implicit full transfer at a second death stands only where the
    // adviser has not recorded the transfer as percentages instead.
    const hasFullTransferredAllowances = lastSurvivorAssessed
        ? (settings.hasFullTransferredAllowances ??
            (!transferPercentagesRecorded && settings.passesToSpouse === true))
        : (settings.hasFullTransferredAllowances ?? false);
    /**
     * ONE assessment as if death fell in `year` — exactly the arithmetic the
     * headline uses, so the by-year restatement can never disagree with it.
     */
    const assessAt = (year, 
    /**
     * A2.14 review: overrides `settings.priorYearExemptionUsed` for ONE
     * counterfactual assessment, so the engine can measure what its own
     * pre-plan assumption is worth instead of asserting a direction.
     */
    priorYearUsedOverride, 
    /**
     * A2.2 review: ignores every asserted normal-expenditure position for ONE
     * counterfactual assessment, so the engine can say what those assertions
     * are worth instead of leaving the biggest mover in the ledger silent.
     */
    ignoreNormalExpenditure = false, 
    /**
     * A2.5: the FIRST of two modelled deaths is a different assessment, not the
     * headline restated. The spouse exemption can apply there (the headline
     * forces it off, because at the last survivor's death there is no spouse to
     * pass to), and nothing has transferred to it yet.
     */
    spouseFlags) => {
        const row = years.find((y) => y.year === year) ?? lastYear;
        const pensionBalance = row.balancesByWrapper.dc_pension +
            row.balancesByWrapper.db_pension +
            row.balancesByWrapper.state_pension;
        const pensionsOutsideByRule = pensionsInEstateFromYear === null || year < pensionsInEstateFromYear;
        const pensionsOutside = settings.pensionsOutsideEstate ?? pensionsOutsideByRule;
        // Junior ISAs belong to the children, never to the estate.
        const jisaBalance = row.balancesByWrapper.jisa;
        const qualifyingValue = bpsOf(row.balancesByWrapper.business, effectiveBprBps);
        const businessRelief = bprAllowance === undefined
            ? qualifyingValue
            : Math.min(qualifyingValue, bprAllowance) +
                bpsOf(Math.max(0, qualifyingValue - bprAllowance), bprExcessRateBps);
        // IE-3: the same subtraction as before, taken in two steps so the estate
        // BEFORE relief — what the residence taper is tested on — is named.
        const pensionsExcluded = pensionsOutside ? pensionBalance : 0;
        const netEstate = row.totalNetWorth - pensionsExcluded - jisaBalance;
        const estateValue = Math.max(0, netEstate - businessRelief);
        const residence = residenceAt(row);
        const gifts = [];
        // Position in `allGifts` of each gift handed to the tax layer.
        const giftIndex = [];
        allGifts.forEach((g, i) => {
            const yearsBefore = year - g.year;
            /**
             * EVERY gift on or before this year is handed over — the tax layer
             * excludes anything at seven years or more from the CHARGE itself.
             *
             * A2.1 capped this at eight years, on the correct reasoning that a gift
             * older than that cannot reach the charge: only a year's OWN unused
             * allowance travels, so year 8's gifts change what year 8 passes to year
             * 7 and nothing below. But the readout is built from the same call, so
             * every older gift came back with no detail and printed "Exemption
             * applied £0" on the client's own gift table — ten such rows above an
             * identical £3,000 gift showing £3,000 (A2.7 review, all three
             * reviewers). Worse, the cap made the walk start at an excluded year 8
             * and grant it a full untouched allowance, so a gift at exactly seven
             * years could show up to £6,000 of exemption it never had.
             *
             * Passing them all costs one walk iteration per extra year and is
             * asserted to move no charged figure (`gift-window-equivalence` in
             * `gift-recurring.test.ts`).
             */
            if (yearsBefore < 0)
                return;
            gifts.push({
                value: pence(g.value),
                yearsBeforeDeath: yearsBefore,
                bprRateBps: g.bprRateBps,
                ...(g.weddingGiftFrom !== undefined ? { weddingGiftFrom: g.weddingGiftFrom } : {}),
                // The recipient names the WEDDING, so several gifts to one wedding
                // share a single limit (per donor per marriage, not per gift).
                ...(g.recipient !== undefined ? { weddingFor: g.recipient } : {}),
                ...(g.exemptionClaimed !== undefined &&
                    !(ignoreNormalExpenditure && g.exemptionClaimed === 'normalExpenditureOutOfIncome')
                    ? { exemptionClaimed: g.exemptionClaimed }
                    : {}),
            });
            giftIndex.push(i);
        });
        const fullTransfer = spouseFlags?.hasFullTransferredAllowances ?? hasFullTransferredAllowances;
        const result = inheritanceTax(rules, {
            estateValue: pence(estateValue),
            // IE-1: tested after debts, before reliefs (GOV.UK RNRB guidance; s.8D).
            estateValueForTaper: pence(Math.max(0, netEstate)),
            residenceToDescendants: pence(residence.value),
            passesToSpouse: spouseFlags?.passesToSpouse ?? passesToSpouse,
            hasFullTransferredAllowances: fullTransfer,
            ...(spouseFlags === undefined && !fullTransfer
                ? {
                    ...(settings.transferredNilRateBandBps !== undefined
                        ? { transferredNilRateBandBps: settings.transferredNilRateBandBps }
                        : {}),
                    ...(settings.transferredResidenceNilRateBandBps !== undefined
                        ? { transferredResidenceNilRateBandBps: settings.transferredResidenceNilRateBandBps }
                        : {}),
                }
                : {}),
            charityRateApplies: settings.charityRateApplies ?? false,
            lifetimeGifts: gifts,
            ...(priorYearUsedOverride !== undefined
                ? { priorYearExemptionUsed: pence(priorYearUsedOverride) }
                : settings.priorYearExemptionUsed !== undefined
                    ? { priorYearExemptionUsed: settings.priorYearExemptionUsed }
                    : {}),
        });
        return {
            row,
            pensionsOutside,
            pensionsExcluded,
            jisaBalance,
            netEstate,
            qualifyingValue,
            businessRelief,
            estateValue,
            residence,
            spouseApplied: spouseFlags?.passesToSpouse ?? passesToSpouse,
            gifts,
            giftIndex,
            result,
        };
    };
    const head = assessAt(assessYear);
    /**
     * A2.14, corrected at its own review. The exemption walk starts one tax year
     * above the earliest gift it is GIVEN, and starts there with a full unused
     * allowance — so an allowance can carry into the plan out of a year the plan
     * holds no record of, in the client's favour. The pension analogue has said
     * so since stage 3 (`aa-carry-forward-pre-plan-nil`); this said nothing.
     *
     * The first cut of this warning was wrong twice, and all three reviewers
     * found it:
     *
     *  - It named `min(allGifts.year) - 1`, the year before the earliest gift in
     *    the PLAN. The walk only ever saw gifts inside the charge window, so on
     *    a long gifting arrangement the note named a year decades away from the
     *    one the engine was actually assuming about. (Widening the window above
     *    makes the two coincide again; deriving the year from the assessed set
     *    is what keeps them coinciding.)
     *  - It asserted a direction — "can only reduce the estimated inheritance
     *    tax" — on every plan with a gift, including plans where the carried
     *    allowance is never consumed and the figure does not move at all. A
     *    caveat about a number it does not change is worse than no caveat.
     *
     * So the engine now MEASURES its own assumption: it re-runs the headline
     * assessment with that year fully used and warns only if the answer differs,
     * stating what the assumption is worth in pounds. Where the adviser has
     * stated the year (A2.8) there is no assumption left to declare.
     */
    if (settings.priorYearExemptionUsed === undefined && head.gifts.length > 0) {
        const annualExemption = rules.inheritanceTax.annualGiftExemption ?? 0;
        // The year the walk actually takes on trust: one above the earliest gift
        // it was handed. Same source as `firstYear` inside the calculator.
        /**
         * A2.2 review: the same filter the calculator's own walk uses. A gift
         * asserted as normal expenditure spends no allowance, so it cannot be the
         * gift the blind year sits above — naming it here would print a year that
         * is not the one being assumed about, which is precisely the defect A2.1's
         * review had to fix on this warning once already.
         */
        const spendingGifts = head.gifts.filter((g) => g.exemptionClaimed !== 'normalExpenditureOutOfIncome');
        const assumedYear = spendingGifts.length === 0
            ? null
            : assessYear - (Math.max(...spendingGifts.map((g) => g.yearsBeforeDeath)) + 1);
        const withoutGrant = assessAt(assessYear, annualExemption);
        const worth = withoutGrant.result.taxDue - head.result.taxDue;
        // A plan whose every gift is asserted has no allowance walk to assume
        // anything about, so there is nothing to declare.
        if (worth !== 0 && assumedYear !== null) {
            warnOnce('gift-exemption-pre-plan-unused', {
                code: 'gift-exemption-pre-plan-unused',
                // Written to be read by the CLIENT as well as the adviser: it prints in
                // the report's assumptions block, so it states which year, what the
                // assumption is worth, and that nothing in the plan evidences it. "Go
                // and record it" is left to the builder note.
                message: `No gifts are recorded for ${taxYearLabel(assumedYear)}, so that year's annual gift exemption is assumed unused and carried forward into ${taxYearLabel(assumedYear + 1)}. That assumption lowers the estimated inheritance tax by about £${Math.round(worth / 100).toLocaleString('en-GB')}, and nothing in this plan records what that year was used for.`,
                year: assumedYear,
                itemLabel: null,
            });
        }
    }
    /**
     * A2.4 (FG-F4-02). The gap register's own words: "if I enter their
     * grandchild's fees as school fees on the grandparent's plan, the model just
     * spends the money. It never appears in the seven-year gift set, so the
     * estate figure I put in front of them is understated for exactly the
     * payments they came to ask about."
     *
     * Shipping the routing does not make the default safe: `notAssessed` is still
     * the default, and it is still the wrong answer for half of fee-paying cases.
     * So a plan that has education fees AND an inheritance-tax estimate says the
     * fees were not weighed against the clock, rather than staying silent about
     * it — which is what the register asked for as the interim measure and is no
     * less true now the treatment exists.
     */
    const educationPlans = (input.plannedEvents ?? []).filter((e) => e.kind === 'schoolFees' || e.kind === 'university');
    // Read as typed, not through a cast. A2.4 review (adviser): the casts here
    // suppressed exactly the compile error that would have caught the wire mapper
    // dropping these fields — the whole item was inert on every real path and the
    // type-checker had been told not to mind.
    const unassessedFees = educationPlans.filter((e) => e.paidBy !== 'thirdParty' && (e.ihtTreatment ?? 'notAssessed') === 'notAssessed');
    /**
     * A2.4 review (adviser): gated on there actually being tax at stake, and it
     * NAMES the plans. Ungated it rode permanently on a young family's plan with
     * no inheritance-tax question at all — which is the shape of note an adviser
     * learns to scroll past, and scrolling past it hides the one grandparent case
     * the note exists for. `year: null` too: it was stamped with the assessment
     * year, so the interface printed the death year beside a note about fees paid
     * a decade earlier.
     */
    if (unassessedFees.length > 0 && head.result.taxDue > 0) {
        const named = unassessedFees.map((e) => e.label).join(', ');
        warnOnce('education-fees-not-assessed-as-gift', {
            code: 'education-fees-not-assessed-as-gift',
            // Adviser-facing, and written to the adviser: the first cut addressed
            // "your adviser" to the person who IS the adviser.
            message: `${named}: these costs are modelled as spending and are not weighed against the seven-year gift rules. Where the fees are for someone outside this household, record the treatment on the plan — this projection does not decide it.`,
            year: null,
            itemLabel: named,
        });
    }
    const thirdPartyFees = educationPlans.filter((e) => e.paidBy === 'thirdParty');
    if (thirdPartyFees.length > 0) {
        const named = thirdPartyFees.map((e) => e.label).join(', ');
        warnOnce('education-fees-third-party', {
            code: 'education-fees-third-party',
            message: `${named}: recorded as paid by someone else, so these costs are not included in this plan's spending and are not counted as gifts made by this household.`,
            year: null,
            itemLabel: named,
        });
    }
    /**
     * A2.2 review (adviser AND client, independently). The single most
     * tax-removing thing an adviser can record was also the quietest: business
     * relief raises a warning, the missing residence band raises a warning, and
     * an assertion that can take an unlimited stream of gifts out of the estimate
     * raised nothing at all — so it never reached "What these figures assume",
     * which is where a client is told what the figures rest on.
     *
     * It is stated the way A2.14 states its own assumption: by MEASURING it. The
     * headline assessment is re-run ignoring the assertions, and the difference
     * is what the position is worth. That answers the question a client actually
     * has — how much of my family's money turns on this — which a sentence
     * repeated in a table cell does not.
     */
    const assertedGifts = allGifts.filter((g) => g.exemptionClaimed === 'normalExpenditureOutOfIncome' && g.year <= assessYear);
    if (assertedGifts.length > 0) {
        const assertedTotal = assertedGifts.reduce((n, g) => n + g.value, 0);
        const withoutAssertions = assessAt(assessYear, undefined, true);
        const worth = withoutAssertions.result.taxDue - head.result.taxDue;
        const plural = assertedGifts.length === 1 ? 'gift' : 'gifts';
        warnOnce('gift-normal-expenditure-asserted', {
            code: 'gift-normal-expenditure-asserted',
            message: `${assertedGifts.length} ${plural} totalling £${Math.round(assertedTotal / 100).toLocaleString('en-GB')} are treated as exempt on the adviser's stated position that they were normal expenditure out of income. This tool does not assess that position — HMRC decides it on the evidence. ${worth > 0
                ? `Without it the estimated inheritance tax would be about £${Math.round(worth / 100).toLocaleString('en-GB')} higher.`
                : 'On these figures it changes the estimated inheritance tax by nothing.'}`,
            year: assessYear,
            itemLabel: null,
        });
    }
    /**
     * A2.8 review (adviser): a figure above the year's own allowance is clamped
     * inside the calculator, and the card accepts any digits — so an adviser who
     * reads the label as "how much was gifted that year" types £10,000, sees
     * £10,000 persist, and the model uses £3,000. Silently using a different
     * number from the one on screen is not a thing this tool does.
     */
    if (settings.priorYearExemptionUsed !== undefined &&
        settings.priorYearExemptionUsed > (rules.inheritanceTax.annualGiftExemption ?? 0)) {
        warnOnce('gift-exemption-prior-year-clamped', {
            code: 'gift-exemption-prior-year-clamped',
            message: `The annual gift exemption already used in the tax year before this plan's earliest gift was recorded as £${Math.round(settings.priorYearExemptionUsed / 100).toLocaleString('en-GB')}, which is more than that year's whole exemption. The estimate uses the whole exemption; a year cannot use more than it has.`,
            year: null,
            itemLabel: null,
        });
    }
    const { businessRelief, qualifyingValue, estateValue, gifts, result } = head;
    /**
     * GOAL 2026-09-10 item 12. A couple with ONE death modelled: the headline
     * is assessed at that death on everything the HOUSEHOLD holds at the end of
     * the year — the survivor's own money included — because the by-year strip,
     * the composition table and the headline all read the same year row. The
     * figure is pinned as the behaviour; what was missing was any statement of
     * whose money it is. The deceased's own share is the estate captured at the
     * death step (sole holdings plus half of anything joint), before it passed.
     */
    if (input.people.length === 2 && modelledDeaths === 1 && !atHorizonEnd) {
        const own = deathEstates.find((d) => d.year === assessYear);
        if (own !== undefined) {
            const deceased = own.personId === 'client' ? 'the client' : 'the partner';
            const survivor = own.personId === 'client' ? 'the partner' : 'the client';
            // Treated exactly as the headline treats the household: pensions in or
            // out by the same rule, business relief on the deceased's OWN qualifying
            // share at the same cap, Junior ISAs never gathered — so the two shares
            // add back to the estate figure to the pound.
            const ownBusinessRelief = bprAllowance === undefined
                ? own.businessQualifying
                : Math.min(own.businessQualifying, bprAllowance) +
                    bpsOf(Math.max(0, own.businessQualifying - bprAllowance), bprExcessRateBps);
            const ownValue = Math.max(0, own.liquid +
                own.property +
                own.business +
                own.other +
                (head.pensionsOutside ? 0 : own.pension) -
                own.debts -
                ownBusinessRelief);
            const survivorValue = Math.max(0, estateValue - Math.min(ownValue, estateValue));
            warnOnce('iht-death-assessed-on-household', {
                code: 'iht-death-assessed-on-household',
                message: `The inheritance-tax estimate for ${assessYear} is made on everything the household holds at the end of that year — ${formatGbpWhole(pence(estateValue))} — not only on what ${deceased} owned. Of that, ${formatGbpWhole(pence(ownValue))} was ${deceased}'s own (sole holdings plus half of anything joint) and ${formatGbpWhole(pence(survivorValue))} belongs to ${survivor}, who is still alive in this plan. No will is recorded here and nothing says what passes to whom, so read this as the household's exposure at that point rather than as ${deceased}'s estate.`,
                year: assessYear,
                itemLabel: null,
            });
        }
    }
    /**
     * GOAL 2026-09-10 item 12. A business interest with NO relief position
     * recorded is counted at its full value. A recorded 0% is the adviser's
     * stated position and raises nothing; an absent field means the question
     * was never asked, and where tax is at stake that is said.
     */
    if (result.taxDue > 0) {
        for (const a of input.assets) {
            if (a.wrapper !== 'business' || a.bprRateBps !== undefined || a.currentValue <= 0)
                continue;
            warnOnce(`iht-bpr-unrecorded:${a.id}`, {
                code: 'iht-business-relief-not-recorded',
                message: `${a.label}: no business-relief position is recorded for this holding, so it is counted at its full value in the inheritance-tax estimate. Whether it would qualify for relief has not been recorded — the estimate neither assumes it does nor establishes that it does not.`,
                year: assessYear,
                itemLabel: a.label,
            });
        }
    }
    if (businessRelief > 0) {
        warnOnce('iht-estate-bpr', {
            code: 'iht-out-of-scope-items',
            message: 'The Inheritance Tax estimate applies business relief to the business interests held in this plan, at the rate recorded for each. Whether they qualify for that relief is a position recorded by your adviser, not something this projection establishes.',
            year: assessYear,
            itemLabel: null,
        });
    }
    if (bprAllowance !== undefined && qualifyingValue > bprAllowance) {
        warnOnce('iht-estate-bpr-capped', {
            code: 'iht-out-of-scope-items',
            message: 'From 6 April 2026 business relief is capped. Qualifying business and agricultural assets above the combined allowance are relieved at half their value rather than in full, and this estimate applies that cap.',
            year: assessYear,
            itemLabel: null,
        });
    }
    if (lastSurvivorAssessed && settings.passesToSpouse === true) {
        warnOnce('iht-last-survivor', {
            code: 'iht-last-survivor',
            message: 'Because the plan models both partners passing away within the projected years, the Inheritance Tax estimate is made at the second death. At that point the estate can no longer pass tax-free to a surviving spouse or civil partner; the estimate instead applies the tax-free allowance carried over from the first death. Any lifetime gifts are counted against this final estate — a simplification where the first person to die had made gifts.',
            year: assessYear,
            itemLabel: null,
        });
    }
    // An "estimated IHT: £0" produced BY the spouse exemption hides a deferred
    // liability: the survivor's later death may still be taxed, just outside
    // (or later than) the years modelled. Say so — never a silent £0.
    if (passesToSpouse && result.taxDue === 0 && estateValue > 0) {
        warnOnce('iht-spouse-deferred', {
            code: 'iht-spouse-deferred',
            message: 'The Inheritance Tax estimate is £0 because the estate is modelled as passing tax-free to a surviving spouse or civil partner. Tax may still be due when the survivor later dies, which falls outside the years modelled here.',
            year: assessYear,
            itemLabel: null,
        });
    }
    if (gifts.some((g) => (g.bprRateBps ?? 0) > 0)) {
        warnOnce('iht-bpr', {
            code: 'iht-out-of-scope-items',
            message: 'The Inheritance Tax estimate applies business relief to the GIFTS of business interests in this plan, at the rate recorded for each. Whether they qualify for that relief is a position recorded by your adviser, not something this projection establishes.',
            year: assessYear,
            itemLabel: null,
        });
    }
    // The residence nil-rate band only applies when the adviser sets a residence
    // value passing to descendants. Below the £2m taper it can be worth up to
    // £175k (£350k transferred); omitting it OVERSTATES the estimated IHT, so flag it.
    // IE-3: a STATED "does not apply" is an answer, not a gap, so it raises
    // nothing; a blank, or a fixed amount of nil, still does.
    if ((head.residence.source === 'notEntered' ||
        (head.residence.source === 'fixedAmount' && head.residence.value === 0)) &&
        estateValue > rules.inheritanceTax.nilRateBand &&
        estateValue < rules.inheritanceTax.residenceNilRateBandTaperThreshold &&
        result.taxDue > 0) {
        warnOnce('iht-no-rnrb', {
            // Its own code since W1.2: this is not an out-of-scope ITEM, and it fires
            // on ANY estate above the nil-rate band with no residence value set —
            // nothing to do with a business. Sharing a code with the business-relief
            // assumption meant printing one printed the other, so a plan with no
            // business at all would have carried this paragraph into the client's
            // PDF. Adviser-facing only until the owner rules on the wording (O-010).
            code: 'iht-no-residence-band',
            message: 'No residence nil-rate band is applied because no residence value passing to direct descendants is set. Where a home passes to children or grandchildren the band is worth up to £175,000 (£350,000 with a transferred allowance), before the £2m taper.',
            year: assessYear,
            itemLabel: null,
        });
    }
    /**
     * IE-3. The adviser stated that the main residence passes to direct
     * descendants, and the plan records no main residence to read — so no
     * residence band is applied, and a blank-looking £0 must not pass for an
     * answer.
     */
    if (head.residence.mainResidenceMissing) {
        warnOnce('iht-main-residence-not-recorded', {
            code: 'iht-main-residence-not-recorded',
            message: 'The inheritance-tax settings say the main residence passes to direct descendants, but no property in this plan is marked as the main residence, so no residence nil-rate band is applied. Mark the home as the main residence under Assets, or record the residence value as a fixed amount.',
            year: null,
            itemLabel: null,
        });
    }
    // A1.3 restatements — one call per projection year, and the headline's
    // per-gift treatment mapped back onto every gift the plan makes by then.
    const byYearOfDeath = years.map((y) => {
        const at = y.year === assessYear ? head : assessAt(y.year);
        return {
            year: y.year,
            estateValue: pence(at.estateValue),
            pensionsInEstate: !at.pensionsOutside,
            estimatedIht: at.result.taxDue,
        };
    });
    /**
     * A2.3 — what the assessed estate is made of, restated from the arithmetic
     * `assessAt` already did rather than recomputed. The client reviewer raised
     * the same gap at A2.1, A2.7 and A2.10: one estate figure and no way to see
     * what is in it, what was left out, or why.
     *
     * The identity that must hold, and that `iht-estate-composition.test.ts`
     * pins: counted lines − business relief − debts = `estateValue`. It is the
     * same expression `assessAt` evaluates, which is why the two cannot drift.
     *
     * Only wrappers the plan actually holds appear: a "£0" row for each of
     * thirteen wrappers is noise, not composition.
     */
    /**
     * A2.3's composition, as a function of ONE assessment so IE-3 can restate it
     * for every year from the arithmetic `assessAt` already did. The headline's
     * `composition` below is this function at `assessYear`, unchanged.
     */
    const compositionAt = (at) => Object.entries(at.row.balancesByWrapper)
        .filter(([, value]) => value !== 0)
        .map(([wrapper, value]) => {
        const isPension = wrapper === 'dc_pension' || wrapper === 'db_pension' || wrapper === 'state_pension';
        // Junior ISAs belong to the child, never to the estate; pensions follow
        // the rule year that actually applied at this assessment, not static copy.
        const excludedBecause = wrapper === 'jisa'
            ? 'childrensJisa'
            : isPension && at.pensionsOutside
                ? // Attributed honestly: the rule-year wording is a statement about
                    // the law, and printing it when the adviser's own override drove
                    // the exclusion put a false one on a client's document.
                    settings.pensionsOutsideEstate !== undefined
                        ? 'pensionsOutsideEstateBySetting'
                        : 'pensionsOutsideEstateByRule'
                : undefined;
        return {
            wrapper,
            value: pence(value),
            inEstate: excludedBecause === undefined,
            ...(excludedBecause !== undefined ? { excludedBecause } : {}),
        };
    });
    const assessRow = head.row;
    const compositionLines = compositionAt(head);
    const countedTotal = compositionLines.filter((l) => l.inEstate).reduce((n, l) => n + l.value, 0);
    const composition = {
        lines: Object.freeze(compositionLines),
        // The business stays in the estate at its full value and the relief is
        // shown coming off, because a smaller business would misdescribe what the
        // client owns.
        businessRelief: pence(head.businessRelief),
        liabilities: pence(assessRow.liabilitiesOutstanding),
        // Whatever the zero floor added, so the table can never disagree with the
        // headline it explains — see the field's own note.
        flooringAdjustment: pence(head.estateValue - (countedTotal - head.businessRelief - assessRow.liabilitiesOutstanding)),
    };
    /**
     * A2.3 review (tech-admin). The identity is the whole reason this readout is
     * worth printing, and the commit that introduced it claimed it "holds by
     * construction" while `Math.max(0, …)` on the estate quietly broke it — a
     * client PDF showed £0 above a table reading −£255,000. Asserting it here, in
     * the style of the engine's other arithmetic guards, means a future term
     * cannot reopen it silently: it fails loudly at the point of construction
     * rather than on someone's report.
     */
    const reconciles = countedTotal -
        composition.businessRelief -
        composition.liabilities +
        composition.flooringAdjustment;
    if (reconciles !== head.estateValue) {
        throw new RangeError(`Estate composition does not reconcile at ${assessYear}: ${reconciles} vs ${head.estateValue}`);
    }
    /** A1.3's gift readout for ONE assessment, so IE-3 can restate it for every year. */
    const giftReadoutsAt = (at, atYear) => {
        const readouts = [];
        allGifts.forEach((g, i) => {
            if (g.year > atYear)
                return;
            const yearsSurvived = atYear - g.year;
            const outsideWindow = yearsSurvived >= 7;
            const k = at.giftIndex.indexOf(i);
            const detail = k >= 0 ? at.result.giftDetails.find((d) => d.inputIndex === k) : undefined;
            readouts.push({
                year: g.year,
                label: g.label,
                value: pence(g.value),
                exemptionApplied: detail?.exemptionUsed ?? pence(0),
                weddingExemption: detail?.weddingExemption ?? pence(0),
                ...(g.recipient !== undefined ? { recipient: g.recipient } : {}),
                yearsSurvivedAtAssessment: yearsSurvived,
                normalExpenditureAsserted: g.exemptionClaimed === 'normalExpenditureOutOfIncome',
                taperBps: outsideWindow ? 0 : (detail?.taperBps ?? 0),
                nilRateBandUsed: detail?.coveredByNrb ?? pence(0),
                outsideWindow,
                taxAtAssessment: detail?.tax ?? pence(0),
            });
        });
        return readouts;
    };
    const giftReadouts = giftReadoutsAt(head, assessYear);
    /**
     * A2.5 — the FIRST death, assessed in its own right.
     *
     * Only where the plan actually models two deaths inside the horizon; there is
     * otherwise no first death to assess and the field is null rather than a
     * restatement of the headline under another name.
     *
     * Two things differ from the headline. The spouse exemption CAN apply here
     * (the headline forces it off, because at the last survivor's death there is
     * no spouse to pass to), and nothing has transferred to this estate yet — so
     * a plan asserting transferred allowances does not get them twice.
     */
    /**
     * A2.5 review (tech-admin). This required BOTH deaths inside the horizon,
     * because it reused `lastSurvivorAssessed`. So a couple whose second death
     * falls beyond the projected years — the common shape, and exactly the plan
     * where "what if he goes first" is the whole question — got no first-death
     * assessment at all, even though that death is fully inside the projection.
     *
     * The condition it actually needs is its own: two people, both with a death
     * modelled, and the earlier of the two falling inside the window and before
     * whatever year the headline assesses. Two deaths in the SAME year leave
     * nothing to assess separately and are documented as such.
     */
    const earliestDeath = deathYears.length > 0 ? Math.min(...deathYears) : null;
    const firstDeathYear = input.people.length === 2 &&
        modelledDeaths === 2 &&
        earliestDeath !== null &&
        earliestDeath <= lastYear.year
        ? earliestDeath
        : null;
    let firstDeath = null;
    const firstEstate = deathEstates.find((d) => d.year === firstDeathYear);
    if (firstDeathYear !== null && firstDeathYear < assessYear && firstEstate !== undefined) {
        const firstPassesToSpouse = settings.passesToSpouse ?? false;
        /**
         * The DECEASED's estate, not the household's — see the capture in the death
         * step. The reliefs are then applied exactly as the headline applies them:
         * pensions in or out by the date-driven rule, business relief at the plan's
         * own rate, and the same zero floor.
         */
        const pensionsOutsideByRuleAtFirst = pensionsInEstateFromYear === null || firstDeathYear < pensionsInEstateFromYear;
        const firstPensionsOutside = settings.pensionsOutsideEstate ?? pensionsOutsideByRuleAtFirst;
        // The deceased's OWN qualifying value, weighted on their own holdings.
        const firstQualifying = firstEstate.businessQualifying;
        const firstBusinessRelief = bprAllowance === undefined
            ? firstQualifying
            : Math.min(firstQualifying, bprAllowance) +
                bpsOf(Math.max(0, firstQualifying - bprAllowance), bprExcessRateBps);
        // IE review (tech-admin): the estate BEFORE relief is named, not rebuilt
        // from the floored figure, so a floored estate cannot misstate the taper base.
        const firstNetEstate = firstEstate.liquid +
            firstEstate.pension +
            firstEstate.property +
            firstEstate.business +
            firstEstate.other -
            firstEstate.debts -
            (firstPensionsOutside ? firstEstate.pension : 0);
        const firstEstateValue = Math.max(0, firstNetEstate - firstBusinessRelief);
        /**
         * A2.5 review (adviser + tech-admin). Lifetime gifts are NOT charged here.
         *
         * Every gift in this plan is the household's — no gift carries a donor — so
         * charging them at the first death attributes all of them to whichever
         * person died first, which is a fact nobody recorded. The first cut did
         * exactly that and printed £67,600 of failed-gift tax at a first death
         * directly beneath a sentence saying nothing is charged there, while the
         * SAME gifts were charged again against the final estate: two bills on one
         * pot, £1.29m of illustrated tax on a £1.5m estate in the reviewer's probe.
         *
         * Gifts stay where the plan has always assessed them — against the final
         * estate — and this assessment says so rather than double-counting them.
         */
        /**
         * A2.5 review round 2 (tech-admin). `residenceToDescendants` is a
         * PLAN-level figure and was handed whole to a personal estate, so a
         * deceased who owned nothing was reported with £500,000 of allowances and
         * 0% of the residence band passing on. Nobody can leave more residence than
         * they owned: capped at the deceased's own share of the plan's property.
         */
        const firstRow = years.find((y) => y.year === firstDeathYear) ?? lastYear;
        const firstResidence = Math.min(residenceAt(firstRow).value, firstEstate.property);
        const first = inheritanceTax(rules, {
            estateValue: pence(firstEstateValue),
            // IE-1: the taper is tested on the deceased's estate before business relief.
            estateValueForTaper: pence(Math.max(0, firstNetEstate)),
            residenceToDescendants: pence(firstResidence),
            passesToSpouse: firstPassesToSpouse,
            hasFullTransferredAllowances: false,
            charityRateApplies: settings.charityRateApplies ?? false,
            lifetimeGifts: [],
        });
        /**
         * What this death leaves unused, as a PROPORTION of its own bands (IHTA
         * 1984 s.8A; s.8G for the residence band) — because what transfers is
         * applied to the bands in force at the second death, not an amount frozen
         * at the first death's rates.
         */
        const bandTotal = first.nilRateBandTotal;
        /**
         * The estate consumes the band where it is not passing to a spouse. The
         * residence band is applied first, since it can shelter nothing else.
         */
        const nrbUsedByEstate = firstPassesToSpouse
            ? 0
            : Math.max(0, Math.min(bandTotal, firstEstateValue - first.residenceNilRateBandTotal));
        /**
         * Truncated on the UNUSED share, not derived by subtracting a truncated
         * used share: truncating what was used rounds what transfers UP, which
         * grants the second estate a fraction of a band it did not inherit.
         * £200,000 of a £325,000 band gives 3,847bps that way and 3,846 this way,
         * and only one of those errs against the taxpayer's favour.
         */
        const nrbUnusedBps = bandTotal <= 0
            ? 0
            : Math.min(10_000, Math.trunc(((bandTotal - nrbUsedByEstate) * 10_000) / bandTotal));
        /**
         * A2.5 review (adviser + tech-admin, independently). This read
         * `chargeableEstate > 0 ? 0 : 10_000` — whether the ESTATE was charged,
         * which is a different question from whether the RESIDENCE BAND was used,
         * and wrong in both directions. Measured: a £400,000 estate with £175,000
         * to descendants spent the whole residence band and was reported as
         * transferring 100% of it. What transfers is the share of the band this
         * death did not apply.
         */
        const rnrbFull = rules.inheritanceTax.residenceNilRateBand;
        const rnrbApplied = firstPassesToSpouse ? 0 : first.residenceNilRateBandTotal;
        const rnrbUnusedBps = rnrbFull <= 0
            ? 0
            : Math.min(10_000, Math.trunc((Math.max(0, rnrbFull - rnrbApplied) * 10_000) / rnrbFull));
        /**
         * The proportions above describe THIS estate. What they cannot describe is
         * a band the first person spent on gifts the plan records for the household
         * rather than for them — so where the plan gifts before this death, the
         * derived share is not handed to the second assessment and the adviser's
         * own recorded position governs, unchanged.
         */
        const giftsBeforeFirstDeath = allGifts.some((g) => g.year <= firstDeathYear);
        firstDeath = {
            year: firstDeathYear,
            personId: firstEstate.personId,
            survivorId: input.people.find((pp) => pp.id !== firstEstate.personId)?.id ?? null,
            estateValue: pence(firstEstateValue),
            passesToSpouse: firstPassesToSpouse,
            pensionsInEstate: !firstPensionsOutside,
            nilRateBands: pence(first.nilRateBandTotal + first.residenceNilRateBandTotal),
            chargeableEstate: first.chargeableEstate,
            taxOnFailedGifts: pence(0),
            taxOnEstate: first.taxOnEstate,
            totalTax: first.taxDue,
            nilRateBandUnusedBps: nrbUnusedBps,
            residenceNilRateBandUnusedBps: rnrbUnusedBps,
            transferProportionUnderived: giftsBeforeFirstDeath,
            secondDeathAssessed: lastSurvivorAssessed,
        };
        /**
         * GOAL 2026-09-10 item 12. The first-death assessment estimates a tax and
         * the death step then passes every pound to the survivor with nothing
         * taken out — so the survivor's figures and the second estate both still
         * hold the money this tax would have taken. Pinned as the behaviour (the
         * plan holds no will and no beneficiary but the survivor); now SAID.
         */
        if (first.taxDue > 0) {
            const deceased = firstEstate.personId === 'client' ? 'the client' : 'the partner';
            const survivor = firstEstate.personId === 'client' ? 'the partner' : 'the client';
            warnOnce('iht-first-death-tax-not-deducted', {
                code: 'iht-first-death-tax-not-deducted',
                message: `At the first death modelled (${firstDeathYear}) the estimated inheritance tax is ${formatGbpWhole(first.taxDue)}. That tax is worked out but not taken out of the plan: everything ${deceased} held passes to ${survivor} in full, so ${survivor}'s figures in the years that follow and the estate assessed at the second death both still include it. No will is recorded — ${survivor} is the only recipient this plan knows — so where anything would pass elsewhere, or the tax would be paid from the estate, the survivor's position here is overstated by that amount.`,
                year: firstDeathYear,
                itemLabel: null,
            });
        }
        if (giftsBeforeFirstDeath) {
            warnOnce('iht-first-death-gift-donor-unknown', {
                code: 'iht-first-death-gift-donor-unknown',
                message: `This plan models two deaths and makes gifts before the first one. A gift is recorded for the household rather than for one person, so the plan cannot tell whose tax-free allowance those gifts used — and therefore cannot work out how much of that allowance passes to the survivor. Gifts are assessed against the final estate, as they always have been, and the estimate at the second death uses the position recorded in the plan's inheritance-tax settings, unchanged. Where it matters, the split between the two estates is a question for the adviser rather than something this projection establishes.`,
                year: firstDeathYear,
                itemLabel: null,
            });
        }
    }
    /**
     * GOAL 2026-09-16 IE-3 — the working for every year, restated from the same
     * `assessAt` call the by-year strip reads, so a working table and the strip
     * can never disagree. Nothing here is a second calculation: every figure is
     * a field the tax layer or the assessment already produced, and the
     * identities below are asserted before anything leaves the engine.
     */
    const basis = input.people.length === 1
        ? 'individual'
        : lastSurvivorAssessed
            ? 'lastSurvivor'
            : atHorizonEnd || assessYear === lastYear.year
                ? 'householdAtHorizon'
                : 'householdAtDeath';
    const assetIdsByWrapper = new Map();
    for (const a of input.assets) {
        const ids = assetIdsByWrapper.get(a.wrapper) ?? [];
        ids.push(a.id);
        assetIdsByWrapper.set(a.wrapper, ids);
    }
    const workingByYear = years.map((y) => {
        const at = y.year === assessYear ? head : assessAt(y.year);
        const b = at.result.breakdown;
        const lines = compositionAt(at);
        const gross = lines.filter((l) => l.inEstate).reduce((n, l) => n + l.value, 0);
        const liabilities = at.row.liabilitiesOutstanding;
        const flooring = at.estateValue - (at.netEstate - at.businessRelief);
        const residenceMissing = [];
        if (at.residence.mainResidenceMissing)
            residenceMissing.push('mainResidenceNotRecorded');
        // "Not entered" is a gap worth naming only where the band could move the
        // figure: the estate reaches past what the nil-rate band covers.
        // IE review (adviser): and only where the plan holds a property that year —
        // with no home at all, no residence band could apply, so there is no gap.
        if (at.residence.source === 'notEntered' &&
            !at.spouseApplied &&
            at.row.balancesByWrapper.property > 0 &&
            b.estateBeforeBands > b.nilRateBand.remainingAfterGifts) {
            residenceMissing.push('residenceNotEntered');
        }
        const working = {
            year: y.year,
            basis,
            taxYearLabel: y.taxYearLabel,
            householdNetWorth: at.row.totalNetWorth,
            unfundedShortfall: at.row.cumulativeUnmetShortfall,
            pensionsExcluded: pence(at.pensionsExcluded),
            jisaExcluded: pence(at.jisaBalance),
            composition: lines.map((l) => ({ ...l, assetIds: assetIdsByWrapper.get(l.wrapper) ?? [] })),
            grossIncludedAssets: gross,
            liabilities: pence(liabilities),
            netEstate: at.netEstate,
            businessRelief: pence(at.businessRelief),
            flooringAdjustment: flooring,
            estateValue: pence(at.estateValue),
            estateValueForTaper: b.estateValueForTaper,
            estateExempt: b.estateExempt,
            estateBeforeBands: b.estateBeforeBands,
            nilRateBand: b.nilRateBand,
            residenceNilRateBand: {
                ...b.residenceNilRateBand,
                source: at.residence.source,
                ...(at.residence.assetId !== undefined ? { residenceAssetId: at.residence.assetId } : {}),
            },
            taxableEstate: b.taxableEstate,
            rateBps: b.rateBps,
            rateBasis: settings.charityRateApplies === true ? 'reducedRateAsserted' : 'standard',
            estateTax: at.result.taxOnEstate,
            giftTax: at.result.taxOnGifts,
            totalTax: at.result.taxDue,
            pensionTreatment: settings.pensionsOutsideEstate !== undefined
                ? at.pensionsOutside
                    ? 'outsideBySetting'
                    : 'insideBySetting'
                : at.pensionsOutside
                    ? 'outsideByRule'
                    : 'insideByRule',
            spouseExemptionApplied: at.spouseApplied,
            status: residenceMissing.length > 0 ? 'incomplete' : 'estimated',
            missing: residenceMissing,
            gifts: giftReadoutsAt(at, y.year),
        };
        const fail = (what) => {
            throw new RangeError(`IHT working does not reconcile at ${y.year}: ${what}`);
        };
        if (working.householdNetWorth - working.pensionsExcluded - working.jisaExcluded !==
            working.netEstate)
            fail('net worth to net estate');
        if (gross - liabilities !== working.netEstate)
            fail('assets less debts');
        if (working.netEstate - working.businessRelief + flooring !== working.estateValue)
            fail('reliefs and floor');
        if (working.estateExempt + working.estateBeforeBands !== working.estateValue)
            fail('exemptions');
        if (working.residenceNilRateBand.usedByEstate +
            working.nilRateBand.usedByEstate +
            working.taxableEstate !==
            working.estateBeforeBands)
            fail('allowances');
        if (working.estateTax + working.giftTax !== working.totalTax)
            fail('tax');
        return working;
    });
    return {
        year: assessYear,
        atHorizonEnd,
        estateValue: pence(estateValue),
        pensionsInEstate: !head.pensionsOutside,
        nilRateBands: pence(result.nilRateBandTotal + result.residenceNilRateBandTotal),
        chargeableEstate: result.chargeableEstate,
        taxOnFailedGifts: result.taxOnGifts,
        taxOnEstate: result.taxOnEstate,
        totalTax: result.taxDue,
        byYearOfDeath,
        gifts: giftReadouts,
        applied: {
            residenceNilRateBandApplied: result.residenceNilRateBandTotal,
            spouseExemptionApplied: passesToSpouse,
            // IE-3: a transfer recorded as percentages is a transfer applied too.
            transferredAllowancesApplied: hasFullTransferredAllowances ||
                (settings.transferredNilRateBandBps ?? 0) > 0 ||
                (settings.transferredResidenceNilRateBandBps ?? 0) > 0,
            charityRateApplied: settings.charityRateApplies ?? false,
        },
        composition,
        firstDeath,
        workingByYear,
    };
}
/** The calendar/tax year a person reaches their modelled death age. */
function clockYearForDeath(p, startYear, _clientAge, _input) {
    return startYear + Math.max(0, (p.deathAge ?? 0) - p.currentAge);
}
/**
 * The rates the projection ACTUALLY APPLIED, per wrapper, value-weighted across
 * the household's holdings in that wrapper at the valuation date.
 *
 * A per-asset override (`asset.growthRateBps` / `yieldBps` / `feeDragBps`) is a
 * legitimate adviser-set modelling choice and wins over the wrapper assumption
 * everywhere the engine resolves a rate — personal pots, DC pots, tracked
 * business/other holdings, Junior ISAs and properties all use the same
 * `override ?? wrapper` rule. This reproduces that one rule in a single pure
 * pass so the published assumptions can be reconciled against it.
 *
 * Weighting is by starting value, matching `effAcc`, the accumulator the
 * projection itself uses. A wrapper the plan does not hold is absent, so its
 * published preset row is left exactly as it was.
 */
function appliedRatesByWrapper(input, rateOf) {
    const acc = new Map();
    for (const asset of input.assets) {
        const w = asset.wrapper;
        const g = asset.growthRateBps ?? rateOf.growth(w);
        const y = asset.yieldBps ?? rateOf.yield(w);
        const f = asset.feeDragBps ?? rateOf.fee(w);
        const value = Math.max(0, asset.currentValue);
        const a = acc.get(w) ?? { value: 0, gW: 0, yW: 0, fW: 0, count: 0, gS: 0, yS: 0, fS: 0 };
        a.value += value;
        a.gW += value * g;
        a.yW += value * y;
        a.fW += value * f;
        a.count += 1;
        a.gS += g;
        a.yS += y;
        a.fS += f;
        acc.set(w, a);
    }
    const out = new Map();
    for (const [w, a] of acc) {
        // Value-weighted where there is value; the unweighted mean where a wrapper
        // is held only as a future container (e.g. a pension funded purely by
        // contributions) — the same two-step fallback `effRate` uses.
        const pick = (weighted, sum) => a.value > 0 ? Math.trunc(weighted / a.value) : a.count > 0 ? Math.trunc(sum / a.count) : 0;
        out.set(w, {
            growthBps: pick(a.gW, a.gS),
            yieldBps: pick(a.yW, a.yS),
            feeBps: pick(a.fW, a.fS),
        });
    }
    return out;
}
/**
 * Reconcile a provenance snapshot against the rates actually applied.
 *
 * The assumptions page is the adviser's means of checking the arithmetic, so a
 * figure it publishes must be one the projection used. Where a per-asset
 * override moved the applied rate away from the published one, the entry is
 * restated and re-provenanced as an override — never left claiming the firm's
 * preset supplied a figure the preset did not supply. A fee or yield that is
 * applied but has no row at all gains one, which is how a silently-charged
 * fee drag becomes visible.
 *
 * Entries that already agree are returned untouched, so a plan with no
 * overrides keeps a byte-identical snapshot.
 */
function reconcileSnapshotToApplied(snapshot, applied) {
    const entries = [...snapshot.entries];
    let changed = false;
    const restate = (key, appliedBps, publishZero) => {
        const at = entries.findIndex((e) => e.key === key);
        if (at === -1) {
            // No row exists. Only worth adding one for a rate that actually bites —
            // publishing "fee 0%" for every wrapper would bury the page in noise.
            if (appliedBps === 0 && !publishZero)
                return;
            entries.push({
                key,
                valueBps: appliedBps,
                source: 'adviser-override',
                detail: 'set on this plan’s holdings',
            });
            changed = true;
            return;
        }
        if (entries[at].valueBps === appliedBps)
            return;
        entries[at] = {
            key,
            valueBps: appliedBps,
            source: 'adviser-override',
            detail: 'set on this plan’s holdings',
        };
        changed = true;
    };
    for (const [w, rates] of applied) {
        restate(`growth.${w}`, rates.growthBps, true);
        restate(`yield.${w}`, rates.yieldBps, false);
        restate(`fee.${w}`, rates.feeBps, false);
    }
    if (!changed)
        return snapshot;
    return {
        preset: snapshot.preset,
        reviewPending: snapshot.reviewPending,
        entries,
        // Restating holdings' own rates does not change WHICH firm version the
        // base figures came from.
        ...(snapshot.set !== undefined ? { set: snapshot.set } : {}),
    };
}
/** Minimal snapshot for assumptions passed directly (not via a preset). */
function synthesiseSnapshot(assumptions) {
    const entries = [
        { key: 'cpi', valueBps: assumptions.inflationBps, source: 'adviser-override', detail: null },
        {
            key: 'wageGrowth',
            valueBps: assumptions.wageGrowthBps,
            source: 'adviser-override',
            detail: null,
        },
        {
            key: 'rpi',
            valueBps: assumptions.rpiBps ?? assumptions.inflationBps,
            source: assumptions.rpiBps === undefined ? 'default' : 'adviser-override',
            detail: assumptions.rpiBps === undefined ? 'defaults to CPI' : null,
        },
    ];
    for (const w of WRAPPER_VALUES) {
        entries.push({
            key: `growth.${w}`,
            valueBps: assumptions.growthBpsByWrapper[w],
            source: 'adviser-override',
            detail: null,
        });
        const y = assumptions.yieldBpsByWrapper?.[w];
        if (y !== undefined && y !== 0) {
            entries.push({ key: `yield.${w}`, valueBps: y, source: 'adviser-override', detail: null });
        }
        const f = assumptions.feeBpsByWrapper?.[w];
        if (f !== undefined && f !== 0) {
            entries.push({ key: `fee.${w}`, valueBps: f, source: 'adviser-override', detail: null });
        }
    }
    return { preset: 'custom', reviewPending: false, entries };
}
//# sourceMappingURL=household.js.map